"use client"

import { useState, useRef, useCallback, useEffect, useMemo } from "react"
import { toast } from "sonner"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/lib/auth-provider"
import { supabase, getHealthySupabaseClient } from "@/lib/supabaseClient"
import posthog from 'posthog-js'
// Composants SVG optimisés avec React.memo
import ProcessorElement from './components/ProcessorElement'
import { PortDragPreview } from './components/PortDragPreview'
import PanelElement from './components/PanelElement'
import SimplePanelElement from './components/SimplePanelElement' // ✅ Version simplifiée pour grandes mires
import CabinetElement from './components/CabinetElement'
import CableElement from './components/CableElement'
import SvgCartouche from './components/SvgCartouche'
import SvgSnapGuides from './components/SvgSnapGuides'
import TauriDragOverlay from './components/TauriDragOverlay'


// @dnd-kit pour le déplacement fluide des éléments
import { DndContext, PointerSensor, useSensor, useSensors, DragOverlay, MeasuringStrategy } from '@dnd-kit/core'
import { useDraggableWorkspace } from './hooks/useDraggableWorkspace'
import { useFilteredCatalog } from './hooks/useFilteredCatalog'
import { DraggableWorkspaceElement } from './components/DraggableWorkspaceElement'

// ✅ OPTIMISATION Phase 2 : Debounce pour les recherches
import { useDebouncedValue } from './hooks/useDebouncedValue'
import debounce from 'lodash/debounce'
import { useTheme } from "next-themes"
import { useIsMobile } from "@/hooks/use-mobile"
import { useLanguage } from "@/lib/i18n/language-provider"
import { SynopticProjectProvider, useSynopticProject } from "@/lib/synoptic-project-provider"
import { useReadOnly } from "@/lib/readonly-context"
import { loadProcessorsFromCSV, loadPanelsFromCSV, loadCabinetsFromCSV, loadPanelsFromLWC } from "@/lib/synoptic/csv-loader"
import useTauri from "@/hooks/useTauri"
import { ProjectHub } from "@/components/synoptic/project-hub"
import { useDemoMode } from "@/hooks/use-demo-mode"
import { OnboardingOverlay, OnboardingStartButton } from "@/components/synoptic/onboarding"
import { useOnboarding } from "@/hooks/use-onboarding"
import { DemoLimitReached, DemoEndedOverlay, DemoWelcome } from "@/components/synoptic/demo-mode"
import { ProjectPicker } from "@/components/synoptic/project-picker"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog"
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import {
  Grid3X3,
  ChevronRight,
  X,
  ArrowLeft,
  Home,
  MousePointer,
  Hand,
  CheckCircle2,
  Magnet,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Link as LinkIcon,
  Plus,
  CheckCircle,
  Cloud,
  Save,
  Copy,
  Pencil,
} from "lucide-react"

// Types et utilitaires extraits pour une meilleure organisation du code
import type {
  LEDProcessor, LEDPanel, PowerCabinet, CableRoute,
  PanelConnection, PowerConnection, WorkspaceElement,
  DataPage, PowerPage, MireConnection, MirePowerConnection,
  WorkflowStep, LEDConfig, ProjectMire
} from '@/types/synoptique'

// Constants extraits
import {
  PORT_COLORS,
  OUTLET_COLORS,
  MAX_DAISY_CHAIN_PANELS,
  getPortColor,
  getOutletColor,
  REFERENCE_ZOOM,
  formatResolution,
  formatNumber,
  calculatePortCapacity,
  calculateMaxPanelsPerPort
} from './constants'

// Dialogs extraits
import { CustomPanelDialog, CustomCabinetDialog } from './components/dialogs'
import { DeleteMireDialog } from './components/dialogs/DeleteMireDialog'
import { getApiUrl } from '@/lib/api-url'
import SmartLink from '@/components/smart-link'

// Extracted hooks & utils
import { useMireGrid, getVirtualCellsFromImage, getVirtualCell, findClosestMireCellInDirection } from './hooks/useMireGrid'
import {
  appendCabinetPowerLink,
  appendPanelDataConnection,
  appendPanelPowerConnection,
  computeCabinetLoadMap,
  hasValidCablePoints,
  isCabinetLinkCable,
  validateCabinetPowerLink,
  sanitizeDataPage,
  sanitizePowerPage,
  type DataPageWiringPatch,
  type PowerPageWiringPatch
} from './utils/cabling-mutations'
import {
  setUserSelect,
} from './utils/connection-helpers'
import { useSaveLoad } from './hooks/useSaveLoad'
import { usePageManagement } from './hooks/usePageManagement'
import { useGroupManagement } from './hooks/useGroupManagement'
import { useAutoGeneratePanels } from './hooks/useAutoGeneratePanels'
import { useExportSvg } from './hooks/useExportSvg'
import { useReportGenerator } from './hooks/useReportGenerator'
import { SynoptiqueHeader } from './components/SynoptiqueHeader'
import { SynoptiqueSidebar } from './components/SynoptiqueSidebar'
import { BottomPanel } from './components/BottomPanel'
import { PageTabsToolbar } from './components/PageTabsToolbar'
import { RightPropertiesPanel } from './components/RightPropertiesPanel'
import { MobileSynopticViewer } from './components/MobileSynopticViewer'

export function SynoptiqueContent() {
  // ReadOnly mode (shared project viewing)
  const { readOnly, sharedProject, isLoading: readOnlyLoading } = useReadOnly()

  // Auth check - require premium (only if Supabase is configured)
  const { isPro: rawIsPro, loading: authLoading, user, session: authSession } = useAuth();
  // En mode readOnly, on bypass les checks Pro
  const isPro = readOnly || rawIsPro;

  // Demo mode for non-premium users
  const demoMode = useDemoMode(isPro);

  // Interactive onboarding for demo users
  const onboarding = useOnboarding();

  // Synoptic project management
  const { saveProject, currentProject, loadProject, loadProjectFromUrl, updateProjectName, projectData, isLoadingFromUrl, loadingProjectName, exitPromptOpen, confirmExitWithoutSaving, stayAndSave, createProject, ensureReportToken } = useSynopticProject();
  const [showProjectPicker, setShowProjectPicker] = useState(false);
  const exitChoiceRef = useRef<'stay' | 'leave' | null>(null);
  const mireLoadAckTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [demoLimitDialog, setDemoLimitDialog] = useState<{ open: boolean; limitType: 'element' | 'connection' | 'cable' | 'processor' | 'cabinet'; currentCount: number; maxCount: number }>({ open: false, limitType: 'element', currentCount: 0, maxCount: 0 });

  // Check if Supabase is configured
  const supabaseConfigured = !!(typeof window !== 'undefined' && process.env.NEXT_PUBLIC_SUPABASE_URL);

  // Tauri hook pour les fonctionnalités desktop (export/import fichiers)
  const { isTauriApp } = useTauri();

  const { t } = useLanguage()
  const { theme, setTheme } = useTheme()
  const isMobile = useIsMobile()
  const [mounted, setMounted] = useState(false)

  // États pour les données chargées dynamiquement depuis les CSV
  const [processors, setProcessors] = useState<LEDProcessor[]>([])
  const [panels, setPanels] = useState<LEDPanel[]>([])
  const [powerCabinets, setPowerCabinets] = useState<PowerCabinet[]>([])
  const [loadingData, setLoadingData] = useState(true)

  // États du workflow
  const [currentStep, setCurrentStep] = useState<WorkflowStep>("testPattern")
  const [projectName, setProjectName] = useState("Nouveau Projet")
  const [tempProjectName, setTempProjectName] = useState<string | null>(null) // Nom temporaire pour l'édition (null = pas en édition)
  const [hasVisitedPowerStep, setHasVisitedPowerStep] = useState(false)

  // États pour la configuration de mire importée
  const [importedLEDConfig, setImportedLEDConfig] = useState<LEDConfig | null>(null)

  // État temporaire pour la config reçue de l'iframe (avant validation par l'utilisateur)
  const [pendingLEDConfig, setPendingLEDConfig] = useState<typeof importedLEDConfig>(null)

  // Compteur de modifications de mire pour l'onboarding (détection de 2 modifications)
  const mireModificationCountRef = useRef(0)

  // Tracking du chargement des données de base (CSV/API) indépendamment des customs user
  const basePanelsLoaded = useRef(false)
  const baseCabinetsLoaded = useRef(false)

  // État pour savoir si l'utilisateur a généré une mire
  const [hasMireGenerated, setHasMireGenerated] = useState(true)

  // États pour gérer plusieurs mires custom
  const [savedMires, setSavedMires] = useState<Array<typeof importedLEDConfig>>([])
  const [currentMireName, setCurrentMireName] = useState<string>('')
  // Index de la mire en cours de modification (null = nouvelle mire)
  const [editingMireIndex, setEditingMireIndex] = useState<number | null>(null)

  // État pour les mires du projet (persistantes, indépendantes du workspace)
  const [projectMires, setProjectMires] = useState<ProjectMire[]>([])

  // États pour l'image de fond de la mire
  const [mireBackgroundImage, setMireBackgroundImage] = useState<string | null>(null)
  const [mireBackgroundOpacity, setMireBackgroundOpacity] = useState(0.3)

  // Historique des connexions pour Undo
  const [connectionHistory, setConnectionHistory] = useState<PanelConnection[]>([]) // Historique pour Undo

  // États d'affichage - adaptés à l'étape actuelle
  const [showProcessors, setShowProcessors] = useState(currentStep === 'data')
  const [showPanels, setShowPanels] = useState(currentStep === 'data')
  const [showCabling, setShowCabling] = useState(false) // Le câblage est géré dans l'étape data

  // États principaux
  const [selectedProcessor, setSelectedProcessor] = useState<LEDProcessor | null>(null)
  const [selectedPanel, setSelectedPanel] = useState<LEDPanel | null>(null)
  const [selectedCabinet, setSelectedCabinet] = useState<PowerCabinet | null>(null)
  const [searchProcessor, setSearchProcessor] = useState("")
  const [searchPanel, setSearchPanel] = useState("")
  const [searchCabinet, setSearchCabinet] = useState("")
  const [showCabinets, setShowCabinets] = useState(false)

  // ✅ OPTIMISATION Phase 2 : Debounce des recherches (300ms)
  // Évite de re-filtrer à chaque frappe → -80% de calculs pendant la recherche
  const debouncedSearchProcessor = useDebouncedValue(searchProcessor, 300)
  const debouncedSearchPanel = useDebouncedValue(searchPanel, 300)
  const debouncedSearchCabinet = useDebouncedValue(searchCabinet, 300)

  // États pour afficher les propriétés des items du panneau de gauche
  const [selectedItemForProperties, setSelectedItemForProperties] = useState<{
    type: 'processor' | 'panel' | 'cabinet'
    data: LEDProcessor | LEDPanel | PowerCabinet
  } | null>(null)

  // ========== Filtered Catalog (extracted to hooks/useFilteredCatalog.ts) ==========
  const {
    filteredProcessors, processorsByBrand, sortedBrands,
    filteredPanels, panelsByBrand, sortedPanelBrands,
    filteredCabinets, cabinetsByBrand, sortedCabinetBrands,
    collapsedProcessorBrands, collapsedPanelBrands, collapsedCabinetBrands,
    setCollapsedProcessorBrands, setCollapsedPanelBrands, setCollapsedCabinetBrands,
    toggleProcessorBrandCollapse, togglePanelBrandCollapse, toggleCabinetBrandCollapse,
  } = useFilteredCatalog({
    processors, panels, powerCabinets,
    debouncedSearchProcessor, debouncedSearchPanel, debouncedSearchCabinet,
  })

  // État pour stocker les images qui ont réussi à charger
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set())

  // État pour stocker les images qui ont échoué à charger (pour éviter de les retenter)
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set())

  // État pour stocker les dimensions réelles des images chargées
  const [loadedImageDimensions, setLoadedImageDimensions] = useState<Map<string, { width: number; height: number }>>(new Map())

  // Fonction pour obtenir l'URL de l'image : par défaut placeholder, sinon l'image si chargée
  const getProcessorImageUrl = (processor: LEDProcessor): string => {
    const imagePath = processor.image

    // Si pas d'image définie ou si l'image n'est pas encore chargée, utiliser le placeholder
    if (!imagePath || imagePath === '/images/processors/placeholder.png' || !loadedImages.has(imagePath)) {
      return '/images/processors/placeholder.png'
    }

    return imagePath
  }

  // Fonction appelée quand une image se charge avec succès
  const handleImageLoad = useCallback((imagePath: string, width: number, height: number) => {
    setLoadedImages(prev => new Set(prev).add(imagePath))
    setLoadedImageDimensions(prev => new Map(prev).set(imagePath, { width, height }))
  }, [])

  // Charger les dimensions d'une image et vérifier si elle existe
  const loadImageDimensions = useCallback((imageUrl: string) => {
    // Ne pas charger le placeholder, si déjà chargé, ou si déjà échoué
    if (imageUrl === '/images/processors/placeholder.png' ||
      loadedImageDimensions.has(imageUrl) ||
      failedImages.has(imageUrl)) return

    const img = new Image()
    img.onload = () => {
      // Image chargée avec succès
      handleImageLoad(imageUrl, img.naturalWidth, img.naturalHeight)
    }
    img.onerror = () => {
      // Image non disponible, on la marque comme échouée pour ne plus la retenter
      setFailedImages(prev => new Set(prev).add(imageUrl))
    }
    img.src = imageUrl
  }, [loadedImageDimensions, failedImages, handleImageLoad])

  // Fonction pour calculer les dimensions réelles de l'image affichée (en tenant compte du preserveAspectRatio)
  const getActualImageBounds = useCallback((element: WorkspaceElement): { x: number; y: number; width: number; height: number } => {
    const processor = element.data as LEDProcessor
    const displayedImageUrl = getProcessorImageUrl(processor) // L'image actuellement affichée (placeholder ou vraie image)

    // Déclencher le chargement de l'image spécifique du processeur en arrière-plan
    if (processor.image && processor.image !== '/images/processors/placeholder.png') {
      loadImageDimensions(processor.image)
    }

    // Utiliser les dimensions de l'image affichée (placeholder ou vraie image)
    const realImageDims = loadedImageDimensions.get(displayedImageUrl)

    if (!realImageDims) {
      // Si on n'a pas encore les dimensions, retourner la zone complète
      return {
        x: element.position.x,
        y: element.position.y,
        width: element.size.width,
        height: element.size.height
      }
    }

    // Ratio de l'image originale
    const imageRatio = realImageDims.width / realImageDims.height
    // Ratio de la zone allouée
    const containerRatio = element.size.width / element.size.height

    let actualWidth, actualHeight, offsetX, offsetY

    if (imageRatio > containerRatio) {
      // L'image est limitée par la largeur
      actualWidth = element.size.width
      actualHeight = element.size.width / imageRatio
      offsetX = 0
      offsetY = (element.size.height - actualHeight) / 2
    } else {
      // L'image est limitée par la hauteur
      actualHeight = element.size.height
      actualWidth = element.size.height * imageRatio
      offsetX = (element.size.width - actualWidth) / 2
      offsetY = 0
    }

    return {
      x: element.position.x + offsetX,
      y: element.position.y + offsetY,
      width: actualWidth,
      height: actualHeight
    }
  }, [loadedImageDimensions, loadedImages, loadImageDimensions])

  // Fonction pour calculer le meilleur point de connexion sur le processeur (câbles Data)
  const getBestConnectionPoint = useCallback((processor: WorkspaceElement, panelX: number, panelY: number) => {
    // Utiliser les dimensions réelles de l'image si c'est un processeur avec image
    const bounds = (processor.data as LEDProcessor).image
      ? getActualImageBounds(processor)
      : {
        x: processor.position.x,
        y: processor.position.y,
        width: processor.size.width,
        height: processor.size.height
      }

    const procX = bounds.x
    const procY = bounds.y
    const procW = bounds.width
    const procH = bounds.height

    // Définir 8 points possibles : 4 coins + 4 milieux de côtés
    const points = [
      { x: procX, y: procY, side: 'top-left' },
      { x: procX + procW * 0.5, y: procY, side: 'top' },
      { x: procX + procW, y: procY, side: 'top-right' },
      { x: procX + procW, y: procY + procH * 0.5, side: 'right' },
      { x: procX + procW, y: procY + procH, side: 'bottom-right' },
      { x: procX + procW * 0.5, y: procY + procH, side: 'bottom' },
      { x: procX, y: procY + procH, side: 'bottom-left' },
      { x: procX, y: procY + procH * 0.5, side: 'left' },
    ]

    // Trouver le point le plus proche du panneau
    let minDistance = Infinity
    let bestPoint = points[0]

    points.forEach(point => {
      const distance = Math.sqrt(
        Math.pow(point.x - panelX, 2) + Math.pow(point.y - panelY, 2)
      )
      if (distance < minDistance) {
        minDistance = distance
        bestPoint = point
      }
    })

    return bestPoint
  }, [getActualImageBounds])

  // Retourne le milieu du côté du panneau le plus proche de (fromX, fromY)
  const getPanelMidSidePoint = useCallback((fromX: number, fromY: number, panel: WorkspaceElement) => {
    const cx = panel.position.x + panel.size.width / 2
    const cy = panel.position.y + panel.size.height / 2
    const dx = cx - fromX, dy = cy - fromY
    if (Math.abs(dx) >= Math.abs(dy)) {
      // Côté gauche ou droit
      return {
        x: dx > 0 ? panel.position.x : panel.position.x + panel.size.width,
        y: cy
      }
    } else {
      // Côté haut ou bas
      return {
        x: cx,
        y: dy > 0 ? panel.position.y : panel.position.y + panel.size.height
      }
    }
  }, [])

  // Fonction pour calculer le meilleur point de connexion sur une armoire (câbles Power)
  const getBestCabinetPoint = useCallback((cabinet: WorkspaceElement, x: number, y: number) => {
    const cx = cabinet.position.x
    const cy = cabinet.position.y
    const cw = cabinet.size.width
    const ch = cabinet.size.height
    const pts = [
      { x: cx, y: cy },
      { x: cx + cw * 0.5, y: cy },
      { x: cx + cw, y: cy },
      { x: cx + cw, y: cy + ch * 0.5 },
      { x: cx + cw, y: cy + ch },
      { x: cx + cw * 0.5, y: cy + ch },
      { x: cx, y: cy + ch },
      { x: cx, y: cy + ch * 0.5 },
    ]
    let best = pts[0]
    let dmin = Infinity
    pts.forEach(p => {
      const d = Math.hypot(p.x - x, p.y - y)
      if (d < dmin) { dmin = d; best = p }
    })
    return best
  }, [])

  // Note: Les images sont chargées uniquement quand les processeurs sont utilisés dans le workspace
  // Cela évite de charger toutes les images inutilement au démarrage

  // Charger aussi les dimensions du placeholder
  useEffect(() => {
    const placeholderUrl = '/images/processors/placeholder.png'
    if (!loadedImageDimensions.has(placeholderUrl)) {
      const img = new Image()
      img.onload = () => {
        setLoadedImageDimensions(prev => new Map(prev).set(placeholderUrl, {
          width: img.naturalWidth,
          height: img.naturalHeight
        }))
      }
      img.src = placeholderUrl
    }
  }, [])

  // États pour le modal de création de panneau personnalisé
  const [isCustomPanelDialogOpen, setIsCustomPanelDialogOpen] = useState(false)
  
  // États pour le modal de création d'armoire personnalisée
  const [isCustomCabinetDialogOpen, setIsCustomCabinetDialogOpen] = useState(false)
  
  // États pour le dialog de suppression de mire
  const [isDeleteMireDialogOpen, setIsDeleteMireDialogOpen] = useState(false)
  const [mireToDelete, setMireToDelete] = useState<{ id: string; name: string } | null>(null)

  // États pour les pages Data multiples
  const [dataPages, setDataPages] = useState<DataPage[]>([
    {
      id: 'data-1',
      name: 'Data 1',
      workspaceElements: [],
      cableRoutes: [],
      panelConnections: [],
      mireConnections: []  // NEW: Mire connections
    }
  ])
  const [currentDataPageId, setCurrentDataPageId] = useState('data-1')

  // États pour les pages Power multiples
  const [powerPages, setPowerPages] = useState<PowerPage[]>([
    {
      id: 'power-1',
      name: 'Power 1',
      workspaceElements: [],
      cableRoutes: [],
      powerConnections: [],
      mirePowerConnections: []  // NEW: Mire power connections
    }
  ])
  const [currentPowerPageId, setCurrentPowerPageId] = useState('power-1')

  const [editingPageId, setEditingPageId] = useState<string | null>(null)
  const [editingPageName, setEditingPageName] = useState('')
  const [draggedTabId, setDraggedTabId] = useState<string | null>(null)
  const [editingPageType, setEditingPageType] = useState<'data' | 'power'>('data')

  const {
    addDataPage,
    deleteDataPage,
    moveDataPage,
    duplicateDataPage,
    addPowerPage,
    deletePowerPage,
    movePowerPage,
    duplicatePowerPage,
    startEditingPage,
    finishEditingPage,
  } = usePageManagement({
    dataPages,
    setDataPages,
    currentDataPageId,
    setCurrentDataPageId,
    powerPages,
    setPowerPages,
    currentPowerPageId,
    setCurrentPowerPageId,
    editingPageId,
    setEditingPageId,
    editingPageName,
    setEditingPageName,
    editingPageType,
    setEditingPageType,
    demoMode,
    isPro,
  })

  // État pour savoir quelle catégorie de pages est visualisée en mode export
  const [exportViewMode, setExportViewMode] = useState<'data' | 'power'>('data')
  // État pour afficher/masquer la cartouche dans l'export
  const [showCartouche, setShowCartouche] = useState(true)
  // État pour afficher/masquer la liaison processeur → 1er panneau dans l'export (indépendant du workspace)
  const [showExportProcessorLink, setShowExportProcessorLink] = useState(true)

  // État pour l'overlay de progression d'export
  const [isExporting, setIsExporting] = useState(false)
  const [exportProgress, setExportProgress] = useState('')
  const [showExportSuccess, setShowExportSuccess] = useState(false)
  // Callback to reset workspace when demo starts/restarts
  const resetDemoWorkspace = useCallback(() => {
    // Reset Data pages
    setDataPages([{
      id: 'data-1',
      name: 'Data 1',
      workspaceElements: [],
      cableRoutes: [],
      panelConnections: [],
      mireConnections: []
    }])
    setCurrentDataPageId('data-1')

    // Reset Power pages
    setPowerPages([{
      id: 'power-1',
      name: 'Power 1',
      workspaceElements: [],
      cableRoutes: [],
      powerConnections: [],
      mirePowerConnections: []
    }])
    setCurrentPowerPageId('power-1')

    // Reset other states
    setImportedLEDConfig(null)
    setHasVisitedPowerStep(false)
    setCurrentStep('testPattern')
  }, [])

  // État pour le chemin du fichier local .vgsyn (pour sauvegarde intelligente)
  const [localFilePath, setLocalFilePath] = useState<string | null>(null)

  // État pour le dialogue Save As
  const [showSaveAsDialog, setShowSaveAsDialog] = useState(false)
  const [saveAsFileName, setSaveAsFileName] = useState('')

  // 🐛 DEBUG: États pour le debugging des sauvegardes
  const [isSavingDebug, setIsSavingDebug] = useState(false)
  const [saveStartTime, setSaveStartTime] = useState<number | null>(null)
  const [debugInfo, setDebugInfo] = useState<string[]>([])
  const [retryCount, setRetryCount] = useState(0)
  const [maxRetries] = useState(3)

  // 🌐 Détection de connexion internet
  const [isOnline, setIsOnline] = useState(true)

  // ⏱️ État pour l'indicateur de fraîcheur de sauvegarde
  const [lastSaveTime, setLastSaveTime] = useState<number | null>(null)
  const [timeSinceLastSave, setTimeSinceLastSave] = useState<number>(0)

  // 🐛 DEBUG: Compteur de pings pour éviter de surcharger la console (conservé pour usage futur)
  const pingCountRef = useRef(0)
  
  // 🐛 DEBUG: Fonction pour logger les étapes de sauvegarde (uniquement dans l'UI, pas dans la console)
  const debugLog = useCallback((message: string, data?: any) => {
    const timestamp = new Date().toISOString()
    const logMessage = `[${timestamp}] ${message}`

    setDebugInfo(prev => {
      const newEntry = typeof data !== 'undefined'
        ? `${logMessage} ${JSON.stringify(data, null, 2)}`
        : logMessage

      const updated = [...prev, newEntry]
      if (updated.length > 50) {
        return updated.slice(updated.length - 50)
      }
      return updated
    })
  }, [])

  // 🎨 Calcul des couleurs du bouton de sauvegarde (version simplifiée fluo-rétro)
  const getSaveButtonColors = useCallback((timeElapsed: number): { border: string, bg: string } => {
    const seconds = timeElapsed / 1000

    if (seconds < 30) {
      return {
        border: 'border-green-500',
        bg: 'bg-green-50'
      }
    } else if (seconds < 60) {
      return {
        border: 'border-green-600',
        bg: 'bg-green-100'
      }
    } else if (seconds < 120) {
      return {
        border: 'border-yellow-500',
        bg: 'bg-yellow-50'
      }
    } else if (seconds < 300) {
      return {
        border: 'border-orange-500',
        bg: 'bg-orange-50'
      }
    } else if (seconds < 600) {
      return {
        border: 'border-orange-600',
        bg: 'bg-orange-100'
      }
    } else {
      return {
        border: 'border-red-500',
        bg: 'bg-red-50'
      }
    }
  }, [])

  // État pour le presse-papier (copier-coller)
  const [clipboard, setClipboard] = useState<{
    elements: WorkspaceElement[]
    cables: CableRoute[]
    panelConnections: PanelConnection[]
    powerConnections: PowerConnection[]
  } | null>(null)

  // Déterminer si on est en mode Data ou Power basé sur l'étape actuelle
  const isDataMode = currentStep === 'data'
  const isPowerMode = currentStep === 'cabling'
  const isExportMode = currentStep === 'export'

  // Getters pour les éléments de la page courante (Data ou Power selon le mode)
  // Note: Ces valeurs peuvent être undefined si les tableaux sont vides (état initial)
  const currentDataPage = dataPages.find(p => p.id === currentDataPageId) ?? dataPages[0]
  const currentPowerPage = powerPages.find(p => p.id === currentPowerPageId) ?? powerPages[0]

  // Éléments accessibles selon le mode
  // En mode export, on regarde exportViewMode pour savoir quelle catégorie afficher
  const showPowerInCurrentView = isPowerMode || (isExportMode && exportViewMode === 'power')
  const showDataInCurrentView = isDataMode || (isExportMode && exportViewMode === 'data')

  const workspaceElements: WorkspaceElement[] = showPowerInCurrentView
    ? (currentPowerPage?.workspaceElements || [])
    : (currentDataPage?.workspaceElements || [])
  const cableRoutes: CableRoute[] = showPowerInCurrentView
    ? (currentPowerPage?.cableRoutes || [])
    : (currentDataPage?.cableRoutes || [])
  const panelConnections: PanelConnection[] = currentDataPage?.panelConnections || []
  const powerConnections: PowerConnection[] = currentPowerPage?.powerConnections || []
  const mireConnections: MireConnection[] = currentDataPage?.mireConnections || []  // NEW: Mire connections getter
  const mirePowerConnections: MirePowerConnection[] = currentPowerPage?.mirePowerConnections || []  // NEW: Mire power connections getter

  // Charge électrique par armoire (panneaux + mires + armoires liées en aval)
  const cabinetLoadInfo = useMemo(
    () => computeCabinetLoadMap(
      currentPowerPage?.workspaceElements || [],
      powerConnections,
      mirePowerConnections,
      currentPowerPage?.cableRoutes || []
    ),
    [currentPowerPage, powerConnections, mirePowerConnections]
  )

  // Liaisons armoire → armoire de la page Power courante
  const cabinetLinkCables = useMemo(() => {
    const elements = currentPowerPage?.workspaceElements || []
    const byId = new Map(elements.map(e => [e.id, e]))
    return (currentPowerPage?.cableRoutes || []).filter(c => isCabinetLinkCable(c, byId))
  }, [currentPowerPage])

  // Setters pour les éléments de la page courante
  const setWorkspaceElements = (elements: WorkspaceElement[] | ((prev: WorkspaceElement[]) => WorkspaceElement[])) => {
    // En mode Export, utiliser exportViewMode pour déterminer quelle page modifier
    const shouldUpdatePowerPage = isPowerMode || (isExportMode && exportViewMode === 'power')

    if (shouldUpdatePowerPage) {
      setPowerPages(prev => prev.map(page =>
        page.id === currentPowerPageId
          ? { ...page, workspaceElements: typeof elements === 'function' ? elements(page.workspaceElements) : elements }
          : page
      ))
    } else {
      setDataPages(prev => prev.map(page =>
        page.id === currentDataPageId
          ? { ...page, workspaceElements: typeof elements === 'function' ? elements(page.workspaceElements) : elements }
          : page
      ))
    }
  }

  const setCableRoutes = (cables: CableRoute[] | ((prev: CableRoute[]) => CableRoute[])) => {
    // En mode Export, utiliser exportViewMode pour déterminer quelle page modifier
    const shouldUpdatePowerPage = isPowerMode || (isExportMode && exportViewMode === 'power')

    if (shouldUpdatePowerPage) {
      setPowerPages(prev => prev.map(page =>
        page.id === currentPowerPageId
          ? { ...page, cableRoutes: typeof cables === 'function' ? cables(page.cableRoutes) : cables }
          : page
      ))
    } else {
      setDataPages(prev => prev.map(page =>
        page.id === currentDataPageId
          ? { ...page, cableRoutes: typeof cables === 'function' ? cables(page.cableRoutes) : cables }
          : page
      ))
    }
  }

  const setPanelConnections = (connections: PanelConnection[] | ((prev: PanelConnection[]) => PanelConnection[])) => {
    setDataPages(prev => prev.map(page =>
      page.id === currentDataPageId
        ? { ...page, panelConnections: typeof connections === 'function' ? connections(page.panelConnections) : connections }
        : page
    ))
  }

  const setPowerConnections = (connections: PowerConnection[] | ((prev: PowerConnection[]) => PowerConnection[])) => {
    setPowerPages(prev => prev.map(page =>
      page.id === currentPowerPageId
        ? { ...page, powerConnections: typeof connections === 'function' ? connections(page.powerConnections) : connections }
        : page
    ))
  }

  // NEW: Setters for mire connections
  const setMireConnections = (connections: MireConnection[] | ((prev: MireConnection[]) => MireConnection[])) => {
    setDataPages(prev => prev.map(page =>
      page.id === currentDataPageId
        ? { ...page, mireConnections: typeof connections === 'function' ? connections(page.mireConnections || []) : connections }
        : page
    ))
  }

  const setMirePowerConnections = (connections: MirePowerConnection[] | ((prev: MirePowerConnection[]) => MirePowerConnection[])) => {
    setPowerPages(prev => prev.map(page =>
      page.id === currentPowerPageId
        ? { ...page, mirePowerConnections: typeof connections === 'function' ? connections(page.mirePowerConnections || []) : connections }
        : page
    ))
  }

  // ========== Mutations de câblage atomiques (anti-corruption) ==========
  // Câble + connexion sont écrits dans la MÊME transition d'état, sur la page
  // ciblée par son ID lu via ref (jamais de stale page ID en key-repeat).
  const currentDataPageIdRef = useRef(currentDataPageId)
  currentDataPageIdRef.current = currentDataPageId
  const currentPowerPageIdRef = useRef(currentPowerPageId)
  currentPowerPageIdRef.current = currentPowerPageId

  /** Applique un patch de câblage à la page Data courante, calculé sur l'état frais */
  const applyDataPageWiring = useCallback((fn: (page: DataPage) => DataPageWiringPatch | null) => {
    const pageId = currentDataPageIdRef.current
    setDataPages(prev => prev.map(page => {
      if (page.id !== pageId) return page
      const patch = fn(page)
      return patch ? { ...page, ...patch } : page
    }))
  }, [])

  /** Applique un patch de câblage à la page Power courante, calculé sur l'état frais */
  const applyPowerPageWiring = useCallback((fn: (page: PowerPage) => PowerPageWiringPatch | null) => {
    const pageId = currentPowerPageIdRef.current
    setPowerPages(prev => prev.map(page => {
      if (page.id !== pageId) return page
      const patch = fn(page)
      return patch ? { ...page, ...patch } : page
    }))
  }, [])

  // États pour le drag & drop
  const [draggedItem, setDraggedItem] = useState<{ type: 'processor' | 'panel' | 'cabinet' | 'image', data: LEDProcessor | LEDPanel | PowerCabinet | WorkspaceElement } | null>(null)
  const [dragMousePosition, setDragMousePosition] = useState<{ x: number; y: number } | null>(null)
  const [isDraggingFromList, setIsDraggingFromList] = useState(false)

  // Détecter si on est dans Tauri
  const isTauri = typeof window !== 'undefined' && (
    (window as any).__TAURI_IPC__ !== undefined ||
    (window as any).__TAURI__ !== undefined ||
    (typeof navigator !== 'undefined' && navigator.userAgent.includes('Tauri'))
  )

  // États pour la navigation viewBox (approche SVG native)
  // Nouveau système : 873% = 100%
  const referenceZoom = 8.73 // 873% = 8.73x
  const [viewBox, setViewBox] = useState({
    x: -430,
    y: -300,
    width: 10000 / referenceZoom, // Zoom initial basé sur le nouveau système
    height: 10000 / referenceZoom
  })
  const [isViewBoxInitialized, setIsViewBoxInitialized] = useState(false)
  const [isPanning, setIsPanning] = useState(false)
  const [panStart, setPanStart] = useState({ x: 0, y: 0 })

  // États pour le drag des éléments placés
  const [isDraggingElement, setIsDraggingElement] = useState(false)
  const [draggedElementId, setDraggedElementId] = useState<string | null>(null)
  const [elementDragStart, setElementDragStart] = useState({ x: 0, y: 0 })
  const [hasMoved, setHasMoved] = useState(false)
  const [elementInitialOffset, setElementInitialOffset] = useState({ x: 0, y: 0 })

  // États pour le redimensionnement des éléments
  const [isResizing, setIsResizing] = useState(false)
  const [resizedElementId, setResizedElementId] = useState<string | null>(null)
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0 })
  const [initialSize, setInitialSize] = useState({ width: 0, height: 0 })
  const [initialPosition, setInitialPosition] = useState({ x: 0, y: 0 })
  const [resizingGroupId, setResizingGroupId] = useState<string | null>(null)
  const [initialGroupBounds, setInitialGroupBounds] = useState<{ x: number, y: number, width: number, height: number } | null>(null)
  const [initialGroupElements, setInitialGroupElements] = useState<{ id: string, position: { x: number, y: number }, size: { width: number, height: number } }[]>([])
  // États pour les panneaux rétractables
  const [isLeftPanelOpen, setIsLeftPanelOpen] = useState(true)
  const [isRightPanelOpen, setIsRightPanelOpen] = useState(true)
  const [isBottomPanelOpen, setIsBottomPanelOpen] = useState(true)
  // États pour les modes de curseur
  const [cursorMode, setCursorMode] = useState<'select' | 'pan'>('select')
  const [isSnappingEnabled, setIsSnappingEnabled] = useState(true)
  const [snapGuides, setSnapGuides] = useState<{ x: number[], y: number[] }>({ x: [], y: [] })
  const [selectedElements, setSelectedElements] = useState<string[]>([])
  // États pour les groupes d'éléments
  const [elementGroups, setElementGroups] = useState<{ [groupId: string]: { name: string, elementIds: string[] } }>({})

  const {
    createGroup,
    deleteGroup,
    getGroupForElement,
    getEffectiveSelectionCount,
    getGroupBounds,
  } = useGroupManagement({
    elementGroups,
    setElementGroups,
    workspaceElements,
    selectedElements,
  })

  // États pour le cartouche d'export
  const [cartouche, setCartouche] = useState({
    agence: '',
    client: '',
    evenement: '',
    lieu: '',
    suite: '',
    quoi: '',
    legende: '',
    date: new Date().toISOString().split('T')[0],
    de: ''
  })

  // Onboarding: track cartouche fields filled count
  useEffect(() => {
    if (!onboarding.state.isActive) return
    const filledCount = Object.values(cartouche).filter(v => v && v.trim() !== '').length
    if (filledCount >= 3) {
      onboarding.completeAction('cartouche-filled', filledCount)
    }
  }, [cartouche, onboarding])

  // États pour la sélection rectangulaire
  const [isRubberBandSelection, setIsRubberBandSelection] = useState(false)
  const [rubberBandStart, setRubberBandStart] = useState({ x: 0, y: 0 })
  const [rubberBandEnd, setRubberBandEnd] = useState({ x: 0, y: 0 })
  const [isCtrlPressed, setIsCtrlPressed] = useState(false)
  const [previewSelectedElements, setPreviewSelectedElements] = useState<string[]>([])
  // États pour le placement de panneaux
  const [selectedPanelForPlacement, setSelectedPanelForPlacement] = useState<LEDPanel | null>(null)
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const [isPlacingPanels, setIsPlacingPanels] = useState(false)
  const [placementStart, setPlacementStart] = useState({ x: 0, y: 0 })
  const [panelPlacementCounter, setPanelPlacementCounter] = useState(0)
  const [previewPanels, setPreviewPanels] = useState<Array<{ x: number, y: number, width: number, height: number }>>([])
  const [previewCables, setPreviewCables] = useState<Array<{ id: string, points: { x: number, y: number }[] }>>([])
  const [isDraggingPlacement, setIsDraggingPlacement] = useState(false)

  // États pour le câblage
  const [selectedCableType, setSelectedCableType] = useState<'data' | 'power' | null>(null)
  const [showProcessorSpecs, setShowProcessorSpecs] = useState(false)
  const [showPanelSpecs, setShowPanelSpecs] = useState(false)
  const [showCabinetSpecs, setShowCabinetSpecs] = useState(false)
  const [showProcessorToFirstPanelLinks, setShowProcessorToFirstPanelLinks] = useState(true)
  const [showCabinetToFirstPanelLinks, setShowCabinetToFirstPanelLinks] = useState(true)
  const [mireNumberingMode, setMireNumberingMode] = useState<'all' | 'first-last' | 'none'>('all')

  // États pour la configuration de calcul de capacité des ports
  const [portCapacityConfig, setPortCapacityConfig] = useState<{
    colorDepth: 8 | 10 | 12 | 16
    refreshRate: 50 | 60
    hdrEnabled: boolean
  }>({
    colorDepth: 8,
    refreshRate: 60,
    hdrEnabled: false
  })

  // État pour la configuration technique du mur LED
  const [wallTechnicalConfig, setWallTechnicalConfig] = useState<{
    colorDepth: 8 | 10 | 12 | 16
    refreshRate: 50 | 60
    hdrEnabled: boolean
    panelResolution?: { width: number; height: number }
    panelWeight?: number
    totalPanels?: number
    totalPixels?: number
    calculatedPortCapacity?: number
  }>({
    colorDepth: 8,
    refreshRate: 60,
    hdrEnabled: false,
    panelResolution: undefined,
    panelWeight: undefined,
    totalPanels: undefined,
    totalPixels: undefined,
    calculatedPortCapacity: undefined
  })

  // Synchroniser wallTechnicalConfig avec portCapacityConfig pour BottomPanel
  useEffect(() => {
    setPortCapacityConfig({
      colorDepth: wallTechnicalConfig.colorDepth,
      refreshRate: wallTechnicalConfig.refreshRate,
      hdrEnabled: wallTechnicalConfig.hdrEnabled
    })
  }, [wallTechnicalConfig.colorDepth, wallTechnicalConfig.refreshRate, wallTechnicalConfig.hdrEnabled])

  // Calculer dynamiquement le max daisy chain basé sur la configuration technique du mur
  const calculatedMaxDaisyChain = useMemo(() => {
    // Si une résolution de panneau est disponible dans wallTechnicalConfig, l'utiliser
    if (wallTechnicalConfig.panelResolution) {
      return calculateMaxPanelsPerPort(wallTechnicalConfig.panelResolution, {
        colorDepth: wallTechnicalConfig.colorDepth,
        refreshRate: wallTechnicalConfig.refreshRate,
        hdrEnabled: wallTechnicalConfig.hdrEnabled
      })
    }
    // Sinon, utiliser une résolution par défaut typique (192x192)
    const defaultResolution = { width: 192, height: 192 }
    return calculateMaxPanelsPerPort(defaultResolution, {
      colorDepth: wallTechnicalConfig.colorDepth,
      refreshRate: wallTechnicalConfig.refreshRate,
      hdrEnabled: wallTechnicalConfig.hdrEnabled
    })
  }, [wallTechnicalConfig.colorDepth, wallTechnicalConfig.refreshRate, wallTechnicalConfig.hdrEnabled, wallTechnicalConfig.panelResolution])

  // États pour le menu des processeurs en bas (étape 2 - Data)
  const [selectedProcessorForConnection, setSelectedProcessorForConnection] = useState<string | null>(null)
  const [selectedPort, setSelectedPort] = useState<number | null>(null)
  const [backupPorts, setBackupPorts] = useState<{ [processorId: string]: { [primaryPort: number]: number } }>({})
  // État pour les processeurs backup : { [primaryProcessorId]: backupProcessorId }
  const [backupProcessors, setBackupProcessors] = useState<{ [primaryProcessorId: string]: string }>({})
  const [draggedPort, setDraggedPort] = useState<number | null>(null)
  const [portDragTarget, setPortDragTarget] = useState<number | null>(null) // Port sous la souris pendant le drag (Tauri)
  const [portDragMousePosition, setPortDragMousePosition] = useState<{ x: number; y: number } | null>(null) // Position de la souris pendant le drag du port (Tauri)
  
  // État pour le resize de la bulle de mire
  const [resizingBubble, setResizingBubble] = useState<{ elementId: string; startX: number; startY: number; startWidth: number; startHeight: number } | null>(null)
  // État pour la sélection d'un processeur comme backup
  const [processorForBackup, setProcessorForBackup] = useState<string | null>(null) // Processeur sélectionné pour être marqué comme backup
  const [isDraggingBackupButton, setIsDraggingBackupButton] = useState(false) // État pour le drag du bouton backup
  const [backupButtonDragMousePosition, setBackupButtonDragMousePosition] = useState<{ x: number; y: number } | null>(null) // Position de la souris pendant le drag
  const [backupButtonDragTarget, setBackupButtonDragTarget] = useState<string | null>(null) // Processeur sous la souris pendant le drag

  // États pour le menu des armoires en bas (étape 3 - Power)
  const [selectedCabinetForConnection, setSelectedCabinetForConnection] = useState<string | null>(null)
  const [selectedOutlet, setSelectedOutlet] = useState<number | null>(null)
  // Mode liaison armoire → armoire : une fois armé depuis le panneau du bas, le prochain clic sur une armoire crée le lien
  const [cabinetLinkMode, setCabinetLinkMode] = useState(false)

  // Le mode liaison n'a de sens qu'à l'étape Power avec une armoire sélectionnée
  useEffect(() => {
    if (currentStep !== 'cabling' || !selectedCabinetForConnection) setCabinetLinkMode(false)
  }, [currentStep, selectedCabinetForConnection])
  const [currentPanelForCabling, setCurrentPanelForCabling] = useState<string | null>(null)

  // NEW: États pour le câblage des cellules de mire
  const [currentMireCell, setCurrentMireCell] = useState<{
    imageId: string
    row: number
    col: number
  } | null>(null)

  const [previewMireCell, setPreviewMireCell] = useState<{
    imageId: string
    row: number
    col: number
  } | null>(null)

  // Ref pour éviter les appels multiples de création de connexions
  const processingPanels = useRef<Set<string>>(new Set())

  // Refs pour optimiser le déplacement d'éléments
  const movedElementsRef = useRef<{ id: string, position: { x: number, y: number }, size: { width: number, height: number }, type: string }[]>([])
  const cableUpdateScheduled = useRef(false)

  // ========== Refs fraîches pour le câblage (clavier rapide / changement de page) ==========
  const currentMireCellRef = useRef<{ imageId: string; row: number; col: number } | null>(null)
  currentMireCellRef.current = currentMireCell
  const currentPanelForCablingRef = useRef<string | null>(null)
  currentPanelForCablingRef.current = currentPanelForCabling

  // Setters qui mettent à jour la ref de façon synchrone (nav clavier en key-repeat)
  const setCurrentMireCellNow = useCallback((cell: { imageId: string; row: number; col: number } | null) => {
    currentMireCellRef.current = cell
    setCurrentMireCell(cell)
  }, [])
  const setCurrentPanelForCablingNow = useCallback((id: string | null) => {
    currentPanelForCablingRef.current = id
    setCurrentPanelForCabling(id)
  }, [])

  // Sélection processeur/port et armoire/prise lues via ref → toujours fraîches
  const wiringSelectionRef = useRef<{
    processorId: string | null
    port: number | null
    cabinetId: string | null
    outlet: number | null
  }>({ processorId: null, port: null, cabinetId: null, outlet: null })
  wiringSelectionRef.current = {
    processorId: selectedProcessorForConnection,
    port: selectedPort,
    cabinetId: selectedCabinetForConnection,
    outlet: selectedOutlet
  }
  const getDataWiringSelection = useCallback(() => {
    const s = wiringSelectionRef.current
    return s.processorId !== null && s.port !== null
      ? { processorId: s.processorId, port: s.port }
      : null
  }, [])
  const getPowerWiringSelection = useCallback(() => {
    const s = wiringSelectionRef.current
    return s.cabinetId !== null && s.outlet !== null
      ? { cabinetId: s.cabinetId, outlet: s.outlet }
      : null
  }, [])

  // État de navigation lu par les écouteurs clavier (montés une seule fois)
  const wiringNavRef = useRef<{
    currentStep: WorkflowStep
    exportViewMode: 'data' | 'power'
    workspaceElements: WorkspaceElement[]
    panelConnections: PanelConnection[]
    powerConnections: PowerConnection[]
    mireConnections: MireConnection[]
    mirePowerConnections: MirePowerConnection[]
  }>({
    currentStep: 'testPattern',
    exportViewMode: 'data',
    workspaceElements: [],
    panelConnections: [],
    powerConnections: [],
    mireConnections: [],
    mirePowerConnections: [],
  })
  wiringNavRef.current = {
    currentStep,
    exportViewMode,
    workspaceElements,
    panelConnections,
    powerConnections,
    mireConnections,
    mirePowerConnections,
  }

  const isPortBackup = (port: number, processorId: string) => {
    const processorBackups = backupPorts[processorId] || {}
    return Object.values(processorBackups).includes(port)
  }

  const isPortPrimary = (port: number, processorId: string) => !isPortBackup(port, processorId)


  // Fonctions de backup optimisées avec useCallback
  const getBackupForPort = useCallback((port: number, processorId: string) => {
    const processorBackups = backupPorts[processorId] || {}
    return processorBackups[port] ?? null
  }, [backupPorts])

  const getBackupsForPort = useCallback((port: number, processorId: string) => {
    const backup = getBackupForPort(port, processorId)
    return backup ? [backup] : []
  }, [getBackupForPort])

  const getPrimaryForBackup = useCallback((backupPort: number, processorId: string) => {
    const processorBackups = backupPorts[processorId] || {}
    const entry = Object.entries(processorBackups).find(([, backup]) => backup === backupPort)
    return entry ? Number(entry[0]) : null
  }, [backupPorts])

  // Fonctions pour gérer les processeurs backup
  const getBackupProcessor = useCallback((processorId: string) => {
    return backupProcessors[processorId] ?? null
  }, [backupProcessors])

  const getPrimaryProcessor = useCallback((backupProcessorId: string) => {
    const entry = Object.entries(backupProcessors).find(([, backupId]) => backupId === backupProcessorId)
    return entry ? entry[0] : null
  }, [backupProcessors])

  const isProcessorBackup = useCallback((processorId: string) => {
    return Object.values(backupProcessors).includes(processorId)
  }, [backupProcessors])


  // Overrides de couleurs par processeur/armoire et numéro de port/prise
  const [portColorOverrides, setPortColorOverrides] = useState<{ [processorId: string]: { [port: number]: string } }>({})
  const [outletColorOverrides, setOutletColorOverrides] = useState<{ [cabinetId: string]: { [outlet: number]: string } }>({})

  // Fonctions de couleurs optimisées avec useCallback
  const getPortColor = useCallback((portNumber: number, processorId?: string | null) => {
    if (processorId && portColorOverrides[processorId]?.[portNumber]) {
      return portColorOverrides[processorId][portNumber]
    }
    return PORT_COLORS[(portNumber - 1) % PORT_COLORS.length]
  }, [portColorOverrides])

  const getOutletColor = useCallback((outletNumber: number, cabinetId?: string | null) => {
    if (cabinetId && outletColorOverrides[cabinetId]?.[outletNumber]) {
      return outletColorOverrides[cabinetId][outletNumber]
    }
    return PORT_COLORS[(outletNumber - 1) % PORT_COLORS.length]
  }, [outletColorOverrides])

  // États pour l'historique (Undo/Redo)
  const [history, setHistory] = useState<{
    workspaceElements: WorkspaceElement[]
    cableRoutes: CableRoute[]
    panelConnections: PanelConnection[]
    powerConnections: PowerConnection[]
    mireConnections: MireConnection[]
    mirePowerConnections: MirePowerConnection[]
    backupPorts: { [processorId: string]: { [primaryPort: number]: number } }
    backupProcessors: { [primaryProcessorId: string]: string }
    elementGroups: { [groupId: string]: { name: string, elementIds: string[] } }
    description?: string  // Description de l'action pour le toast
    // Scope du snapshot : vue (data/power) + pages cibles au moment de l'action.
    // Indispensable pour restaurer sur la bonne page après un changement d'onglet.
    view?: 'data' | 'power'
    viewPageId?: string
    dataPageId?: string
    powerPageId?: string
  }[]>([])
  const [historyIndex, setHistoryIndex] = useState(-1)
  const [isUndoRedoOperation, setIsUndoRedoOperation] = useState(false)
  const [pendingHistoryDescription, setPendingHistoryDescription] = useState<string | null>(null)
  const [needsImmediateSave, setNeedsImmediateSave] = useState(false)

  // Référence du workspace SVG
  const workspaceRef = useRef<SVGSVGElement>(null)

  // Déselectionner les éléments lors du changement d'étape
  useEffect(() => {
    setSelectedElements([])
    setSelectedProcessorForConnection(null)
    setSelectedPort(null)
    setSelectedCabinetForConnection(null)
    setSelectedOutlet(null)
    setCurrentPanelForCabling(null)

    // Fermer tous les déroulants de marques de panneaux quand on arrive à l'étape 2 (Data)
    if (currentStep === 'data' && panels.length > 0) {
      const allPanelBrands = new Set(panels.map(panel => panel.brand))
      setCollapsedPanelBrands(allPanelBrands)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep])

  useEffect(() => {
    setMounted(true)
  }, [])

  // Auto-start onboarding for demo users on first load (only if not already completed/skipped)
  useEffect(() => {
    if (demoMode.state.isDemo && !isPro && !onboarding.state.isActive && !onboarding.hasCompletedOnboarding) {
      // Small delay to ensure UI is ready
      const timer = setTimeout(() => {
        onboarding.startOnboarding()
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [demoMode.state.isDemo, isPro, onboarding.state.isActive, onboarding.hasCompletedOnboarding, onboarding])

  // Charger le projet depuis l'URL au démarrage (si présent dans l'URL ou localStorage)
  useEffect(() => {
    if (mounted && user && isPro && !currentProject) {
      loadProjectFromUrl()
    }
  }, [mounted, user, isPro, currentProject, loadProjectFromUrl])

  // Charger les panneaux et armoires personnalisés au montage
  useEffect(() => {
    if (!mounted) return
    const loadCustomElements = async () => {
      if (isPro && authSession?.access_token && !isTauriApp) {
        try {
          const [panelsRes, cabinetsRes] = await Promise.all([
            fetch(getApiUrl('/api/synoptic/user-panels'), { headers: { Authorization: `Bearer ${authSession.access_token}` } }),
            fetch(getApiUrl('/api/synoptic/user-cabinets'), { headers: { Authorization: `Bearer ${authSession.access_token}` } })
          ])
          if (panelsRes.ok) {
            const { panels: userPanels } = await panelsRes.json()
            if (userPanels?.length) setPanels(prev => {
              const existingIds = new Set(prev.map((p: any) => p.id))
              const newOnes = userPanels.map((row: any) => ({ ...row.panel_data, id: row.panel_data?.id || `custom-panel-${row.id}` }))
              return [...prev, ...newOnes.filter((p: any) => !existingIds.has(p.id))]
            })
          }
          if (cabinetsRes.ok) {
            const { cabinets: userCabinets } = await cabinetsRes.json()
            if (userCabinets?.length) setPowerCabinets(prev => {
              const existingIds = new Set(prev.map((c: any) => c.id))
              const newOnes = userCabinets.map((row: any) => ({ ...row.cabinet_data, id: row.cabinet_data?.id || `custom-cabinet-${row.id}` }))
              return [...prev, ...newOnes.filter((c: any) => !existingIds.has(c.id))]
            })
          }
        } catch (e) { /* silently fail */ }
      } else {
        try {
          const storedPanels = JSON.parse(localStorage.getItem('vg_custom_panels') || '[]')
          if (storedPanels.length) setPanels(prev => {
            const existingIds = new Set(prev.map((p: any) => p.id))
            return [...prev, ...storedPanels.filter((p: any) => !existingIds.has(p.id))]
          })
          const storedCabinets = JSON.parse(localStorage.getItem('vg_custom_cabinets') || '[]')
          if (storedCabinets.length) setPowerCabinets(prev => {
            const existingIds = new Set(prev.map((c: any) => c.id))
            return [...prev, ...storedCabinets.filter((c: any) => !existingIds.has(c.id))]
          })
        } catch (e) { /* silently fail */ }
      }
    }
    loadCustomElements()
  }, [mounted, isPro, isTauriApp]) // eslint-disable-line react-hooks/exhaustive-deps

  // 🌐 Détection de l'état de connexion internet
  useEffect(() => {
    const updateConnectionStatus = () => {
      const online = navigator.onLine
      setIsOnline(online)

      debugLog('🌐 Changement état connexion', {
        online,
        timestamp: new Date().toISOString()
      })
    }

    // Écouter les événements de connexion
    window.addEventListener('online', updateConnectionStatus)
    window.addEventListener('offline', updateConnectionStatus)

    // État initial
    updateConnectionStatus()

    return () => {
      window.removeEventListener('online', updateConnectionStatus)
      window.removeEventListener('offline', updateConnectionStatus)
    }
  }, [debugLog])

  // 🏓 Ping automatique pour maintenir la session (toutes les 2 minutes)
  useEffect(() => {
    if (!user || !isPro) return

    const pingInterval = setInterval(async () => {
      if (document.visibilityState !== 'visible') {
        debugLog('⏸️ Ping ignoré (onglet inactif)')
        return
      }

      try {
        debugLog('🏓 Rafraîchissement session Supabase...')

        const healthyClient = await getHealthySupabaseClient()
        const pingTimeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('PING_TIMEOUT')), 8000)
        )
        const pingResult = await Promise.race([
          healthyClient.auth.refreshSession(),
          pingTimeoutPromise
        ]) as { data: any; error: any }

        if (pingResult.error || !pingResult.data?.session) {
          debugLog('❌ Erreur refresh session', { error: pingResult.error?.message })
          if (!authSession) {
            toast.warning(t('synoptic.toast.cloudSessionWarning'))
          }
        } else {
          debugLog('✅ Session rafraîchie avec succès')
        }
      } catch (error) {
        debugLog('❌ Erreur ping session', { error })
      }
    }, 2 * 60 * 1000)

    return () => clearInterval(pingInterval)
  }, [user, isPro, debugLog, authSession])

  // ⏱️ Suivi du temps depuis la dernière sauvegarde (toutes les secondes)
  useEffect(() => {
    const interval = setInterval(() => {
      if (lastSaveTime) {
        setTimeSinceLastSave(Date.now() - lastSaveTime)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [lastSaveTime])

  // Ref pour suivre si le projet a déjà été restauré
  const projectRestoredRef = useRef<string | null>(null)
  const hasShownToastRef = useRef<boolean>(false)

  // Restaurer les données du projet chargé (une seule fois par projet)
  useEffect(() => {
    if (!currentProject) {
      // Reset quand il n'y a pas de projet
      projectRestoredRef.current = null
      hasShownToastRef.current = false
      return
    }

    // Vérifier si ce projet a déjà été restauré
    if (projectRestoredRef.current === currentProject.id) {
      return
    }

    try {
      // 🧹 ÉTAPE 1 : NETTOYER COMPLÈTEMENT LE WORKSPACE

      // Réinitialiser les pages Data (vider complètement)
      setDataPages([{
        id: 'data-1',
        name: 'Data 1',
        workspaceElements: [],
        cableRoutes: [],
        panelConnections: [],
        mireConnections: []  // NEW: Mire connections
      }])
      setCurrentDataPageId('data-1')

      // Réinitialiser les pages Power (vider complètement)
      setPowerPages([{
        id: 'power-1',
        name: 'Power 1',
        workspaceElements: [],
        cableRoutes: [],
        powerConnections: [],
        mirePowerConnections: []  // NEW: Mire power connections
      }])
      setCurrentPowerPageId('power-1')

      // Réinitialiser les autres états
      setImportedLEDConfig(null)
      setHasVisitedPowerStep(false)
      setExportViewMode('data')
      setShowCartouche(true)
      setBackupPorts({})  // Réinitialiser les ports de backup

      // Marquer ce projet comme restauré AVANT la restauration pour éviter les multiples appels
      projectRestoredRef.current = currentProject.id

      // ÉTAPE 2 : RESTAURER LES DONNÉES DU PROJET (si elles existent)
      if (projectData && Object.keys(projectData).length > 0) {
        // C'est un projet existant avec des données sauvegardées
        const hasData = projectData.dataPages && projectData.dataPages.length > 0

        if (hasData) {
          // Désactiver la sauvegarde automatique pendant la restauration
          setSkipNextHistorySave(true)
          setIsUndoRedoOperation(true)

          // Projet existant → Restaurer et aller à l'étape Processeur

          if (projectData.hasVisitedPowerStep !== undefined) {
            setHasVisitedPowerStep(projectData.hasVisitedPowerStep)
          }

          if (projectData.importedLEDConfig) {
            setImportedLEDConfig(projectData.importedLEDConfig)
          }

          let restoredDataPages: DataPage[] | null = null
          let restoredPowerPages: PowerPage[] | null = null

          if (projectData.dataPages && Array.isArray(projectData.dataPages)) {
            // Sanitize : dédup connexions, orphelins, câbles inter-mode, points invalides
            const sanitizedDataPages = projectData.dataPages.map((page: DataPage) => sanitizeDataPage({
              ...page,
              workspaceElements: page.workspaceElements || [],
              cableRoutes: page.cableRoutes || [],
              panelConnections: page.panelConnections || [],
              mireConnections: page.mireConnections || []
            }))
            restoredDataPages = sanitizedDataPages
            setDataPages(sanitizedDataPages)
          }

          if (projectData.currentDataPageId) {
            setCurrentDataPageId(projectData.currentDataPageId)
          }

          if (projectData.powerPages && Array.isArray(projectData.powerPages)) {
            const sanitizedPowerPages = projectData.powerPages.map((page: PowerPage) => sanitizePowerPage({
              ...page,
              workspaceElements: page.workspaceElements || [],
              cableRoutes: page.cableRoutes || [],
              powerConnections: page.powerConnections || [],
              mirePowerConnections: page.mirePowerConnections || []
            }))
            restoredPowerPages = sanitizedPowerPages
            setPowerPages(sanitizedPowerPages)
          }

          if (projectData.currentPowerPageId) {
            setCurrentPowerPageId(projectData.currentPowerPageId)
          }

          if (projectData.exportViewMode) {
            setExportViewMode(projectData.exportViewMode)
          }

          if (projectData.showCartouche !== undefined) {
            setShowCartouche(projectData.showCartouche)
          }

          if (projectData.cartouche) {
            setCartouche(projectData.cartouche)
          }

          // Restaurer les ports de backup (redondances)
          if (projectData.backupPorts) {
            setBackupPorts(projectData.backupPorts)
          }
          
          // Restaurer les processeurs backup
          if (projectData.backupProcessors) {
            setBackupProcessors(projectData.backupProcessors)
          } else {
            // Aucun backupProcessors dans les données restaurées
            setBackupProcessors({})
          }

          // Restaurer les mires du projet
          if (projectData.projectMires && Array.isArray(projectData.projectMires)) {
            setProjectMires(projectData.projectMires)
          } else {
            setProjectMires([])
          }

          // Restaurer la configuration technique du mur
          if (projectData.wallTechnicalConfig) {
            setWallTechnicalConfig(projectData.wallTechnicalConfig)
          }

          // Réactiver la sauvegarde automatique après restauration
          setTimeout(() => {
            setSkipNextHistorySave(false)
            setIsUndoRedoOperation(false)

            // Initialiser l'historique avec l'état chargé comme point de départ
            setTimeout(() => {
              // Récupérer les données des pages courantes (versions assainies)
              const dataPageId = projectData.currentDataPageId || 'data-1'
              const powerPageId = projectData.currentPowerPageId || 'power-1'
              const currentPage = restoredDataPages?.find(p => p.id === dataPageId) || restoredDataPages?.[0]
              const currentPowerPage = restoredPowerPages?.find(p => p.id === powerPageId) || restoredPowerPages?.[0]

              if (currentPage) {
                const initialState = {
                  workspaceElements: currentPage.workspaceElements || [],
                  cableRoutes: currentPage.cableRoutes || [],
                  panelConnections: currentPage.panelConnections || [],
                  powerConnections: currentPowerPage?.powerConnections || [],
                  mireConnections: currentPage.mireConnections || [],
                  mirePowerConnections: currentPowerPage?.mirePowerConnections || [],
                  backupPorts: projectData.backupPorts || {},
                  backupProcessors: projectData.backupProcessors || {},
                  elementGroups: {},
                  description: 'État initial',
                  view: 'data' as const,
                  viewPageId: dataPageId,
                  dataPageId,
                  powerPageId,
                }
                setHistory([initialState])
                setHistoryIndex(0)
              }
            }, 100)
          }, 200)

          // Aller à l'étape Data
          setCurrentStep('data')

          // Afficher le toast une seule fois
          if (!hasShownToastRef.current) {
            toast.success(t('toast.projectRestored'))
            hasShownToastRef.current = true
          }
        } else {
          // Nouveau projet vide → Aller à l'étape Mire
          setCurrentStep('testPattern')

          // Afficher le toast une seule fois
          if (!hasShownToastRef.current) {
            toast.success(t('toast.projectLoaded', { name: currentProject.name }))
            hasShownToastRef.current = true
          }
        }
      } else {
        // Pas de données → Projet existant vide → Aller à l'étape Mire
        setCurrentStep('testPattern')

        // Afficher le toast avec le nom du projet
        if (!hasShownToastRef.current) {
          toast.success(t('toast.projectLoaded', { name: currentProject.name }))
          hasShownToastRef.current = true
        }
      }

    } catch (error) {
      toast.error(t('toast.projectRestorationError'))
    }
  }, [currentProject, t])

  // ========== ReadOnly: Charger les données du projet partagé ==========
  const sharedProjectRestoredRef = useRef<string | null>(null)
  useEffect(() => {
    if (!readOnly || !sharedProject || !sharedProject.data) return
    if (sharedProjectRestoredRef.current === sharedProject.id) return

    sharedProjectRestoredRef.current = sharedProject.id

    try {
      const data = sharedProject.data as Record<string, unknown>
      const hasData = Array.isArray(data.dataPages) && (data.dataPages as unknown[]).length > 0

      if (hasData) {
        if (data.hasVisitedPowerStep !== undefined) {
          setHasVisitedPowerStep(data.hasVisitedPowerStep as boolean)
        }
        if (data.importedLEDConfig) {
          setImportedLEDConfig(data.importedLEDConfig as typeof importedLEDConfig)
        }
        if (Array.isArray(data.dataPages)) {
          const updatedDataPages = (data.dataPages as DataPage[]).map((page: DataPage) => sanitizeDataPage({
            ...page,
            workspaceElements: page.workspaceElements || [],
            cableRoutes: page.cableRoutes || [],
            panelConnections: page.panelConnections || [],
            mireConnections: page.mireConnections || []
          }))
          setDataPages(updatedDataPages)
        }
        if (data.currentDataPageId) {
          setCurrentDataPageId(data.currentDataPageId as string)
        }
        if (Array.isArray(data.powerPages)) {
          const updatedPowerPages = (data.powerPages as PowerPage[]).map((page: PowerPage) => sanitizePowerPage({
            ...page,
            workspaceElements: page.workspaceElements || [],
            cableRoutes: page.cableRoutes || [],
            powerConnections: page.powerConnections || [],
            mirePowerConnections: page.mirePowerConnections || []
          }))
          setPowerPages(updatedPowerPages)
        }
        if (data.currentPowerPageId) {
          setCurrentPowerPageId(data.currentPowerPageId as string)
        }
        if (data.exportViewMode) {
          setExportViewMode(data.exportViewMode as string)
        }
        if (data.showCartouche !== undefined) {
          setShowCartouche(data.showCartouche as boolean)
        }
        if (data.cartouche) {
          setCartouche(data.cartouche as typeof cartouche)
        }
        if (data.backupPorts) {
          setBackupPorts(data.backupPorts as Record<string, unknown>)
        }
        if (data.backupProcessors) {
          setBackupProcessors(data.backupProcessors as Record<string, string>)
        }

        setProjectName(sharedProject.name || 'Projet partagé')
        setCurrentStep('data')
      }
    } catch {
      // Silently fail - project data may be malformed
    }
  }, [readOnly, sharedProject])

  // ========== Step Change Effects ==========

  // Deselect elements when step changes
  useEffect(() => {
    setSelectedElements([])
    setSelectedProcessorForConnection(null)
    setSelectedPort(null)
    setSelectedCabinetForConnection(null)
    setSelectedOutlet(null)
    setCurrentPanelForCabling(null)
    setCurrentMireCell(null)
  }, [currentStep])

  // Collapse all processor brands when entering data step
  useEffect(() => {
    if (currentStep === 'data') {
      const allProcessorBrands = Array.from(new Set(processors.map(p => p.brand)))
      setCollapsedProcessorBrands(new Set(allProcessorBrands))
    }
  }, [currentStep, processors])

  // Collapse all panel brands when entering data step
  useEffect(() => {
    if (currentStep === 'data') {
      const allPanelBrands = Array.from(new Set(panels.map(p => p.brand)))
      setCollapsedPanelBrands(new Set(allPanelBrands))
    }
  }, [currentStep, panels])

  // ✅ OPTIMISATION : Chargement CONDITIONNEL des données selon l'étape
  // Étape 1 (testPattern) : Rien à charger
  // Étape 2 (data) : Charger processeurs + panneaux
  // Étape 3 (cabling) : Charger tout (processeurs + panneaux + armoires)
  // Étape 4+ : Tout déjà chargé
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingData(true)
        const promises: Promise<void>[] = []

        // Étape 1 : testPattern - Rien à charger
        if (currentStep === 'testPattern') {
          setLoadingData(false)
          return
        }

        // Fonction helper pour charger les processeurs (API ou CSV selon l'environnement)
        const loadProcessors = async () => {
          if (isTauri) {
            const data = await loadProcessorsFromCSV()
            setProcessors(data)
          } else {
            const response = await fetch(getApiUrl('/api/synoptic/processors'))
            const data = await response.json()
            setProcessors(data)
          }
        }

        // Fonction helper pour charger les panneaux de base (API ou CSV) - fusionne avec les customs user
        const loadPanels = async () => {
          const [baseData, lwcData] = await Promise.all([
            isTauri
              ? loadPanelsFromCSV()
              : fetch(getApiUrl('/api/synoptic/panels')).then(r => r.json()),
            isTauri
              ? loadPanelsFromLWC()
              : fetch(getApiUrl('/api/synoptic/led-panels-db')).then(r => r.json()).catch(() => []),
          ])
          // Déduplication : LWC (données enrichies) prime sur base — clé = brand+name+pitch normalisés
          const normKey = (p: any) => {
            const b = (p.brand || '').toLowerCase().replace(/[\s\-_\.]+/g, '')
            const n = (p.name || '').toLowerCase().replace(/[\s\-_\.]+/g, '')
            const pitch = String(p.pixelPitch || 0)
            return `${b}|${n}|${pitch}`
          }
          const seen = new Set<string>()
          const deduped: any[] = []
          for (const p of [...(lwcData || []), ...(baseData || [])]) {
            const key = normKey(p)
            if (!seen.has(key)) { seen.add(key); deduped.push(p) }
          }
          const data = deduped
          basePanelsLoaded.current = true
          setPanels(prev => {
            // Garder les customs user (id ne commençant pas par 'panel-' ni 'lwc-'), ajouter les données de base
            const userCustoms = prev.filter((p: any) => !String(p.id).startsWith('panel-') && !String(p.id).startsWith('lwc-'))
            const baseIds = new Set(data.map((p: any) => p.id))
            return [...data, ...userCustoms.filter((p: any) => !baseIds.has(p.id))]
          })
        }

        // Fonction helper pour charger les armoires de base (API ou CSV) - fusionne avec les customs user
        const loadCabinets = async () => {
          const data = isTauri
            ? await loadCabinetsFromCSV()
            : await fetch(getApiUrl('/api/synoptic/cabinets')).then(r => r.json())
          baseCabinetsLoaded.current = true
          setPowerCabinets(prev => {
            // Garder les customs user (id ne commençant pas par 'cabinet-'), ajouter les données de base
            const userCustoms = prev.filter((c: any) => !String(c.id).startsWith('cabinet-'))
            const baseIds = new Set(data.map((c: any) => c.id))
            return [...data, ...userCustoms.filter((c: any) => !baseIds.has(c.id))]
          })
        }

        // Étape 2 : data - Charger processeurs + panneaux
        if (currentStep === 'data') {
          if (processors.length === 0) {
            promises.push(loadProcessors())
          }
          if (!basePanelsLoaded.current) {
            promises.push(loadPanels())
          }
        }

        // Étape 3 : cabling - Charger tout si manquant
        if (currentStep === 'cabling') {
          if (processors.length === 0) {
            promises.push(loadProcessors())
          }
          if (!basePanelsLoaded.current) {
            promises.push(loadPanels())
          }
          if (!baseCabinetsLoaded.current) {
            promises.push(loadCabinets())
          }
        }

        // Étape export - Charger tout si pas déjà fait
        if (currentStep === 'export') {
          if (processors.length === 0) {
            promises.push(loadProcessors())
          }
          if (!basePanelsLoaded.current) {
            promises.push(loadPanels())
          }
          if (!baseCabinetsLoaded.current) {
            promises.push(loadCabinets())
          }
        }

        // ✅ Chargement PARALLÈLE de ce qui est nécessaire
        await Promise.all(promises)

        setLoadingData(false)
      } catch (error) {
        // console.error('Error loading data:', error)
        setLoadingData(false)
      }
    }

    loadData()
  }, [currentStep, isTauri]) // ✅ Réagir au changement d'étape et d'environnement

  // ✅ OPTIMISATION Phase 2 : Préchargement intelligent de l'étape suivante
  // Précharge les données de l'étape suivante en arrière-plan après 2s
  // Rend les transitions entre étapes instantanées
  useEffect(() => {
    const timer = setTimeout(() => {
      // Précharger l'étape suivante selon l'étape actuelle
      if (currentStep === 'testPattern' && processors.length === 0) {
        // Étape 1 → Précharger étape 2 (processeurs et panneaux pour Data)
        if (isTauri) {
          loadProcessorsFromCSV()
            .then(data => setProcessors(data))
            .catch(() => { })
        } else {
          fetch(getApiUrl('/api/synoptic/processors'))
            .then(r => r.json())
            .then(data => setProcessors(data))
            .catch(() => { })
        }
      } else if (currentStep === 'data' && !baseCabinetsLoaded.current) {
        // Étape 2 → Précharger étape 3 (armoires) - fusionner avec les customs existants
        const loadData = () => isTauri ? loadCabinetsFromCSV() : fetch(getApiUrl('/api/synoptic/cabinets')).then(r => r.json())
        loadData().then(data => {
          baseCabinetsLoaded.current = true
          setPowerCabinets(prev => {
            const userCustoms = prev.filter((c: any) => !String(c.id).startsWith('cabinet-'))
            const baseIds = new Set(data.map((c: any) => c.id))
            return [...data, ...userCustoms.filter((c: any) => !baseIds.has(c.id))]
          })
        }).catch(() => { })
      }
    }, 2000) // Attendre 2s après le chargement de l'étape actuelle

    return () => clearTimeout(timer)
  }, [currentStep, processors.length, powerCabinets.length, isTauri])

  // Fonction pour capturer l'image de la mire depuis l'iframe
  const captureMireImage = useCallback(async (retryCount = 0): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
      // Demander à l'iframe de capturer et envoyer l'image
      // Utiliser un sélecteur plus robuste qui ne dépend pas de la traduction
      const iframe = document.querySelector('iframe[src*="/grid/"]') as HTMLIFrameElement
      
      if (!iframe) {
        console.error('Iframe du générateur de mires introuvable dans le DOM');
        reject('Iframe non trouvée')
        return
      }
      
      if (!iframe.contentWindow) {
        console.error('contentWindow de l\'iframe non accessible');
        reject('Iframe non accessible')
        return
      }
      
      // Vérifier que l'iframe est bien chargée
      if (!iframe.contentDocument || iframe.contentDocument.readyState !== 'complete') {
        console.warn('Iframe pas encore complètement chargée, retry...');
        if (retryCount < 2) {
          setTimeout(async () => {
            try {
              const result = await captureMireImage(retryCount + 1)
              resolve(result)
            } catch (error) {
              reject(error)
            }
          }, 1000)
          return
        } else {
          reject('Iframe non chargée après plusieurs tentatives')
          return
        }
      }

      // Générer un ID unique pour cette requête pour éviter les race conditions
      const requestId = `capture_${Date.now()}_${Math.random()}`
      let timeoutId: NodeJS.Timeout | null = null
      let resolved = false

      // Écouter la réponse de l'iframe
      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return
        if (resolved) return // Déjà résolu, ignorer

        if (event.data && event.data.type === 'MIRE_IMAGE_CAPTURE' && event.data.requestId === requestId) {
          resolved = true
          if (timeoutId) clearTimeout(timeoutId)
          window.removeEventListener('message', handleMessage)
          resolve(event.data.imageData)
        }
      }

      window.addEventListener('message', handleMessage)

      // Demander à l'iframe de capturer l'image avec l'ID unique
      iframe.contentWindow.postMessage({ type: 'CAPTURE_MIRE_IMAGE', requestId }, window.location.origin)

      // Timeout après 5 secondes (augmenté de 3s à 5s) avec retry
      timeoutId = setTimeout(async () => {
        if (resolved) return // Déjà résolu entre-temps
        
        resolved = true
        window.removeEventListener('message', handleMessage)

        // Retry jusqu'à 3 fois
        if (retryCount < 2) {
          try {
            const result = await captureMireImage(retryCount + 1)
            resolve(result)
          } catch (error) {
            reject(error)
          }
        } else {
          reject('Timeout lors de la capture de l\'image après 3 tentatives')
        }
      }, 5000)
    })
  }, [])

  // Recharger une mire sauvegardée dans l'éditeur (iframe /grid) pour modification
  const loadMireForEditing = useCallback((index: number) => {
    const mire = savedMires[index]
    if (!mire) return

    const iframe = document.querySelector('iframe[src*="/grid/"]') as HTMLIFrameElement | null
    if (!iframe?.contentWindow) {
      toast.error(t('synoptic.toast.mireEditorNotReady'))
      return
    }

    const sendMire = () => {
      iframe.contentWindow?.postMessage({ type: 'LOAD_MIRE_CONFIG', mire }, window.location.origin)
    }

    // Si l'iframe n'est pas encore chargée, attendre son événement load
    if (iframe.contentDocument?.readyState === 'complete') {
      sendMire()
    } else {
      iframe.addEventListener('load', sendMire, { once: true })
    }

    setEditingMireIndex(index)
    setCurrentMireName(mire.name || `Mire ${index + 1}`)

    // Timeout de sécurité : si /grid n'accuse pas réception, annuler le mode édition
    if (mireLoadAckTimeoutRef.current) clearTimeout(mireLoadAckTimeoutRef.current)
    mireLoadAckTimeoutRef.current = setTimeout(() => {
      toast.error(t('synoptic.toast.mireEditorLoadTimeout'))
      setEditingMireIndex(null)
      setCurrentMireName('')
    }, 3000)
  }, [savedMires, t])

  // Sauvegarder la mire en attente : écraser la mire en cours d'édition ou l'ajouter comme nouvelle
  const handleSaveMireToList = useCallback(async (mode: 'append' | 'overwrite') => {
    if (!pendingLEDConfig) return

    const toastId = toast.loading(t('synoptic.toast.capturingPattern') || 'Capture en cours...')
    try {
      const mireImage = await captureMireImage()
      const isOverwrite = mode === 'overwrite' && editingMireIndex !== null
      const existingMire = isOverwrite ? savedMires[editingMireIndex] : null
      const mireName = currentMireName.trim() || existingMire?.name || `Mire ${isOverwrite ? editingMireIndex + 1 : savedMires.length + 1}`

      const configWithImage = {
        ...pendingLEDConfig,
        name: mireName,
        capturedImage: mireImage,
        // En écrasement, préserver le timestamp d'origine pour garder l'identité de la mire
        ...(isOverwrite && existingMire?.timestamp ? { timestamp: existingMire.timestamp } : {})
      }

      if (isOverwrite) {
        setSavedMires(prev => prev.map((m, i) => i === editingMireIndex ? configWithImage : m))
      } else {
        setSavedMires(prev => [...prev, configWithImage])
        if (onboarding.state.isActive) {
          onboarding.completeAction('mire-added-to-list')
        }
      }

      setCurrentMireName('')
      setEditingMireIndex(null)
      setPendingLEDConfig(null)
      setHasMireGenerated(false)

      const toastKey = isOverwrite ? 'toast.patternUpdated' : 'toast.patternAdded'
      toast.success(t(toastKey, { name: mireName }), {
        description: `${pendingLEDConfig.ledWallMode?.columns ?? '?'}×${pendingLEDConfig.ledWallMode?.rows ?? '?'} panneaux`,
        id: toastId
      })
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la mire:', error)
      toast.error(t('synoptic.toast.mireAddError'), {
        description: error instanceof Error ? error.message : 'Veuillez réessayer',
        id: toastId
      })
    }
  }, [pendingLEDConfig, editingMireIndex, savedMires, currentMireName, captureMireImage, onboarding, t])

  // Écouter les messages postMessage de l'iframe (générateur de mires)
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      // Vérifier l'origine pour la sécurité
      if (event.origin !== window.location.origin) return

      // ✅ Ne traiter les messages que si on est sur l'étape testPattern
      if (currentStep !== 'testPattern') return

      // Accusé de réception du chargement d'une mire dans l'éditeur
      if (event.data && event.data.type === 'MIRE_CONFIG_LOADED') {
        if (mireLoadAckTimeoutRef.current) {
          clearTimeout(mireLoadAckTimeoutRef.current)
          mireLoadAckTimeoutRef.current = null
        }
        return
      }

      // Vérifier que c'est bien un message de config LED
      if (event.data && event.data.type === 'LED_CONFIG_UPDATE') {
        // ✅ Stocker temporairement la config SANS capturer l'image
        // L'utilisateur devra cliquer sur le bouton pour valider
        setPendingLEDConfig(event.data)
        setHasMireGenerated(true)

        // Onboarding: tracker les modifications de mire
        if (onboarding.state.isActive) {
          mireModificationCountRef.current += 1
          onboarding.completeAction('mire-customized', mireModificationCountRef.current)
        }
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [currentStep, onboarding])

  // ✅ NOUVELLE APPROCHE : Afficher la VRAIE image de la mire d'abord
  // Conversion en panneaux seulement quand l'utilisateur sélectionne un port
  const mireImageRef = useRef<string | null>(null)
  const hasMireImage = useRef(false)
  const lastMireTimestamp = useRef<number>(0)

  useEffect(() => {
    if (!importedLEDConfig || !hasMireGenerated) {
      return
    }

    // Nouvelle mire ? Réinitialiser
    if (importedLEDConfig.timestamp !== lastMireTimestamp.current) {
      hasMireImage.current = false
      lastMireTimestamp.current = importedLEDConfig.timestamp
    }

    if (hasMireImage.current) {
      return // Déjà créé
    }

    if (!importedLEDConfig.ledWallMode) {
      return // Config invalide — ne pas crasher sur la destructuration
    }
    const { columns, rows } = importedLEDConfig.ledWallMode
    const totalPanels = columns * rows

    // Utiliser l'image déjà capturée (stockée dans importedLEDConfig)
    const mireImageData = importedLEDConfig.capturedImage

    if (!mireImageData) {
      return
    }

    // Créer un élément IMAGE avec la vraie image de la mire
    const { panelWidth, panelHeight } = importedLEDConfig.ledWallMode
    // Respecter le ratio réel des panneaux (base 40 pour la plus grande dimension)
    const BASE_PANEL_SIZE = 40
    const panelAspect = panelWidth / panelHeight
    const workspacePanelWidth = panelAspect >= 1 ? BASE_PANEL_SIZE : BASE_PANEL_SIZE * panelAspect
    const workspacePanelHeight = panelAspect >= 1 ? BASE_PANEL_SIZE / panelAspect : BASE_PANEL_SIZE
    const totalWidth = columns * workspacePanelWidth
    const totalHeight = rows * workspacePanelHeight
    const mireName = `${importedLEDConfig.name || 'Grille'} - ${panelWidth}×${panelHeight}px - ${columns}×${rows}`

    const mireImageElement: WorkspaceElement = {
      id: `mire_image_${Date.now()}`,
      type: 'image',
      data: {
        name: mireName,
        mireConfig: importedLEDConfig,
        isPendingConversion: true,
        imageData: mireImageData // ✅ Utiliser l'image déjà capturée
      },
      position: {
        x: -totalWidth / 2,
        y: -totalHeight / 2
      },
      size: {
        width: totalWidth,
        height: totalHeight
      }
    }

    const configTimestamp = importedLEDConfig.timestamp
    const isMireAlreadyPresent = (elements: WorkspaceElement[]) => elements.some(el => {
      if (el.type !== 'image') return false
      const data = el.data as { isPendingConversion?: boolean; mireConfig?: { timestamp?: number } } | undefined
      if (data?.isPendingConversion) return true
      return data?.mireConfig?.timestamp === configTimestamp
    })

    // Ne jamais insérer une seconde mire pour la même config (changement de
    // couleur, switch d'onglet, retour sur le projet). Garde au niveau render…
    if (isMireAlreadyPresent(workspaceElements)) {
      hasMireImage.current = true
      return
    }

    // …et garde autoritaire dans l'updater pour couvrir les courses d'état.
    setWorkspaceElements(prev => {
      if (isMireAlreadyPresent(prev)) return prev
      return [...prev, mireImageElement]
    })

    mireImageRef.current = mireImageElement.id
    hasMireImage.current = true

    toast.success(t('synoptic.toast.mireImported'), {
      description: `${columns}×${rows} panneaux`,
      duration: 5000
    })
  }, [importedLEDConfig, hasMireGenerated, workspaceElements, t])

  // ========== Mire Virtual Grid (extracted to hooks/useMireGrid.ts) ==========
  // Refs for functions declared later in the component (avoids initialization order issues)
  const setSkipNextHistorySaveRef = useRef<(v: boolean) => void>(() => {})
  const saveToHistoryImmediateRef = useRef<(desc: string) => void>(() => {})
  const mireGrid = useMireGrid({
    workspaceElements,
    mireConnections,
    mirePowerConnections,
    readOnly,
    getDataSelection: getDataWiringSelection,
    getPowerSelection: getPowerWiringSelection,
    applyDataPageWiring,
    applyPowerPageWiring,
    setCurrentMireCell: setCurrentMireCellNow,
    setSkipNextHistorySaveRef,
    saveToHistoryImmediateRef,
    onboarding,
  })
  const { isCellConnected, getCellConnection, handleMireCellClickForData, handleMireCellClickForPower } = mireGrid

  // Handlers mire via ref → l'écouteur clavier appelle toujours la version fraîche
  const mireHandlersRef = useRef({ handleMireCellClickForData, handleMireCellClickForPower })
  mireHandlersRef.current = { handleMireCellClickForData, handleMireCellClickForPower }


  // DEPRECATED: convertMireImageToPanels and auto-conversion code removed (was dead code)

  // Charger la config LED depuis localStorage au montage (persistance)
  useEffect(() => {
    const savedConfig = localStorage.getItem('visualgrid_led_config')
    if (savedConfig) {
      try {
        const config = JSON.parse(savedConfig)
        // Vérifier que la config n'est pas trop vieille (max 24h)
        const age = Date.now() - config.timestamp
        const maxAge = 24 * 60 * 60 * 1000 // 24 heures

        if (age < maxAge) {
          setImportedLEDConfig(config)
          setHasMireGenerated(true)
        } else {
          // Config trop vieille, la supprimer
          localStorage.removeItem('visualgrid_led_config')
        }
      } catch (error) {
        // console.warn('[LocalStorage] Failed to parse LED config:', error)
      }
    }
  }, [])

  // Créer une configuration par défaut si aucune n'est chargée
  useEffect(() => {
    // Si aucune config n'est chargée et qu'on est sur l'étape testPattern, créer une config par défaut
    if (!importedLEDConfig && currentStep === 'testPattern') {
      const defaultConfig = {
        timestamp: Date.now(),
        width: 1920,
        height: 1152,
        gridSize: 192,
        ledWallMode: {
          enabled: true,
          columns: 10,
          rows: 6,
          panelWidth: 192,
          panelHeight: 192,
          borderWidth: 2
        }
      }

      setImportedLEDConfig(defaultConfig)
      setHasMireGenerated(true)
    }
  }, [currentStep, importedLEDConfig])

  // Ref pour accéder aux valeurs actuelles du viewBox dans l'écouteur
  const viewBoxRef = useRef(viewBox)
  viewBoxRef.current = viewBox

  // Écouter les mouvements de souris globalement pour le preview des panneaux
  useEffect(() => {
    if (!selectedPanelForPlacement || !mounted || !isViewBoxInitialized) return

    const handleGlobalMouseMove = (event: MouseEvent) => {
      if (!workspaceRef.current) return

      // Utiliser directement les coordonnées de l'événement natif
      const rect = workspaceRef.current.getBoundingClientRect()

      // Vérifier que le rect est valide
      if (rect.width === 0 || rect.height === 0) return

      // Convertir les coordonnées écran en coordonnées normalisées (0-1)
      const normalizedX = (event.clientX - rect.left) / rect.width
      const normalizedY = (event.clientY - rect.top) / rect.height

      // Utiliser les valeurs actuelles du viewBox via la ref
      const currentViewBox = viewBoxRef.current
      const svgX = currentViewBox.x + normalizedX * currentViewBox.width
      const svgY = currentViewBox.y + normalizedY * currentViewBox.height


      setMousePosition({ x: svgX, y: svgY })
    }

    // Ajouter l'écouteur global immédiatement
    document.addEventListener('mousemove', handleGlobalMouseMove)

    // Nettoyer à la désélection
    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove)
    }
  }, [selectedPanelForPlacement, mounted, isViewBoxInitialized, viewBox])


  // Sauvegarder l'état initial dans l'historique
  useEffect(() => {
    if (mounted && history.length === 0) {
      const initialState = {
        workspaceElements: [...workspaceElements],
        cableRoutes: [...cableRoutes],
        panelConnections: [...panelConnections],
        powerConnections: [...powerConnections],
        mireConnections: [...mireConnections],
        mirePowerConnections: [...mirePowerConnections],
        backupPorts: JSON.parse(JSON.stringify(backupPorts)),
        backupProcessors: JSON.parse(JSON.stringify(backupProcessors)),
        elementGroups: JSON.parse(JSON.stringify(elementGroups))
      }
      setHistory([initialState])
      setHistoryIndex(0)
    }
  }, [mounted, workspaceElements, cableRoutes, panelConnections, powerConnections, mireConnections, mirePowerConnections, backupPorts, backupProcessors, elementGroups, history.length])

  // Gérer les mouvements d'éléments pour éviter la sauvegarde continue
  const [skipNextHistorySave, setSkipNextHistorySave] = useState(false)
  setSkipNextHistorySaveRef.current = setSkipNextHistorySave

  useEffect(() => {
    if (isDraggingElement || isResizing) {
      setSkipNextHistorySave(true)
    } else if (hasMoved) {
      setHasMoved(false)
      // Laisser la sauvegarde immédiate se faire d'abord (50ms), puis réactiver l'auto-save
      setTimeout(() => setSkipNextHistorySave(false), 100)
    }
  }, [isDraggingElement, isResizing, hasMoved])

  // Réinitialiser le panneau actif pour câblage Power si l'armoire ou la prise est désélectionnée
  useEffect(() => {
    if (!selectedCabinetForConnection || selectedOutlet === null) {
      setCurrentPanelForCabling(null)
    }
  }, [selectedCabinetForConnection, selectedOutlet])

  // Réinitialiser le panneau actif pour câblage Data si le processeur ou le port est désélectionné
  useEffect(() => {
    if (!selectedProcessorForConnection || selectedPort === null) {
      setCurrentPanelForCabling(null)
    }
  }, [selectedProcessorForConnection, selectedPort])

  // Réinitialiser le panneau actif pour câblage quand on change d'étape
  useEffect(() => {
    if (currentStep !== 'cabling' && currentStep !== 'data') {
      setCurrentPanelForCabling(null)
    }
  }, [currentStep])

  // Gérer le dégroupement/regroupement automatique des mires en fonction de la sélection de port/outlet
  useEffect(() => {
    const permanentGroups = Object.entries(elementGroups).filter(([_, group]) => group.permanent)

    if (permanentGroups.length === 0) return

    // Si un port ou outlet est sélectionné, dégrouper les mires pour permettre le câblage individuel
    if (selectedPort !== null || selectedOutlet !== null) {
      const needsUngrouping = permanentGroups.some(([groupId, group]) => {
        return workspaceElements.some(el => el.groupId === groupId)
      })

      if (needsUngrouping) {
        permanentGroups.forEach(([groupId, group]) => {
          // Retirer le groupId de tous les éléments du groupe
          setWorkspaceElements(prev => prev.map(el =>
            group.elementIds.includes(el.id)
              ? { ...el, groupId: undefined }
              : el
          ))
        })

        // Marquer les groupes comme temporairement dégroupés (ne pas les supprimer)
        setElementGroups(prev => {
          const newGroups = { ...prev }
          let hasChanges = false
          permanentGroups.forEach(([groupId, group]) => {
            if (!(group as any).isTemporarilyUngrouped) {
              newGroups[groupId] = { ...group, isTemporarilyUngrouped: true }
              hasChanges = true
            }
          })
          return hasChanges ? newGroups : prev
        })
      }
    }
    // Si aucun port/outlet n'est sélectionné, regrouper les mires
    else {
      const needsRegrouping = permanentGroups.some(([_, group]) => (group as any).isTemporarilyUngrouped)

      if (needsRegrouping) {
        permanentGroups.forEach(([groupId, group]) => {
          // Réassigner le groupId à tous les éléments du groupe
          setWorkspaceElements(prev => prev.map(el =>
            group.elementIds.includes(el.id)
              ? { ...el, groupId }
              : el
          ))
        })

        // Retirer le flag temporaire
        setElementGroups(prev => {
          const newGroups = { ...prev }
          let hasChanges = false
          permanentGroups.forEach(([groupId, group]) => {
            if ((group as any).isTemporarilyUngrouped) {
              const { isTemporarilyUngrouped, ...cleanGroup } = group as any
              newGroups[groupId] = cleanGroup
              hasChanges = true
            }
          })
          return hasChanges ? newGroups : prev
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPort, selectedOutlet])

  // Gestionnaire de touches pour la suppression et désélection
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignorer si on est dans un input ou textarea
      const target = event.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return

      if ((event.key === 'Delete' || event.key === 'Backspace') && selectedElements.length > 0 && !readOnly) {
        event.preventDefault()

        // Générer une description pour l'historique
        const elementsToDelete = workspaceElements.filter(el => selectedElements.includes(el.id))
        let description = 'Élément supprimé'

        if (elementsToDelete.length === 1) {
          const elementType = elementsToDelete[0].type === 'processor' ? 'Processeur' :
            elementsToDelete[0].type === 'panel' ? 'Panneau' :
              elementsToDelete[0].type === 'cabinet' ? 'Armoire' :
                elementsToDelete[0].type === 'image' ? 'Mire' : 'Élément'
          description = `${elementType} supprimé`
        } else if (elementsToDelete.length > 1) {
          description = `${elementsToDelete.length} éléments supprimés`
        }

        // Supprimer tous les éléments sélectionnés et leurs câbles associés
        setWorkspaceElements(prev => prev.filter(elem => !selectedElements.includes(elem.id)))

        // Supprimer les câbles associés (Data et Power)
        setCableRoutes(prev => prev.filter(route => {
          // Supprimer si from ou to est dans les éléments supprimés
          if (selectedElements.includes(route.from) || selectedElements.includes(route.to)) {
            return false
          }

          // Supprimer les câbles des cellules de mire (from/to = imageId_cell_row_col)
          if (selectedElements.some(id => route.from.startsWith(`${id}_cell_`) || route.to.startsWith(`${id}_cell_`))) {
            return false
          }

          // Pour les câbles Power, vérifier aussi si l'armoire source a été supprimée
          if (route.type === 'power') {
            const powerConn = powerConnections.find(conn => conn.panelId === route.to)
            if (powerConn && selectedElements.includes(powerConn.cabinetId)) {
              return false // Supprimer le câble si son armoire est supprimée
            }
          }

          return true
        }))

        setPanelConnections(prev => prev.filter(conn => !selectedElements.includes(conn.panelId) && !selectedElements.includes(conn.processorId)))
        setPowerConnections(prev => prev.filter(conn => !selectedElements.includes(conn.panelId) && !selectedElements.includes(conn.cabinetId)))
        setMireConnections(prev => prev.filter(conn => !selectedElements.includes(conn.imageId) && !selectedElements.includes(conn.processorId)))
        setMirePowerConnections(prev => prev.filter(conn => !selectedElements.includes(conn.imageId) && !selectedElements.includes(conn.cabinetId)))

        // Désélectionner l'armoire ou le processeur si supprimé
        if (selectedCabinetForConnection && selectedElements.includes(selectedCabinetForConnection)) {
          setSelectedCabinetForConnection(null)
          setSelectedOutlet(null)
        }
        if (selectedProcessorForConnection && selectedElements.includes(selectedProcessorForConnection)) {
          setSelectedProcessorForConnection(null)
          setSelectedPort(null)
        }

        setSelectedElements([])

        // Sauvegarder immédiatement avec description
        saveToHistoryImmediate(description)
      }
      if (event.key === 'Escape') {
        event.preventDefault()
        setSelectedElements([])
        setSelectedPanelForPlacement(null)
        setCurrentPanelForCabling(null) // Réinitialiser aussi le câblage Power
        setCabinetLinkMode(false) // Annuler le mode liaison armoire → armoire
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedElements, workspaceElements, powerConnections, selectedCabinetForConnection, selectedProcessorForConnection])
  // Gestionnaire de touches pour les raccourcis
  useEffect(() => {
    const handleKeyDownShortcuts = (event: KeyboardEvent) => {
      // Ignore if typing in an input or editable element
      const target = event.target as HTMLElement
      const isEditable = target.tagName === 'INPUT' || 
                        target.tagName === 'TEXTAREA' || 
                        target.isContentEditable ||
                        target.getAttribute('contenteditable') === 'true' ||
                        target.closest('[contenteditable="true"]')
      
      if (isEditable) {
        return
      }

      // Bloquer les raccourcis d'édition en mode readOnly
      if (!readOnly) {
        // Raccourcis clavier pour undo/redo
        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
          event.preventDefault()
          undo()
          return
        }

        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'y') {
          event.preventDefault()
          redo()
          return
        }

        // Raccourcis copier-coller
        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'c') {
          event.preventDefault()
          copySelectedElements()
          return
        }

        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'v') {
          event.preventDefault()
          pasteElements()
          return
        }
      }

      if (event.key.toLowerCase() === 'v' && !event.ctrlKey) {
        setCursorMode('select')
      } else if (event.key.toLowerCase() === 'h') {
        setCursorMode('pan')
      }
    }

    window.addEventListener('keydown', handleKeyDownShortcuts)
    return () => {
      window.removeEventListener('keydown', handleKeyDownShortcuts)
    }
  }, [historyIndex, history.length, selectedElements, clipboard, workspaceElements, cableRoutes])

  // ========== Export SVG/PNG/PDF (extracted to hooks/useExportSvg.ts) ==========
  const {
    createExportSvgForDataPage,
    createExportSvgForPowerPage,
    createExportSvg,
    exportToSVG,
    exportToImage,
    exportToPDF,
    getExportFilename,
  } = useExportSvg({
    workspaceRef,
    isTauriApp,
    isTauri,
    isPro,
    isPowerMode,
    backupPorts,
    backupProcessors,
    showCartouche,
    cartouche,
    showExportProcessorLink,
    dataPages,
    powerPages,
    currentDataPageId,
    currentPowerPageId,
    currentDataPage,
    currentPowerPage,
    demoMode,
    onboarding,
    portColorOverrides,
    outletColorOverrides,
    getActualImageBounds,
    isProcessorBackup,
    getBackupProcessor,
    setIsExporting,
    setExportProgress,
    setShowExportSuccess,
  })

  // Fonction de comparaison d'états — compare le scope (page/vue) ET le contenu
  // réel des connexions/câbles, pas seulement les longueurs
  const areStatesEqual = useCallback((state1: any, state2: any) => {
    if (!state1 || !state2) return false

    // Même scope ? (un snapshot d'une autre page n'est jamais « égal »)
    if ((state1.viewPageId ?? null) !== (state2.viewPageId ?? null)) return false
    if ((state1.dataPageId ?? null) !== (state2.dataPageId ?? null)) return false
    if ((state1.powerPageId ?? null) !== (state2.powerPageId ?? null)) return false

    // Comparaison rapide par longueur
    if (state1.workspaceElements.length !== state2.workspaceElements.length) return false
    if (state1.cableRoutes.length !== state2.cableRoutes.length) return false
    if (state1.panelConnections.length !== state2.panelConnections.length) return false
    if (state1.powerConnections.length !== state2.powerConnections.length) return false
    if ((state1.mireConnections?.length ?? 0) !== (state2.mireConnections?.length ?? 0)) return false
    if ((state1.mirePowerConnections?.length ?? 0) !== (state2.mirePowerConnections?.length ?? 0)) return false

    // Comparaison par hash incluant position ET taille
    const elementsHash = (elements: WorkspaceElement[]) =>
      elements.map(e => `${e.id}-${e.position.x}-${e.position.y}-${e.size.width}-${e.size.height}`).join(',')
    if (elementsHash(state1.workspaceElements) !== elementsHash(state2.workspaceElements)) return false

    // Contenu des câbles (endpoints) et des connexions (ordres inclus)
    const cablesHash = (routes: CableRoute[]) =>
      routes.map(r => `${r.id}:${r.type}:${r.from}>${r.to}`).join('|')
    if (cablesHash(state1.cableRoutes) !== cablesHash(state2.cableRoutes)) return false

    const connectionsHash = (conns: ReadonlyArray<PanelConnection | PowerConnection | MireConnection | MirePowerConnection>) =>
      conns.map(c => JSON.stringify(c)).join('|')
    if (connectionsHash(state1.panelConnections) !== connectionsHash(state2.panelConnections)) return false
    if (connectionsHash(state1.powerConnections) !== connectionsHash(state2.powerConnections)) return false
    if (connectionsHash(state1.mireConnections || []) !== connectionsHash(state2.mireConnections || [])) return false
    if (connectionsHash(state1.mirePowerConnections || []) !== connectionsHash(state2.mirePowerConnections || [])) return false

    return true
  }, [])

  /** Construit un snapshot complet avec le scope page/mode au moment de l'action */
  const buildHistorySnapshot = useCallback((description?: string) => ({
    workspaceElements: [...workspaceElements],
    cableRoutes: [...cableRoutes],
    panelConnections: [...panelConnections],
    powerConnections: [...powerConnections],
    mireConnections: [...mireConnections],
    mirePowerConnections: [...mirePowerConnections],
    backupPorts: JSON.parse(JSON.stringify(backupPorts)),
    backupProcessors: JSON.parse(JSON.stringify(backupProcessors)),
    elementGroups: JSON.parse(JSON.stringify(elementGroups)),
    view: (showPowerInCurrentView ? 'power' : 'data') as 'data' | 'power',
    viewPageId: showPowerInCurrentView ? currentPowerPageId : currentDataPageId,
    dataPageId: currentDataPageId,
    powerPageId: currentPowerPageId,
    ...(description ? { description } : {})
  }), [workspaceElements, cableRoutes, panelConnections, powerConnections, mireConnections, mirePowerConnections, backupPorts, backupProcessors, elementGroups, showPowerInCurrentView, currentPowerPageId, currentDataPageId])

  // Fonction de sauvegarde d'historique debounced - OPTIMISÉ
  // Utiliser un callback ref pour éviter les problèmes de closure
  const historyIndexRef = useRef(historyIndex)
  historyIndexRef.current = historyIndex
  // Scope page/mode du dernier snapshot — un switch d'onglet n'est pas une édition
  const historyScopeRef = useRef<string | null>(null)

  const saveToHistoryDebouncedRef = useRef<ReturnType<typeof debounce> | null>(null)

  if (!saveToHistoryDebouncedRef.current) {
    saveToHistoryDebouncedRef.current = debounce((state: any) => {
      setHistory(prev => {
        const currentIndex = historyIndexRef.current
        const newHistory = prev.slice(0, currentIndex + 1)
        newHistory.push(state)
        return newHistory.slice(-50)
      })
      setHistoryIndex(prev => Math.min(prev + 1, 49))
      setPendingHistoryDescription(null) // Réinitialiser la description après sauvegarde
    }, 300) // Attendre 300ms après la dernière action
  }

  const saveToHistoryDebounced = saveToHistoryDebouncedRef.current

  // useEffect pour sauvegarder immédiatement quand le flag est activé
  useEffect(() => {
    if (!needsImmediateSave || !mounted || isUndoRedoOperation || !pendingHistoryDescription) return

    const currentState = buildHistorySnapshot(pendingHistoryDescription)

    // Éviter de sauvegarder le même état plusieurs fois
    if (history.length > 0) {
      const lastState = history[historyIndex]
      if (!areStatesEqual(currentState, lastState)) {
        // Annuler tout debounce en cours
        if (saveToHistoryDebounced && typeof saveToHistoryDebounced.cancel === 'function') {
          saveToHistoryDebounced.cancel()
        }

        // Sauvegarder immédiatement
        setHistory(prev => {
          const newHistory = prev.slice(0, historyIndex + 1)
          newHistory.push(currentState)
          return newHistory.slice(-50)
        })
        setHistoryIndex(prev => Math.min(prev + 1, 49))
      }
    } else {
      // Première sauvegarde
      setHistory([currentState])
      setHistoryIndex(0)
    }

    // Réinitialiser les flags
    setNeedsImmediateSave(false)
    setPendingHistoryDescription(null)
  }, [needsImmediateSave, pendingHistoryDescription, mounted, isUndoRedoOperation, history, historyIndex, areStatesEqual, saveToHistoryDebounced, buildHistorySnapshot])

  // Fonction pour sauvegarder immédiatement dans l'historique avec description
  const saveToHistoryImmediate = useCallback((description: string) => {
    setPendingHistoryDescription(description)
    setNeedsImmediateSave(true)
  }, [])
  saveToHistoryImmediateRef.current = saveToHistoryImmediate

  // Fonctions pour gérer les processeurs backup (définies après saveToHistoryImmediate)
  const setBackupProcessor = useCallback((primaryProcessorId: string, backupProcessorId: string) => {
    if (primaryProcessorId === backupProcessorId) return
    
    setSkipNextHistorySave(true)
    setBackupProcessors(prev => {
      const newBackups = { ...prev }
      
      // Supprimer l'ancien backup du processeur primaire s'il existe
      if (newBackups[primaryProcessorId]) {
        delete newBackups[primaryProcessorId]
      }
      
      // Libérer le processeur backup de toute relation précédente
      Object.keys(newBackups).forEach(primary => {
        if (newBackups[primary] === backupProcessorId) {
          delete newBackups[primary]
        }
      })
      
      // Établir la nouvelle relation
      newBackups[primaryProcessorId] = backupProcessorId
      
      return newBackups
    })
    
    setTimeout(() => {
      setSkipNextHistorySave(false)
      saveToHistoryImmediate(`Processeur ${backupProcessorId} configuré comme backup du processeur ${primaryProcessorId}`)
    }, 100)
  }, [saveToHistoryImmediate])

  const removeBackupProcessor = useCallback((primaryProcessorId: string) => {
    setSkipNextHistorySave(true)
    
    // Trouver le backup processor ID avant suppression
    const backupId = backupProcessors[primaryProcessorId]
    
    // Supprimer la relation processeur backup (chercher dans les deux sens)
    setBackupProcessors(prev => {
      const newBackups = { ...prev }
      // Supprimer l'entrée directe
      delete newBackups[primaryProcessorId]
      // Aussi chercher et supprimer si primaryProcessorId est une valeur (backup d'un autre)
      Object.keys(newBackups).forEach(key => {
        if (newBackups[key] === primaryProcessorId) {
          delete newBackups[key]
        }
      })
      return newBackups
    })
    // Nettoyer aussi les ports backup associés à ce processeur
    setBackupPorts(prev => {
      const newBackups = { ...prev }
      delete newBackups[primaryProcessorId]
      return newBackups
    })
    
    toast.success(t('synoptic.toast.backupDeleted'), {
      description: t('synoptic.toast.backupDeletedDesc')
    })
    
    setTimeout(() => {
      setSkipNextHistorySave(false)
      saveToHistoryImmediate(`Backup du processeur ${primaryProcessorId} supprimé`)
    }, 100)
  }, [saveToHistoryImmediate, backupProcessors])

  // Fonctions pour l'historique (Undo/Redo) - Sauvegarde automatique OPTIMISÉE
  useEffect(() => {
    if (!mounted || isUndoRedoOperation || skipNextHistorySave) return

    // Un changement de page/mode n'est PAS une édition : ne pas historiser
    // l'état de la nouvelle page (le debounce en cours, s'il existe, contient
    // déjà le snapshot de l'ancienne page et se termine normalement).
    const scopeKey = `${currentStep}:${exportViewMode}:${currentDataPageId}:${currentPowerPageId}`
    if (historyScopeRef.current !== null && historyScopeRef.current !== scopeKey) {
      historyScopeRef.current = scopeKey
      return
    }
    historyScopeRef.current = scopeKey

    const currentState = buildHistorySnapshot()

    // Éviter de sauvegarder le même état plusieurs fois avec comparaison optimisée
    if (history.length > 0) {
      const lastState = history[historyIndex]
      if (areStatesEqual(currentState, lastState)) {
        return // État identique, pas besoin de sauvegarder
      }
    }

    // Utiliser la sauvegarde debounced pour éviter trop de sauvegardes pendant le drag
    saveToHistoryDebounced(currentState)
  }, [workspaceElements, cableRoutes, panelConnections, powerConnections, mireConnections, mirePowerConnections, backupPorts, backupProcessors, elementGroups, mounted, isUndoRedoOperation, skipNextHistorySave, history, historyIndex, areStatesEqual, saveToHistoryDebounced, buildHistorySnapshot, currentStep, exportViewMode, currentDataPageId, currentPowerPageId])

  /**
   * Restaure un snapshot d'historique sur la page qui l'a créé (pas sur la page
   * actuellement affichée). Rebascule la vue sur la page cible si nécessaire.
   */
  const applyHistoryState = useCallback((state: typeof history[number]) => {
    const nav = wiringNavRef.current
    const dataPageId = state.dataPageId ?? currentDataPageIdRef.current
    const powerPageId = state.powerPageId ?? currentPowerPageIdRef.current
    const viewIsPower = state.view
      ? state.view === 'power'
      : (nav.currentStep === 'cabling' || (nav.currentStep === 'export' && nav.exportViewMode === 'power'))
    const viewPageId = state.viewPageId ?? (viewIsPower ? powerPageId : dataPageId)

    // workspaceElements/cableRoutes → page de la vue du snapshot ;
    // connexions → toujours leur page respective (data vs power)
    setDataPages(prev => prev.map(page => {
      const isViewPage = !viewIsPower && page.id === viewPageId
      const isConnPage = page.id === dataPageId
      if (!isViewPage && !isConnPage) return page
      return {
        ...page,
        ...(isViewPage ? { workspaceElements: state.workspaceElements, cableRoutes: state.cableRoutes } : {}),
        ...(isConnPage ? { panelConnections: state.panelConnections, mireConnections: state.mireConnections || [] } : {}),
      }
    }))
    setPowerPages(prev => prev.map(page => {
      const isViewPage = viewIsPower && page.id === viewPageId
      const isConnPage = page.id === powerPageId
      if (!isViewPage && !isConnPage) return page
      return {
        ...page,
        ...(isViewPage ? { workspaceElements: state.workspaceElements, cableRoutes: state.cableRoutes } : {}),
        ...(isConnPage ? { powerConnections: state.powerConnections, mirePowerConnections: state.mirePowerConnections || [] } : {}),
      }
    }))
    setBackupPorts(state.backupPorts || {})
    setBackupProcessors(state.backupProcessors || {})
    setElementGroups(state.elementGroups)

    // Rebascule sur la page/vue qui a produit le snapshot
    if (dataPageId && dataPageId !== currentDataPageIdRef.current) setCurrentDataPageId(dataPageId)
    if (powerPageId && powerPageId !== currentPowerPageIdRef.current) setCurrentPowerPageId(powerPageId)
    if (nav.currentStep === 'export') {
      setExportViewMode(viewIsPower ? 'power' : 'data')
    } else {
      const targetStep: WorkflowStep = viewIsPower ? 'cabling' : 'data'
      if (nav.currentStep !== targetStep) setCurrentStep(targetStep)
    }
  }, [])

  const undo = () => {
    if (historyIndex <= 0) {
      toast.info(t('synoptic.toast.nothingToUndo'))
      return
    }

    setIsUndoRedoOperation(true)
    const currentState = history[historyIndex]
    const previousState = history[historyIndex - 1]

    applyHistoryState(previousState)
    setHistoryIndex(prev => prev - 1)

    // Réinitialiser le câblage Power en cours si on fait un undo
    setCurrentPanelForCablingNow(null)

    // Afficher le toast avec la description
    const description = currentState.description || 'Action'
    toast.success(`← ${description}`)

    setTimeout(() => setIsUndoRedoOperation(false), 10)
  }

  const redo = () => {
    if (historyIndex >= history.length - 1) {
      toast.info(t('synoptic.toast.nothingToRedo'))
      return
    }

    setIsUndoRedoOperation(true)
    const nextState = history[historyIndex + 1]

    applyHistoryState(nextState)
    setHistoryIndex(prev => prev + 1)

    // Réinitialiser le câblage Power en cours si on fait un redo
    setCurrentPanelForCablingNow(null)

    // Afficher le toast avec la description
    const description = nextState.description || 'Action'
    toast.success(`→ ${description}`)

    setTimeout(() => setIsUndoRedoOperation(false), 10)
  }

  // Gestionnaire pour désélectionner le panneau en cliquant dessus à nouveau
  const handlePanelClick = (panel: LEDPanel) => {
    // Toujours afficher les propriétés du panneau cliqué
    setSelectedItemForProperties({ type: 'panel', data: panel })

    // Vérifier qu'un processeur et un port sont sélectionnés
    if (!selectedProcessorForConnection || selectedPort === null) {
      alert(t("alert.selectProcessorFirst"))
      return
    }

    if (selectedPanelForPlacement?.id === panel.id) {
      // Désélectionner si déjà sélectionné
      setSelectedPanelForPlacement(null)
      setSelectedPanel(null)
      setPanelPlacementCounter(0)
    } else {
      // Sélectionner le nouveau panneau
      setSelectedPanelForPlacement(panel)
      setSelectedPanel(panel)
      setPanelPlacementCounter(0)
    }
  }


  // Supprimer un backup
  const removeBackup = (backupPort: number, processorId: string) => {
    setBackupPorts(prev => {
      const newBackups = { ...prev }
      if (newBackups[processorId]) {
        const processorBackups = { ...newBackups[processorId] }
        delete processorBackups[backupPort]
        newBackups[processorId] = processorBackups
      }
      return newBackups
    })
  }


  // Fonction pour calculer les guides de snap
  const calculateSnapGuides = (draggedId: string, position: { x: number, y: number }) => {
    if (!isSnappingEnabled) return position

    const snapTolerance = 15
    const otherElements = workspaceElements.filter(el => el.id !== draggedId)
    const draggedElement = workspaceElements.find(el => el.id === draggedId)
    const guides = { x: [] as number[], y: [] as number[] }

    let snappedX = position.x
    let snappedY = position.y

    // Pour les processeurs avec image, calculer l'offset entre le conteneur et l'image réelle
    let offsetX = 0
    let offsetY = 0
    if (draggedElement && draggedElement.type === 'processor' && (draggedElement.data as LEDProcessor).image) {
      const bounds = getActualImageBounds(draggedElement)
      offsetX = bounds.x - draggedElement.position.x
      offsetY = bounds.y - draggedElement.position.y
    }

    // Snap à la grille magnétique (tous les 100px)
    const gridSize = 100
    const draggedImageX = position.x + offsetX
    const draggedImageY = position.y + offsetY
    const nearestGridX = Math.round(draggedImageX / gridSize) * gridSize
    const nearestGridY = Math.round(draggedImageY / gridSize) * gridSize

    if (Math.abs(draggedImageX - nearestGridX) < snapTolerance) {
      snappedX = nearestGridX - offsetX
      guides.x.push(nearestGridX)
    }

    if (Math.abs(draggedImageY - nearestGridY) < snapTolerance) {
      snappedY = nearestGridY - offsetY
      guides.y.push(nearestGridY)
    }

    // Calculer les guides pour l'alignement avec d'autres éléments
    otherElements.forEach(element => {
      // Pour les processeurs avec image, utiliser les dimensions réelles de l'image
      const bounds = element.type === 'processor' && (element.data as LEDProcessor).image
        ? getActualImageBounds(element)
        : {
          x: element.position.x,
          y: element.position.y,
          width: element.size.width,
          height: element.size.height
        }

      const elemX = bounds.x
      const elemY = bounds.y
      const elemRight = bounds.x + bounds.width
      const elemBottom = bounds.y + bounds.height
      const elemCenterX = bounds.x + bounds.width / 2
      const elemCenterY = bounds.y + bounds.height / 2

      // Snap horizontal (alignement vertical)
      if (Math.abs(draggedImageX - elemX) < snapTolerance) {
        snappedX = elemX - offsetX
        guides.x.push(elemX)
      } else if (Math.abs(draggedImageX - elemRight) < snapTolerance) {
        snappedX = elemRight - offsetX
        guides.x.push(elemRight)
      } else if (Math.abs(draggedImageX - elemCenterX) < snapTolerance) {
        snappedX = elemCenterX - offsetX
        guides.x.push(elemCenterX)
      }

      // Snap vertical (alignement horizontal)
      if (Math.abs(draggedImageY - elemY) < snapTolerance) {
        snappedY = elemY - offsetY
        guides.y.push(elemY)
      } else if (Math.abs(draggedImageY - elemBottom) < snapTolerance) {
        snappedY = elemBottom - offsetY
        guides.y.push(elemBottom)
      } else if (Math.abs(draggedImageY - elemCenterY) < snapTolerance) {
        snappedY = elemCenterY - offsetY
        guides.y.push(elemCenterY)
      }
    })

    setSnapGuides(guides)
    return { x: snappedX, y: snappedY }
  }

  // Fonction pour générer une référence de panneau
  const generatePanelReference = () => {
    if (!selectedPanelForPlacement) return "Panel"

    setPanelPlacementCounter(prev => prev + 1)
    return `${selectedPanelForPlacement.name.replace(/\s+/g, '')}-${panelPlacementCounter + 1}`
  }

  // Fonction pour détecter les éléments dans le rectangle de sélection
  const getElementsInRectangle = (startPoint: { x: number, y: number }, endPoint: { x: number, y: number }) => {
    const minX = Math.min(startPoint.x, endPoint.x)
    const maxX = Math.max(startPoint.x, endPoint.x)
    const minY = Math.min(startPoint.y, endPoint.y)
    const maxY = Math.max(startPoint.y, endPoint.y)

    return workspaceElements.filter(element => {
      // Pour les processeurs avec image, utiliser les dimensions réelles de l'image
      const bounds = element.type === 'processor' && (element.data as LEDProcessor).image
        ? getActualImageBounds(element)
        : {
          x: element.position.x,
          y: element.position.y,
          width: element.size.width,
          height: element.size.height
        }

      const elemLeft = bounds.x
      const elemRight = bounds.x + bounds.width
      const elemTop = bounds.y
      const elemBottom = bounds.y + bounds.height

      // Vérifier si l'élément intersecte avec le rectangle de sélection
      return !(elemRight < minX || elemLeft > maxX || elemBottom < minY || elemTop > maxY)
    }).map(element => element.id)
  }
  // Créer un câble DATA en daisy chain ENTRE LES DALLES UNIQUEMENT
  const createDaisyChainCabling = (newPanel: WorkspaceElement, currentElements?: WorkspaceElement[]) => {
    if (!selectedProcessorForConnection || selectedPort === null) return

    const elementsToUse = currentElements || workspaceElements
    const processor = elementsToUse.find(e => e.id === selectedProcessorForConnection)
    if (!processor) return

    // Trouver les panneaux déjà connectés à ce port
    const existingConnections = panelConnections.filter(
      conn => conn.processorId === selectedProcessorForConnection && conn.port === selectedPort
    ).sort((a, b) => a.order - b.order)

    // LIMITE : Maximum de panneaux en daisy chain pour drag & drop UNIQUEMENT
    // Le clic simple peut dépasser cette limite
    const isDragDrop = currentElements !== undefined // Si currentElements est fourni, c'est un drag & drop

    if (isDragDrop && existingConnections.length >= MAX_DAISY_CHAIN_PANELS) {
      return false // Retourner false pour indiquer l'échec
    }

    // Calculer l'ordre du nouveau panneau
    const newOrder = existingConnections.length + 1

    // Vérifier si la connexion existe déjà (éviter les doublons)
    const connectionExists = panelConnections.some(conn =>
      conn.panelId === newPanel.id &&
      conn.processorId === selectedProcessorForConnection &&
      conn.port === selectedPort
    )

    if (!connectionExists) {
      // Ajouter la nouvelle connexion
      const newConnection: PanelConnection = {
        panelId: newPanel.id,
        processorId: selectedProcessorForConnection,
        port: selectedPort,
        order: newOrder
      }

      setPanelConnections(prev => [...prev, newConnection])
    }

    // MODIFIE : Ne plus connecter automatiquement au processeur, seulement entre dalles
    let fromElement: WorkspaceElement
    let fromPoint: { x: number, y: number }

    if (existingConnections.length === 0) {
      // Premier panneau : PAS de connexion au processeur
      return
    } else {
      // Panneaux suivants : connecter au point de sortie (droite) du dernier panneau
      const lastConnection = existingConnections[existingConnections.length - 1]
      const lastPanel = elementsToUse.find(e => e.id === lastConnection.panelId)
      if (!lastPanel) return

      fromElement = lastPanel
      fromPoint = {
        x: lastPanel.position.x + lastPanel.size.width, // Point de sortie (droite)
        y: lastPanel.position.y + lastPanel.size.height / 2
      }
    }

    // Point de départ et d'arrivée : ajuster selon la direction du câble et les badges
    const isFirstPanel = existingConnections.length + 1 === 1
    const hasBackup = selectedProcessorForConnection ? getBackupForPort(selectedPort || 0, selectedProcessorForConnection) : null

    // Calculer la direction du câble
    const deltaX = newPanel.position.x - fromPoint.x
    const deltaY = newPanel.position.y - fromPoint.y
    const isHorizontal = Math.abs(deltaX) > Math.abs(deltaY)

    // Initialiser le point d'arrivée
    let toPoint = {
      x: newPanel.position.x + newPanel.size.width / 2,
      y: newPanel.position.y + newPanel.size.height / 2
    }

    // Ajuster le point de départ depuis le processeur si c'est le premier panneau
    if (isFirstPanel && fromElement.type === 'processor') {
      if (isHorizontal) {
        // Câble horizontal : partir du côté approprié du processeur
        fromPoint = {
          x: deltaX > 0
            ? fromElement.position.x + fromElement.size.width * 0.75 // Aller vers la droite → partir de droite
            : fromElement.position.x + fromElement.size.width * 0.25, // Aller vers la gauche → partir de gauche
          y: fromElement.position.y + fromElement.size.height / 2
        }
      } else {
        // Câble vertical : partir du côté approprié du processeur
        if (deltaY > 0) {
          // Aller vers le bas → partir du bas
          fromPoint = {
            x: fromElement.position.x + fromElement.size.width / 2,
            y: fromElement.position.y + fromElement.size.height * 0.75
          }
        } else {
          // Aller vers le haut → partir du haut
          fromPoint = {
            x: fromElement.position.x + fromElement.size.width / 2,
            y: fromElement.position.y + fromElement.size.height * 0.25
          }
        }
      }

      // Recalculer la direction après avoir ajusté le point de départ du processeur
      const newDeltaX = newPanel.position.x - fromPoint.x
      const newDeltaY = newPanel.position.y - fromPoint.y
      const newIsHorizontal = Math.abs(newDeltaX) > Math.abs(newDeltaY)

      // Ajuster le point d'arrivée selon la nouvelle direction - AU MILIEU DU BORD
      if (newIsHorizontal) {
        // Câble horizontal : arriver au milieu du bord gauche ou droit
        toPoint = {
          x: newDeltaX > 0
            ? newPanel.position.x // Câble vient de la gauche → arriver au bord gauche
            : newPanel.position.x + newPanel.size.width, // Câble vient de la droite → arriver au bord droit
          y: newPanel.position.y + newPanel.size.height / 2 // Milieu vertical
        }
      } else {
        // Câble vertical : arriver au milieu du bord haut ou bas
        if (newDeltaY > 0) {
          // Câble vient du haut → arriver au bord haut
          toPoint = {
            x: newPanel.position.x + newPanel.size.width / 2, // Milieu horizontal
            y: newPanel.position.y // Bord haut
          }
        } else {
          // Câble vient du bas → arriver au bord bas
          toPoint = {
            x: newPanel.position.x + newPanel.size.width / 2, // Milieu horizontal
            y: newPanel.position.y + newPanel.size.height // Bord bas
          }
        }
      }
    } else if (isFirstPanel) {
      // Premier panneau mais pas depuis processeur - AU MILIEU DU BORD
      if (isHorizontal) {
        // Câble horizontal : arriver au milieu du bord gauche ou droit
        toPoint = {
          x: deltaX > 0
            ? newPanel.position.x // Câble vient de la gauche → arriver au bord gauche
            : newPanel.position.x + newPanel.size.width, // Câble vient de la droite → arriver au bord droit
          y: newPanel.position.y + newPanel.size.height / 2 // Milieu vertical
        }
      } else {
        // Câble vertical : arriver au milieu du bord haut ou bas
        if (deltaY > 0) {
          // Câble vient du haut → arriver au bord haut
          toPoint = {
            x: newPanel.position.x + newPanel.size.width / 2, // Milieu horizontal
            y: newPanel.position.y // Bord haut
          }
        } else {
          // Câble vient du bas → arriver au bord bas
          toPoint = {
            x: newPanel.position.x + newPanel.size.width / 2, // Milieu horizontal
            y: newPanel.position.y + newPanel.size.height // Bord bas
          }
        }
      }
    }

    // Générer un ID unique pour éviter les doublons
    const uniqueId = `data-${fromElement.id}-${newPanel.id}-${Math.random().toString(36).substr(2, 9)}`

    const newCable: CableRoute = {
      id: uniqueId,
      type: "data",
      from: fromElement.id,
      to: newPanel.id,
      length: 0,
      color: getPortColor(selectedPort, selectedProcessorForConnection),
      points: [fromPoint, toPoint]
    }

    setCableRoutes(prev => [...prev, newCable])
  }

  // Placer un seul panneau avec snapping et câblage automatique
  const placeSinglePanel = (x: number, y: number) => {
    if (!selectedPanelForPlacement || !selectedProcessorForConnection || selectedPort === null) return

    // Désactiver la sauvegarde automatique
    setSkipNextHistorySave(true)

    // Appliquer le snapping lors du placement
    let snappedPosition = isSnappingEnabled ? calculateSnapGuides('temp', { x, y }) : { x, y }

    // Limites de la zone A3 avec marge de 20px
    const minX = -450
    const maxX = 450
    const minY = -320
    const maxY = 320

    // Contraindre la position pour que le panneau reste dans la zone A3
    const panelWidth = selectedPanelForPlacement.dimensions.width
    const panelHeight = selectedPanelForPlacement.dimensions.height

    if (snappedPosition.x < minX) snappedPosition.x = minX
    if (snappedPosition.x + panelWidth > maxX) snappedPosition.x = maxX - panelWidth
    if (snappedPosition.y < minY) snappedPosition.y = minY
    if (snappedPosition.y + panelHeight > maxY) snappedPosition.y = maxY - panelHeight

    snappedPosition = { x: snappedPosition.x, y: snappedPosition.y }

    const newPanel: WorkspaceElement = {
      id: `panel-${Date.now()}`,
      type: 'panel',
      position: snappedPosition,
      size: {
        width: selectedPanelForPlacement.dimensions.width,
        height: selectedPanelForPlacement.dimensions.height
      },
      data: {
        ...selectedPanelForPlacement,
        reference: generatePanelReference()
      }
    }

    // Ajouter le panneau au workspace
    setWorkspaceElements(prev => [...prev, newPanel])

    // Sélectionner automatiquement le nouveau panneau pour faciliter la connexion
    setSelectedElements(prev => [...prev, newPanel.id])

    // Les connexions daisy chain seront créées via le bouton "Connecter panneaux sélectionnés"
    // ou automatiquement lors du drag & drop multiple

    // Reset les guides après placement
    setSnapGuides({ x: [], y: [] })

    // Réactiver la sauvegarde automatique et sauvegarder immédiatement
    setTimeout(() => {
      setSkipNextHistorySave(false)
      saveToHistoryImmediate('Panneau placé')
    }, 100)
  }
  // Calculer les panneaux de preview pendant le drag
  const calculatePreviewPanels = (start: { x: number, y: number }, end: { x: number, y: number }) => {
    if (!selectedPanelForPlacement) return []

    const deltaX = end.x - start.x
    const deltaY = end.y - start.y

    const panelWidth = selectedPanelForPlacement.dimensions.width
    const panelHeight = selectedPanelForPlacement.dimensions.height

    // Calculer combien de panneaux on peut placer dans chaque direction
    let countX = Math.max(1, Math.floor(Math.abs(deltaX) / panelWidth) + 1)
    let countY = Math.max(1, Math.floor(Math.abs(deltaY) / panelHeight) + 1)

    // LIMITE : Maximum de panneaux pour le preview aussi
    const totalPanelsToPlace = countX * countY

    if (totalPanelsToPlace > MAX_DAISY_CHAIN_PANELS) {
      // Appliquer la même logique d'ajustement que placeMultiplePanels
      const isHorizontalGesture = Math.abs(deltaX) > Math.abs(deltaY)

      if (isHorizontalGesture) {
        // Privilégier la largeur, ajuster la hauteur
        countX = Math.min(countX, MAX_DAISY_CHAIN_PANELS)
        countY = Math.max(1, Math.floor(MAX_DAISY_CHAIN_PANELS / countX))
        if (countX * countY > MAX_DAISY_CHAIN_PANELS) {
          countX = Math.floor(MAX_DAISY_CHAIN_PANELS / countY)
        }
      } else {
        // Privilégier la hauteur, ajuster la largeur
        countY = Math.min(countY, MAX_DAISY_CHAIN_PANELS)
        countX = Math.max(1, Math.floor(MAX_DAISY_CHAIN_PANELS / countY))
        if (countX * countY > MAX_DAISY_CHAIN_PANELS) {
          countY = Math.floor(MAX_DAISY_CHAIN_PANELS / countX)
        }
      }

      // S'assurer que le total ne dépasse pas la limite
      const finalTotal = countX * countY
      if (finalTotal > MAX_DAISY_CHAIN_PANELS) {
        if (isHorizontalGesture) {
          countX = MAX_DAISY_CHAIN_PANELS
          countY = 1
        } else {
          countY = MAX_DAISY_CHAIN_PANELS
          countX = 1
        }
      }
    }

    // Déterminer la direction principale
    const isHorizontalGesture = Math.abs(deltaX) > Math.abs(deltaY)

    let previewPanels: Array<{ x: number, y: number, width: number, height: number }> = []

    // Détecter le type de placement basé sur les comptes finaux
    const isPureHorizontal = countY === 1 && countX > 1 // Une seule ligne
    const isPureVertical = countX === 1 && countY > 1   // Une seule colonne

    if (isPureHorizontal) {
      // Placement en ligne horizontale
      const directionX = deltaX > 0 ? 1 : -1
      for (let i = 0; i < countX; i++) {
        previewPanels.push({
          x: start.x + (i * panelWidth * directionX),
          y: start.y,
          width: panelWidth,
          height: panelHeight
        })
      }
    } else if (isPureVertical) {
      // Placement en ligne verticale
      const directionY = deltaY > 0 ? 1 : -1
      for (let i = 0; i < countY; i++) {
        previewPanels.push({
          x: start.x,
          y: start.y + (i * panelHeight * directionY),
          width: panelWidth,
          height: panelHeight
        })
      }
    } else {
      // Placement en grille - générer dans l'ordre serpentin pour correspondre au câblage
      const directionX = deltaX > 0 ? 1 : -1
      const directionY = deltaY > 0 ? 1 : -1

      // D'abord, créer tous les panneaux dans l'ordre de création (ligne par ligne)
      let tempPanels: Array<{ x: number, y: number, width: number, height: number }> = []
      for (let row = 0; row < countY; row++) {
        for (let col = 0; col < countX; col++) {
          tempPanels.push({
            x: start.x + (col * panelWidth * directionX),
            y: start.y + (row * panelHeight * directionY),
            width: panelWidth,
            height: panelHeight
          })
        }
      }

      // Si c'est une grille (plus d'1 ligne ET plus d'1 colonne), appliquer l'ordre serpentin
      const isGrid = countX > 1 && countY > 1
      if (isGrid) {
        const isHorizontalFirst = Math.abs(deltaX) > Math.abs(deltaY)

        // Réorganiser les panneaux selon l'ordre serpentin
        if (isHorizontalFirst) {
          // Serpentin horizontal (lignes alternées)
          for (let row = 0; row < countY; row++) {
            if (row % 2 === 0) {
              // Ligne paire : gauche à droite
              for (let col = 0; col < countX; col++) {
                const index = row * countX + col
                if (index < tempPanels.length) {
                  previewPanels.push(tempPanels[index])
                }
              }
            } else {
              // Ligne impaire : droite à gauche
              for (let col = countX - 1; col >= 0; col--) {
                const index = row * countX + col
                if (index < tempPanels.length) {
                  previewPanels.push(tempPanels[index])
                }
              }
            }
          }
        } else {
          // Serpentin vertical (colonnes alternées)
          for (let col = 0; col < countX; col++) {
            if (col % 2 === 0) {
              // Colonne paire : haut en bas
              for (let row = 0; row < countY; row++) {
                const index = row * countX + col
                if (index < tempPanels.length) {
                  previewPanels.push(tempPanels[index])
                }
              }
            } else {
              // Colonne impaire : bas en haut
              for (let row = countY - 1; row >= 0; row--) {
                const index = row * countX + col
                if (index < tempPanels.length) {
                  previewPanels.push(tempPanels[index])
                }
              }
            }
          }
        }
      } else {
        // Ce n'est pas une grille (une seule ligne ou une seule colonne)
        previewPanels = tempPanels
      }
    }

    return previewPanels
  }

  // Calculer les câbles de preview pour le placement multiple
  const calculatePreviewCables = (
    previewPanels: Array<{ x: number, y: number, width: number, height: number }>,
    start: { x: number, y: number },
    end: { x: number, y: number }
  ) => {
    if (!selectedProcessorForConnection || selectedPort === null || previewPanels.length === 0 || !selectedPanelForPlacement) {
      return []
    }

    const processor = workspaceElements.find(e => e.id === selectedProcessorForConnection)
    if (!processor) return []

    // Utiliser directement l'ordre des previewPanels car ils sont déjà dans l'ordre serpentin
    let previewCables: Array<{ id: string, points: { x: number, y: number }[] }> = []

    // Câble du processeur vers le premier panneau
    if (previewPanels.length > 0) {
      let fromPoint = {
        x: processor.position.x + processor.size.width / 2,
        y: processor.position.y + processor.size.height / 2
      }

      // Calculer la direction du câble processeur → premier panneau
      const procDeltaX = previewPanels[0].x - fromPoint.x
      const procDeltaY = previewPanels[0].y - fromPoint.y
      const isProcCableHorizontal = Math.abs(procDeltaX) > Math.abs(procDeltaY)

      // Initialiser le point d'arrivée
      let toPoint = {
        x: previewPanels[0].x + previewPanels[0].width / 2,
        y: previewPanels[0].y + previewPanels[0].height / 2
      }

      // Ajuster le point de départ du processeur si c'est un premier panneau
      if (selectedPort) {
        if (isProcCableHorizontal) {
          // Câble horizontal : partir du côté approprié du processeur
          fromPoint = {
            x: procDeltaX > 0
              ? processor.position.x + processor.size.width * 0.75 // Aller vers la droite → partir de droite
              : processor.position.x + processor.size.width * 0.25, // Aller vers la gauche → partir de gauche
            y: processor.position.y + processor.size.height / 2
          }
        } else {
          // Câble vertical : partir du côté approprié du processeur
          if (procDeltaY > 0) {
            // Aller vers le bas → partir du bas
            fromPoint = {
              x: processor.position.x + processor.size.width / 2,
              y: processor.position.y + processor.size.height * 0.75
            }
          } else {
            // Aller vers le haut → partir du haut
            fromPoint = {
              x: processor.position.x + processor.size.width / 2,
              y: processor.position.y + processor.size.height * 0.25
            }
          }
        }

        // Recalculer les deltas après avoir ajusté le point de départ
        const newProcDeltaX = previewPanels[0].x - fromPoint.x
        const newProcDeltaY = previewPanels[0].y - fromPoint.y
        const newIsProcCableHorizontal = Math.abs(newProcDeltaX) > Math.abs(newProcDeltaY)

        // Utiliser les nouveaux deltas pour ajuster le point d'arrivée - AU MILIEU DU BORD
        if (newIsProcCableHorizontal) {
          // Câble horizontal : arriver au milieu du bord gauche ou droit
          toPoint = {
            x: newProcDeltaX > 0
              ? previewPanels[0].x // Processeur à gauche → arriver au bord gauche
              : previewPanels[0].x + previewPanels[0].width, // Processeur à droite → arriver au bord droit
            y: previewPanels[0].y + previewPanels[0].height / 2 // Milieu vertical
          }
        } else {
          // Câble vertical : arriver au milieu du bord haut ou bas
          if (newProcDeltaY > 0) {
            // Câble vient du haut → arriver au bord haut
            toPoint = {
              x: previewPanels[0].x + previewPanels[0].width / 2, // Milieu horizontal
              y: previewPanels[0].y // Bord haut
            }
          } else {
            // Câble vient du bas → arriver au bord bas
            toPoint = {
              x: previewPanels[0].x + previewPanels[0].width / 2, // Milieu horizontal
              y: previewPanels[0].y + previewPanels[0].height // Bord bas
            }
          }
        }
      }
      // Si pas de port sélectionné, toPoint reste au centre (déjà défini)

      previewCables.push({
        id: `preview-processor-to-panel-0`,
        points: [fromPoint, toPoint]
      })
    }

    // Câbles entre les panneaux (daisy chain dans l'ordre des previewPanels)
    for (let i = 0; i < previewPanels.length - 1; i++) {
      // Ajuster les points selon les badges
      const hasBackup = selectedPort && selectedProcessorForConnection && getBackupForPort(selectedPort, selectedProcessorForConnection)

      // Calculer la direction du câble entre panneaux
      const panelDeltaX = previewPanels[i + 1].x - previewPanels[i].x
      const panelDeltaY = previewPanels[i + 1].y - previewPanels[i].y
      const isPanelCableHorizontal = Math.abs(panelDeltaX) > Math.abs(panelDeltaY)

      // Point de départ : ajuster si le panneau source a un badge RY
      // Note: Dans cette boucle, aucun panneau source n'est le premier (qui a PX)
      // Mais le panneau source peut être l'avant-dernier (qui pourrait avoir RY si pas de backup)
      let fromPoint = {
        x: previewPanels[i].x + previewPanels[i].width / 2,
        y: previewPanels[i].y + previewPanels[i].height / 2
      }

      // Note: Dans cette boucle, le panneau source ne peut jamais avoir de badge RY 
      // car RY est sur le dernier panneau et i va seulement jusqu'à length-2
      // Mais le panneau source peut avoir PX si c'est le premier panneau (i = 0)
      const isSourceFirstPanel = (i === 0) && selectedPort
      if (isSourceFirstPanel) {
        // Panneau source a un badge PX - partir du milieu du bord opposé
        if (isPanelCableHorizontal) {
          // Câble horizontal : partir du milieu du bord gauche ou droit
          fromPoint = {
            x: panelDeltaX > 0
              ? previewPanels[i].x + previewPanels[i].width // Aller vers la droite → partir du bord droit
              : previewPanels[i].x, // Aller vers la gauche → partir du bord gauche
            y: previewPanels[i].y + previewPanels[i].height / 2 // Milieu vertical
          }
        } else {
          // Câble vertical : partir du milieu du bord haut ou bas
          if (panelDeltaY > 0) {
            // Aller vers le bas → partir du bord bas
            fromPoint = {
              x: previewPanels[i].x + previewPanels[i].width / 2, // Milieu horizontal
              y: previewPanels[i].y + previewPanels[i].height // Bord bas
            }
          } else {
            // Aller vers le haut → partir du bord haut
            fromPoint = {
              x: previewPanels[i].x + previewPanels[i].width / 2, // Milieu horizontal
              y: previewPanels[i].y // Bord haut
            }
          }
        }
      }

      // Point d'arrivée : ajuster si le panneau destination a un badge RY
      const isDestLastPanel = (i + 1 === previewPanels.length - 1) && hasBackup
      let toPoint = {
        x: previewPanels[i + 1].x + previewPanels[i + 1].width / 2,
        y: previewPanels[i + 1].y + previewPanels[i + 1].height / 2
      }

      if (isDestLastPanel) {
        // Panneau destination a un badge RY - arriver au milieu du bord
        if (isPanelCableHorizontal) {
          // Câble horizontal : arriver au milieu du bord gauche ou droit
          toPoint = {
            x: panelDeltaX > 0
              ? previewPanels[i + 1].x // Câble vient de la gauche → arriver au bord gauche
              : previewPanels[i + 1].x + previewPanels[i + 1].width, // Câble vient de la droite → arriver au bord droit
            y: previewPanels[i + 1].y + previewPanels[i + 1].height / 2 // Milieu vertical
          }
        } else {
          // Câble vertical : arriver au milieu du bord haut ou bas
          if (panelDeltaY > 0) {
            // Câble vient du haut → arriver au bord haut
            toPoint = {
              x: previewPanels[i + 1].x + previewPanels[i + 1].width / 2, // Milieu horizontal
              y: previewPanels[i + 1].y // Bord haut
            }
          } else {
            // Câble vient du bas → arriver au bord bas
            toPoint = {
              x: previewPanels[i + 1].x + previewPanels[i + 1].width / 2, // Milieu horizontal
              y: previewPanels[i + 1].y + previewPanels[i + 1].height // Bord bas
            }
          }
        }
      }

      previewCables.push({
        id: `preview-panel-${i}-to-${i + 1}`,
        points: [fromPoint, toPoint]
      })
    }

    return previewCables
  }

  // Calculer la position optimale du numéro d'un panneau pour éviter les câbles
  const calculateOptimalNumberPosition = (element: WorkspaceElement, connection: any) => {
    if (!connection) {
      return {
        x: element.position.x + element.size.width / 2,
        y: element.position.y + element.size.height / 2,
        textAnchor: "middle" as const,
        fill: "#2D2D2D",
        isCornerPosition: false
      }
    }

    // Analyser les connexions pour déterminer la position du panneau
    const allConnections = panelConnections.filter(conn =>
      conn.processorId === connection.processorId &&
      conn.port === connection.port
    ).sort((a, b) => a.order - b.order)

    const isFirstPanel = allConnections[0]?.panelId === element.id
    const isLastPanel = allConnections[allConnections.length - 1]?.panelId === element.id
    const hasBackup = getBackupForPort(connection.port, connection.processorId)

    // Tous les panneaux utilisent maintenant le même système de positionnement en haut à gauche
    // Pour les panneaux intermédiaires, analyser les directions des câbles
    const cables = cableRoutes.filter(cable =>
      cable.from === element.id || cable.to === element.id
    )

    // Tous les panneaux ont maintenant leur numéro en noir
    const textColor = "#2D2D2D"

    if (cables.length === 0) {
      return {
        x: element.position.x + element.size.width / 2,
        y: element.position.y + element.size.height / 2,
        textAnchor: "middle" as const,
        fill: textColor,
        isCornerPosition: false
      }
    }

    // Analyser les directions des câbles
    const directions = {
      top: false,
      bottom: false,
      left: false,
      right: false
    }

    cables.forEach(cable => {
      if (!hasValidCablePoints(cable)) return
      const isIncoming = cable.to === element.id
      const otherPoint = isIncoming ? cable.points[0] : cable.points[1]
      if (!otherPoint) return
      const elementCenter = {
        x: element.position.x + element.size.width / 2,
        y: element.position.y + element.size.height / 2
      }

      const dx = otherPoint.x - elementCenter.x
      const dy = otherPoint.y - elementCenter.y

      if (Math.abs(dx) > Math.abs(dy)) {
        // Câble horizontal
        if (dx > 0) directions.right = true
        else directions.left = true
      } else {
        // Câble vertical
        if (dy > 0) directions.bottom = true
        else directions.top = true
      }
    })

    // Choisir le coin le moins encombré
    const corners = [
      { name: 'topLeft', blocked: directions.top || directions.left, x: element.position.x + 8, y: element.position.y + 12, anchor: "start" as const },
      { name: 'topRight', blocked: directions.top || directions.right, x: element.position.x + element.size.width - 8, y: element.position.y + 12, anchor: "end" as const },
      { name: 'bottomLeft', blocked: directions.bottom || directions.left, x: element.position.x + 8, y: element.position.y + element.size.height - 8, anchor: "start" as const },
      { name: 'bottomRight', blocked: directions.bottom || directions.right, x: element.position.x + element.size.width - 8, y: element.position.y + element.size.height - 8, anchor: "end" as const }
    ]

    // Trouver le premier coin non bloqué
    const bestCorner = corners.find(corner => !corner.blocked) || corners[0] // Fallback sur le premier coin

    return {
      x: bestCorner.x,
      y: bestCorner.y,
      textAnchor: bestCorner.anchor,
      fill: textColor,
      isCornerPosition: true
    }
  }

  // Créer un câblage daisy chain séquentiel pour plusieurs panneaux
  const createMultiplePanelsDaisyChain = (panels: WorkspaceElement[]) => {
    createMultiplePanelsDaisyChainWithElements(panels, workspaceElements)
  }
  // Créer un câblage daisy chain séquentiel avec des éléments spécifiques
  // Créer un câblage daisy chain séquentiel avec des éléments spécifiques (ENTRE DALLES UNIQUEMENT)
  const createMultiplePanelsDaisyChainWithElements = (panels: WorkspaceElement[], elements: WorkspaceElement[]) => {
    if (!selectedProcessorForConnection || selectedPort === null || panels.length === 0) return

    // Utiliser l'état actuel des éléments pour avoir les positions à jour
    const currentElements = [...workspaceElements]
    const processor = currentElements.find(e => e.id === selectedProcessorForConnection)
    if (!processor) return

    // Créer une clé unique pour ce batch de panneaux
    const batchKey = `${selectedProcessorForConnection}-${selectedPort}-${panels.map(p => p.id).sort().join('-')}`

    // Vérifier si ce batch est déjà en cours de traitement
    if (processingPanels.current.has(batchKey)) {
      return
    }

    // Marquer ce batch comme en cours de traitement
    processingPanels.current.add(batchKey)

    // Nettoyer après un délai pour éviter les fuites mémoire
    setTimeout(() => {
      processingPanels.current.delete(batchKey)
    }, 1000)

    // Trouver les connexions existantes sur ce port
    let existingConnections = panelConnections.filter(
      conn => conn.processorId === selectedProcessorForConnection && conn.port === selectedPort
    ).sort((a, b) => a.order - b.order)

    // Créer toutes les connexions en une seule fois pour éviter les doublons
    // Filtrer les panneaux qui n'ont pas déjà une connexion
    const panelsToConnect = panels.filter(panel =>
      !panelConnections.some(conn =>
        conn.panelId === panel.id &&
        conn.processorId === selectedProcessorForConnection &&
        conn.port === selectedPort
      )
    )

    if (panelsToConnect.length === 0) {
      return
    }

    const newConnections: PanelConnection[] = panelsToConnect.map((panel, index) => ({
      panelId: panel.id,
      processorId: selectedProcessorForConnection!,
      port: selectedPort!,
      order: existingConnections.length + index + 1
    }))

    // Ajouter toutes les connexions en une seule opération
    setPanelConnections(prev => [...prev, ...newConnections])


    // Traiter chaque panneau connecté séquentiellement pour les câbles
    panelsToConnect.forEach((panel, index) => {

      // MODIFIE : Ne plus connecter au processeur, seulement entre dalles
      let fromElement: WorkspaceElement
      let fromPoint: { x: number, y: number }

      // Calculer la direction du câble (sera utilisé plus tard)
      let cableDeltaX: number
      let cableDeltaY: number
      let isCableHorizontal: boolean

      if (existingConnections.length === 0 && index === 0) {
        // Premier panneau de tous : PAS de connexion au processeur
        existingConnections.push(newConnections[index])
        return
      } else {
        // Connecter au panneau précédent dans la chaîne
        let previousPanel: WorkspaceElement

        if (index === 0 && existingConnections.length > 0) {
          // Premier panneau du batch mais il y a déjà des connexions : connecter au dernier existant
          const lastExistingConnection = existingConnections[existingConnections.length - 1]
          previousPanel = currentElements.find(e => e.id === lastExistingConnection.panelId)!
        } else {
          // Connecter au panneau précédent du batch
          previousPanel = panelsToConnect[index - 1]
        }

        fromElement = previousPanel

        // Ajuster le point de départ selon les badges
        const allConnectionsForPrev = [...existingConnections, ...newConnections].sort((a, b) => a.order - b.order)
        const isPrevLastPanel = allConnectionsForPrev[allConnectionsForPrev.length - 1]?.panelId === previousPanel.id
        const isPrevFirstPanel = allConnectionsForPrev.find(c => c.panelId === previousPanel.id)?.order === 1
        const hasBackupForPrev = selectedProcessorForConnection ? getBackupForPort(selectedPort || 0, selectedProcessorForConnection) : null

        // Calculer la direction du câble pour ajuster les points (disponible pour tous les cas)
        cableDeltaX = panel.position.x - previousPanel.position.x
        cableDeltaY = panel.position.y - previousPanel.position.y
        isCableHorizontal = Math.abs(cableDeltaX) > Math.abs(cableDeltaY)

        if (isPrevLastPanel && hasBackupForPrev) {
          // Panneau précédent a un badge RY
          if (isCableHorizontal) {
            // Câble horizontal : partir du côté approprié
            fromPoint = {
              x: cableDeltaX > 0
                ? previousPanel.position.x + previousPanel.size.width * 0.75 // Partir vers la droite → partir de droite
                : previousPanel.position.x + previousPanel.size.width * 0.25, // Partir vers la gauche → partir de gauche
              y: previousPanel.position.y + previousPanel.size.height / 2
            }
          } else {
            // Câble vertical : partir du milieu haut/bas selon la direction (logique inversée pour RY)
            if (cableDeltaY > 0) {
              // Aller vers le bas → pour RY, partir du milieu haut (logique inversée)
              fromPoint = {
                x: previousPanel.position.x + previousPanel.size.width / 2,
                y: previousPanel.position.y + previousPanel.size.height * 0.25
              }
            } else {
              // Aller vers le haut → partir du milieu bas
              fromPoint = {
                x: previousPanel.position.x + previousPanel.size.width / 2,
                y: previousPanel.position.y + previousPanel.size.height * 0.75
              }
            }
          }
        } else if (isPrevFirstPanel && selectedPort) {
          // Panneau précédent a un badge PX
          if (isCableHorizontal) {
            // Câble horizontal : partir du côté approprié
            fromPoint = {
              x: cableDeltaX > 0
                ? previousPanel.position.x + previousPanel.size.width * 0.75 // Partir vers la droite → partir de droite
                : previousPanel.position.x + previousPanel.size.width * 0.25, // Partir vers la gauche → partir de gauche
              y: previousPanel.position.y + previousPanel.size.height / 2
            }
          } else {
            // Câble vertical : partir du milieu haut/bas selon la direction
            if (cableDeltaY > 0) {
              // Aller vers le bas → partir du milieu bas
              fromPoint = {
                x: previousPanel.position.x + previousPanel.size.width / 2,
                y: previousPanel.position.y + previousPanel.size.height * 0.75
              }
            } else {
              // Aller vers le haut → partir du milieu haut
              fromPoint = {
                x: previousPanel.position.x + previousPanel.size.width / 2,
                y: previousPanel.position.y + previousPanel.size.height * 0.25
              }
            }
          }
        } else {
          // Panneau normal
          fromPoint = {
            x: previousPanel.position.x + previousPanel.size.width / 2,
            y: previousPanel.position.y + previousPanel.size.height / 2
          }
        }
      }

      // Ajuster le point d'arrivée pour éviter les badges PX/RY selon la direction
      const allCurrentConnections = [...existingConnections, ...newConnections].sort((a, b) => a.order - b.order)
      const isDestFirstPanel = allCurrentConnections.find(c => c.panelId === panel.id)?.order === 1
      const isDestLastPanel = allCurrentConnections[allCurrentConnections.length - 1]?.panelId === panel.id
      const hasBackupForDest = selectedProcessorForConnection ? getBackupForPort(selectedPort || 0, selectedProcessorForConnection) : null

      let toPoint = {
        x: panel.position.x + panel.size.width / 2,
        y: panel.position.y + panel.size.height / 2
      }

      // Ajuster selon les badges
      if (isDestFirstPanel) {
        // Premier panneau (badge PX)
        if (isCableHorizontal) {
          // Câble horizontal : arriver du côté d'où vient le câble
          toPoint = {
            x: cableDeltaX > 0
              ? panel.position.x + panel.size.width * 0.25 // Câble vient de la gauche → arriver à gauche
              : panel.position.x + panel.size.width * 0.75, // Câble vient de la droite → arriver à droite
            y: panel.position.y + panel.size.height / 2
          }
        } else {
          // Câble vertical : ajuster selon d'où vient le câble
          if (cableDeltaY > 0) {
            // Câble vient du haut → arriver en bas
            toPoint = {
              x: panel.position.x + panel.size.width / 2,
              y: panel.position.y + panel.size.height * 0.75
            }
          } else {
            // Câble vient du bas → arriver en haut (côté opposé au badge PX)
            toPoint = {
              x: panel.position.x + panel.size.width / 2,
              y: panel.position.y + panel.size.height * 0.25
            }
          }
        }
      } else if (isDestLastPanel && hasBackupForDest) {
        // Dernier panneau (badge RY)
        if (isCableHorizontal) {
          // Câble horizontal : arriver du côté d'où vient le câble
          toPoint = {
            x: cableDeltaX > 0
              ? panel.position.x + panel.size.width * 0.25 // Câble vient de la gauche → arriver à gauche
              : panel.position.x + panel.size.width * 0.75, // Câble vient de la droite → arriver à droite
            y: panel.position.y + panel.size.height / 2
          }
        } else {
          // Câble vertical : ajuster selon d'où vient le câble et si c'est RY
          if (cableDeltaY > 0) {
            // Câble vient du haut
            if (isDestLastPanel && hasBackupForDest) {
              // Pour RY, arriver au milieu haut (logique inversée)
              toPoint = {
                x: panel.position.x + panel.size.width / 2,
                y: panel.position.y + panel.size.height * 0.25
              }
            } else {
              // Pour PX, arriver au milieu bas  
              toPoint = {
                x: panel.position.x + panel.size.width / 2,
                y: panel.position.y + panel.size.height * 0.75
              }
            }
          } else {
            // Câble vient du bas → arriver au milieu bas pour RY, milieu haut pour PX
            if (isDestLastPanel && hasBackupForDest) {
              // Pour RY, arriver au milieu bas
              toPoint = {
                x: panel.position.x + panel.size.width / 2,
                y: panel.position.y + panel.size.height * 0.75
              }
            } else {
              // Pour PX, arriver au milieu haut
              toPoint = {
                x: panel.position.x + panel.size.width / 2,
                y: panel.position.y + panel.size.height * 0.25
              }
            }
          }
        }
      }

      // Créer le câble
      const uniqueId = `data-${fromElement.id}-${panel.id}-${Math.random().toString(36).substr(2, 9)}`

      const newCable: CableRoute = {
        id: uniqueId,
        type: "data",
        from: fromElement.id,
        to: panel.id,
        length: 0,
        color: getPortColor(selectedPort, selectedProcessorForConnection),
        points: [fromPoint, toPoint]
      }

      setCableRoutes(prev => [...prev, newCable])

      // Mettre à jour les connexions existantes pour le prochain panneau
      existingConnections.push(newConnections[index])
    })
  }
  // Mettre à jour les positions des câbles quand les éléments bougent
  const updateCablePositions = (movedElements: { id: string, position: { x: number, y: number }, size: { width: number, height: number }, type: string }[]) => {
    setCableRoutes(prev => prev.map(cable => {
      if (!hasValidCablePoints(cable)) return cable
      let needsUpdate = false
      let newFromPoint = cable.points[0]
      let newToPoint = cable.points[1]

      // Mettre à jour le point de départ si l'élément source a bougé
      const movedFromElement = movedElements.find(el => el.id === cable.from)
      if (movedFromElement) {
        if (movedFromElement.type === 'processor') {
          // Récupérer l'élément complet pour avoir accès aux données du processeur
          const fullProcessorElement = workspaceElements.find(el => el.id === movedFromElement.id)

          // Utiliser les dimensions réelles de l'image si c'est un processeur avec image
          const processorBounds = fullProcessorElement && (fullProcessorElement.data as LEDProcessor).image
            ? getActualImageBounds(fullProcessorElement)
            : {
              x: movedFromElement.position.x,
              y: movedFromElement.position.y,
              width: movedFromElement.size.width,
              height: movedFromElement.size.height
            }

          // Trouver l'élément destination pour calculer la direction
          const toElement = workspaceElements.find(el => el.id === cable.to)

          if (toElement) {
            // Récupérer la connexion du panneau destination pour obtenir le port et processeur
            const destConnection = panelConnections.find(conn => conn.panelId === toElement.id)

            // Vérifier si le panneau destination est le premier (badge PX)
            const allPortConnections = panelConnections.filter(conn =>
              conn.processorId === destConnection?.processorId &&
              conn.port === destConnection?.port
            ).sort((a, b) => a.order - b.order)

            const isDestFirstPanel = allPortConnections[0]?.panelId === toElement.id

            if (isDestFirstPanel && destConnection?.port) {
              // Calculer la direction du câble processeur → premier panneau
              const cableDeltaX = toElement.position.x - processorBounds.x
              const cableDeltaY = toElement.position.y - processorBounds.y
              const isCableHorizontal = Math.abs(cableDeltaX) > Math.abs(cableDeltaY)

              if (isCableHorizontal) {
                // Câble horizontal : partir du côté approprié du processeur
                newFromPoint = {
                  x: cableDeltaX > 0
                    ? processorBounds.x + processorBounds.width * 0.75 // Aller vers la droite → partir de droite
                    : processorBounds.x + processorBounds.width * 0.25, // Aller vers la gauche → partir de gauche
                  y: processorBounds.y + processorBounds.height / 2
                }
              } else {
                // Câble vertical : partir du côté approprié du processeur
                if (cableDeltaY > 0) {
                  // Aller vers le bas → partir du bas
                  newFromPoint = {
                    x: processorBounds.x + processorBounds.width / 2,
                    y: processorBounds.y + processorBounds.height * 0.75
                  }
                } else {
                  // Aller vers le haut → partir du haut
                  newFromPoint = {
                    x: processorBounds.x + processorBounds.width / 2,
                    y: processorBounds.y + processorBounds.height * 0.25
                  }
                }
              }
            } else {
              // Câble standard du processeur (pas vers le premier panneau)
              newFromPoint = {
                x: processorBounds.x + processorBounds.width / 2,
                y: processorBounds.y + processorBounds.height
              }
            }
          } else {
            // Pas d'élément destination trouvé, utiliser la position standard
            newFromPoint = {
              x: processorBounds.x + processorBounds.width / 2,
              y: processorBounds.y + processorBounds.height
            }
          }
        } else {
          // Ajuster le point de départ si c'est un panneau avec badge RY
          // Récupérer la connexion du panneau source pour obtenir le port et processeur
          const sourceConnection = panelConnections.find(conn => conn.panelId === movedFromElement.id)

          const allPortConnections = panelConnections.filter(conn =>
            conn.processorId === sourceConnection?.processorId &&
            conn.port === sourceConnection?.port
          ).sort((a, b) => a.order - b.order)

          const isSourceLastPanel = allPortConnections[allPortConnections.length - 1]?.panelId === movedFromElement.id
          const hasBackupForSource = sourceConnection ? getBackupForPort(sourceConnection.port, sourceConnection.processorId) : null

          // Trouver l'élément destination pour calculer la direction
          const toElement = workspaceElements.find(el => el.id === cable.to)

          const isSourceFirstPanel = allPortConnections[0]?.panelId === movedFromElement.id

          if (isSourceLastPanel && hasBackupForSource && toElement) {
            // Panneau source a un badge RY
            const cableDeltaX = toElement.position.x - movedFromElement.position.x
            const cableDeltaY = toElement.position.y - movedFromElement.position.y
            const isCableHorizontal = Math.abs(cableDeltaX) > Math.abs(cableDeltaY)

            if (isCableHorizontal) {
              // Câble horizontal : partir du côté approprié
              newFromPoint = {
                x: cableDeltaX > 0
                  ? movedFromElement.position.x + movedFromElement.size.width * 0.75 // Aller vers la droite → partir de droite
                  : movedFromElement.position.x + movedFromElement.size.width * 0.25, // Aller vers la gauche → partir de gauche
                y: movedFromElement.position.y + movedFromElement.size.height / 2
              }
            } else {
              // Câble vertical : partir du milieu haut/bas selon la direction (logique inversée pour RY)
              if (cableDeltaY > 0) {
                // Aller vers le bas → pour RY, partir du milieu haut (logique inversée)
                newFromPoint = {
                  x: movedFromElement.position.x + movedFromElement.size.width / 2,
                  y: movedFromElement.position.y + movedFromElement.size.height * 0.25
                }
              } else {
                // Aller vers le haut → partir du milieu bas
                newFromPoint = {
                  x: movedFromElement.position.x + movedFromElement.size.width / 2,
                  y: movedFromElement.position.y + movedFromElement.size.height * 0.75
                }
              }
            }
          } else if (isSourceFirstPanel && sourceConnection?.port && toElement) {
            // Panneau source a un badge PX - partir du milieu du bord opposé
            const cableDeltaX = toElement.position.x - movedFromElement.position.x
            const cableDeltaY = toElement.position.y - movedFromElement.position.y
            const isCableHorizontal = Math.abs(cableDeltaX) > Math.abs(cableDeltaY)

            if (isCableHorizontal) {
              // Câble horizontal : partir du milieu du bord gauche ou droit
              newFromPoint = {
                x: cableDeltaX > 0
                  ? movedFromElement.position.x + movedFromElement.size.width // Aller vers la droite → partir du bord droit
                  : movedFromElement.position.x, // Aller vers la gauche → partir du bord gauche
                y: movedFromElement.position.y + movedFromElement.size.height / 2 // Milieu vertical
              }
            } else {
              // Câble vertical : partir du milieu du bord haut ou bas
              if (cableDeltaY > 0) {
                // Aller vers le bas → partir du bord bas
                newFromPoint = {
                  x: movedFromElement.position.x + movedFromElement.size.width / 2, // Milieu horizontal
                  y: movedFromElement.position.y + movedFromElement.size.height // Bord bas
                }
              } else {
                // Aller vers le haut → partir du bord haut
                newFromPoint = {
                  x: movedFromElement.position.x + movedFromElement.size.width / 2, // Milieu horizontal
                  y: movedFromElement.position.y // Bord haut
                }
              }
            }
          } else {
            // Panneau normal
            newFromPoint = {
              x: movedFromElement.position.x + movedFromElement.size.width / 2,
              y: movedFromElement.position.y + movedFromElement.size.height / 2
            }
          }
        }

        if (movedFromElement.type === 'cabinet') {
          // Câble Power depuis une armoire
          const toElement = workspaceElements.find(el => el.id === cable.to)

          if (toElement && cable.type === 'power') {
            // Vérifier si c'est le premier panneau de la chaîne Power
            const powerConn = powerConnections.find(conn => conn.panelId === toElement.id)
            if (powerConn) {
              const allOutletConnections = powerConnections.filter(conn =>
                conn.cabinetId === powerConn.cabinetId &&
                conn.outlet === powerConn.outlet
              ).sort((a, b) => a.order - b.order)

              const isFirstPanelInChain = allOutletConnections[0]?.panelId === toElement.id

              if (isFirstPanelInChain) {
                // Calculer la direction du câble armoire → premier panneau
                const cableDeltaX = toElement.position.x - movedFromElement.position.x
                const cableDeltaY = toElement.position.y - movedFromElement.position.y
                const isCableHorizontal = Math.abs(cableDeltaX) > Math.abs(cableDeltaY)

                if (isCableHorizontal) {
                  // Câble horizontal : partir du côté approprié de l'armoire
                  newFromPoint = {
                    x: cableDeltaX > 0
                      ? movedFromElement.position.x + movedFromElement.size.width * 0.75 // Aller vers la droite → partir de droite
                      : movedFromElement.position.x + movedFromElement.size.width * 0.25, // Aller vers la gauche → partir de gauche
                    y: movedFromElement.position.y + movedFromElement.size.height / 2
                  }
                } else {
                  // Câble vertical : partir du côté approprié de l'armoire
                  if (cableDeltaY > 0) {
                    // Aller vers le bas → partir du bas
                    newFromPoint = {
                      x: movedFromElement.position.x + movedFromElement.size.width / 2,
                      y: movedFromElement.position.y + movedFromElement.size.height * 0.75
                    }
                  } else {
                    // Aller vers le haut → partir du haut
                    newFromPoint = {
                      x: movedFromElement.position.x + movedFromElement.size.width / 2,
                      y: movedFromElement.position.y + movedFromElement.size.height * 0.25
                    }
                  }
                }
              } else if (toElement.type === 'cabinet') {
                // Liaison armoire → armoire : repartir du bord directionnel de l'armoire source
                const cableDeltaX = toElement.position.x - movedFromElement.position.x
                const cableDeltaY = toElement.position.y - movedFromElement.position.y
                if (Math.abs(cableDeltaX) > Math.abs(cableDeltaY)) {
                  newFromPoint = {
                    x: cableDeltaX > 0
                      ? movedFromElement.position.x + movedFromElement.size.width * 0.75
                      : movedFromElement.position.x + movedFromElement.size.width * 0.25,
                    y: movedFromElement.position.y + movedFromElement.size.height / 2
                  }
                } else {
                  newFromPoint = {
                    x: movedFromElement.position.x + movedFromElement.size.width / 2,
                    y: cableDeltaY > 0
                      ? movedFromElement.position.y + movedFromElement.size.height * 0.75
                      : movedFromElement.position.y + movedFromElement.size.height * 0.25
                  }
                }
              } else {
                // Câble standard de l'armoire
                newFromPoint = {
                  x: movedFromElement.position.x + movedFromElement.size.width / 2,
                  y: movedFromElement.position.y + movedFromElement.size.height / 2
                }
              }
            }
          } else {
            // Câble standard
            newFromPoint = {
              x: movedFromElement.position.x + movedFromElement.size.width / 2,
              y: movedFromElement.position.y + movedFromElement.size.height / 2
            }
          }
        }
        needsUpdate = true
      }

      // Mettre à jour le point d'arrivée si l'élément destination a bougé
      const movedToElement = movedElements.find(el => el.id === cable.to)
      if (movedToElement) {
        // Trouver l'élément source pour calculer la direction
        const fromElement = workspaceElements.find(el => el.id === cable.from)

        // Gérer les câbles Power
        if (cable.type === 'power' && fromElement && fromElement.type === 'cabinet') {
          const powerConn = powerConnections.find(conn => conn.panelId === movedToElement.id)
          if (powerConn) {
            const allOutletConnections = powerConnections.filter(conn =>
              conn.cabinetId === powerConn.cabinetId &&
              conn.outlet === powerConn.outlet
            ).sort((a, b) => a.order - b.order)

            const isFirstPanelInChain = allOutletConnections[0]?.panelId === movedToElement.id

            if (isFirstPanelInChain) {
              // Calculer la direction du câble armoire → premier panneau
              const cableDeltaX = movedToElement.position.x - fromElement.position.x
              const cableDeltaY = movedToElement.position.y - fromElement.position.y
              const isCableHorizontal = Math.abs(cableDeltaX) > Math.abs(cableDeltaY)

              if (isCableHorizontal) {
                // Câble horizontal : arriver du côté d'où vient le câble
                newToPoint = {
                  x: cableDeltaX > 0
                    ? movedToElement.position.x + movedToElement.size.width * 0.25 // Câble vient de la gauche → arriver à gauche
                    : movedToElement.position.x + movedToElement.size.width * 0.75, // Câble vient de la droite → arriver à droite
                  y: movedToElement.position.y + movedToElement.size.height / 2
                }
              } else {
                // Câble vertical : ajuster selon d'où vient le câble
                if (cableDeltaY > 0) {
                  // Câble vient du haut → arriver au milieu bas
                  newToPoint = {
                    x: movedToElement.position.x + movedToElement.size.width / 2,
                    y: movedToElement.position.y + movedToElement.size.height * 0.75
                  }
                } else {
                  // Câble vient du bas → arriver au milieu haut
                  newToPoint = {
                    x: movedToElement.position.x + movedToElement.size.width / 2,
                    y: movedToElement.position.y + movedToElement.size.height * 0.25
                  }
                }
              }
              needsUpdate = true
            } else {
              // Panneau non-premier dans la chaîne Power
              newToPoint = {
                x: movedToElement.position.x + movedToElement.size.width / 2,
                y: movedToElement.position.y + movedToElement.size.height / 2
              }
              needsUpdate = true
            }
          } else if (movedToElement.type === 'cabinet') {
            // Liaison armoire → armoire : arriver au bord directionnel de l'armoire cible
            const cableDeltaX = movedToElement.position.x - fromElement.position.x
            const cableDeltaY = movedToElement.position.y - fromElement.position.y
            if (Math.abs(cableDeltaX) > Math.abs(cableDeltaY)) {
              newToPoint = {
                x: cableDeltaX > 0
                  ? movedToElement.position.x + movedToElement.size.width * 0.25
                  : movedToElement.position.x + movedToElement.size.width * 0.75,
                y: movedToElement.position.y + movedToElement.size.height / 2
              }
            } else {
              newToPoint = {
                x: movedToElement.position.x + movedToElement.size.width / 2,
                y: cableDeltaY > 0
                  ? movedToElement.position.y + movedToElement.size.height * 0.75
                  : movedToElement.position.y + movedToElement.size.height * 0.25
              }
            }
            needsUpdate = true
          }
        } else {
          // Gérer les câbles Data
          // Récupérer la connexion du panneau destination pour obtenir le port et processeur
          const destConnection = panelConnections.find(conn => conn.panelId === movedToElement.id)

          const allPortConnections = panelConnections.filter(conn =>
            conn.processorId === destConnection?.processorId &&
            conn.port === destConnection?.port
          ).sort((a, b) => a.order - b.order)

          const isDestFirstPanel = allPortConnections[0]?.panelId === movedToElement.id
          const isDestLastPanel = allPortConnections[allPortConnections.length - 1]?.panelId === movedToElement.id
          const hasBackupForDest = destConnection ? getBackupForPort(destConnection.port, destConnection.processorId) : null

          if ((isDestFirstPanel || (isDestLastPanel && hasBackupForDest)) && fromElement) {
            // Calculer la direction du câble
            const cableDeltaX = movedToElement.position.x - fromElement.position.x
            const cableDeltaY = movedToElement.position.y - fromElement.position.y
            const isCableHorizontal = Math.abs(cableDeltaX) > Math.abs(cableDeltaY)

            if (isCableHorizontal) {
              // Câble horizontal : arriver au milieu du bord gauche ou droit
              newToPoint = {
                x: cableDeltaX > 0
                  ? movedToElement.position.x // Câble vient de la gauche → arriver au bord gauche
                  : movedToElement.position.x + movedToElement.size.width, // Câble vient de la droite → arriver au bord droit
                y: movedToElement.position.y + movedToElement.size.height / 2 // Milieu vertical
              }
            } else {
              // Câble vertical : arriver au milieu du bord haut ou bas
              if (cableDeltaY > 0) {
                // Câble vient du haut → arriver au bord haut
                newToPoint = {
                  x: movedToElement.position.x + movedToElement.size.width / 2, // Milieu horizontal
                  y: movedToElement.position.y // Bord haut
                }
              } else {
                // Câble vient du bas → arriver au bord bas
                newToPoint = {
                  x: movedToElement.position.x + movedToElement.size.width / 2, // Milieu horizontal
                  y: movedToElement.position.y + movedToElement.size.height // Bord bas
                }
              }
            }
          } else {
            // Panneau normal
            newToPoint = {
              x: movedToElement.position.x + movedToElement.size.width / 2,
              y: movedToElement.position.y + movedToElement.size.height / 2
            }
          }
        }

        needsUpdate = true
      }

      // NOUVEAU : Recalculer le point de départ si l'élément destination a bougé et que l'élément source a un badge
      if (movedToElement && !movedFromElement) {
        const fromElement = workspaceElements.find(el => el.id === cable.from)
        if (fromElement && fromElement.type === 'cabinet' && cable.type === 'power') {
          // Armoire électrique : recalculer le point de départ
          const powerConn = powerConnections.find(conn => conn.panelId === movedToElement.id)
          if (powerConn) {
            const allOutletConnections = powerConnections.filter(conn =>
              conn.cabinetId === powerConn.cabinetId &&
              conn.outlet === powerConn.outlet
            ).sort((a, b) => a.order - b.order)

            const isFirstPanelInChain = allOutletConnections[0]?.panelId === movedToElement.id

            if (isFirstPanelInChain) {
              // Calculer la nouvelle direction du câble armoire → panneau
              const cableDeltaX = movedToElement.position.x - fromElement.position.x
              const cableDeltaY = movedToElement.position.y - fromElement.position.y
              const isCableHorizontal = Math.abs(cableDeltaX) > Math.abs(cableDeltaY)

              if (isCableHorizontal) {
                // Câble horizontal : partir du côté approprié de l'armoire
                newFromPoint = {
                  x: cableDeltaX > 0
                    ? fromElement.position.x + fromElement.size.width * 0.75 // Aller vers la droite → partir de droite
                    : fromElement.position.x + fromElement.size.width * 0.25, // Aller vers la gauche → partir de gauche
                  y: fromElement.position.y + fromElement.size.height / 2
                }
              } else {
                // Câble vertical : partir du côté approprié de l'armoire
                if (cableDeltaY > 0) {
                  // Aller vers le bas → partir du bas
                  newFromPoint = {
                    x: fromElement.position.x + fromElement.size.width / 2,
                    y: fromElement.position.y + fromElement.size.height * 0.75
                  }
                } else {
                  // Aller vers le haut → partir du haut
                  newFromPoint = {
                    x: fromElement.position.x + fromElement.size.width / 2,
                    y: fromElement.position.y + fromElement.size.height * 0.25
                  }
                }
              }
              needsUpdate = true
            }
          }
        } else if (fromElement && fromElement.type !== 'processor') {
          // Récupérer la connexion du panneau source pour obtenir le port et processeur
          const sourceConnection = panelConnections.find(conn => conn.panelId === fromElement.id)

          const allPortConnections = panelConnections.filter(conn =>
            conn.processorId === sourceConnection?.processorId &&
            conn.port === sourceConnection?.port
          ).sort((a, b) => a.order - b.order)

          const isSourceFirstPanel = allPortConnections[0]?.panelId === fromElement.id
          const isSourceLastPanel = allPortConnections[allPortConnections.length - 1]?.panelId === fromElement.id
          const hasBackupForSource = sourceConnection ? getBackupForPort(sourceConnection.port, sourceConnection.processorId) : null

          if ((isSourceFirstPanel && sourceConnection?.port) || (isSourceLastPanel && hasBackupForSource)) {
            // Calculer la nouvelle direction du câble
            const cableDeltaX = movedToElement.position.x - fromElement.position.x
            const cableDeltaY = movedToElement.position.y - fromElement.position.y
            const isCableHorizontal = Math.abs(cableDeltaX) > Math.abs(cableDeltaY)

            if (isCableHorizontal) {
              // Câble horizontal : partir du côté approprié
              newFromPoint = {
                x: cableDeltaX > 0
                  ? fromElement.position.x + fromElement.size.width * 0.75 // Aller vers la droite → partir de droite
                  : fromElement.position.x + fromElement.size.width * 0.25, // Aller vers la gauche → partir de gauche
                y: fromElement.position.y + fromElement.size.height / 2
              }
            } else {
              // Câble vertical : partir du milieu haut/bas selon la direction et le type de badge
              if (cableDeltaY > 0) {
                // Aller vers le bas  
                if (isSourceLastPanel && hasBackupForSource) {
                  // Badge RY → partir du milieu haut (logique inversée)
                  newFromPoint = {
                    x: fromElement.position.x + fromElement.size.width / 2,
                    y: fromElement.position.y + fromElement.size.height * 0.25
                  }
                } else {
                  // Badge PX → partir du milieu bas
                  newFromPoint = {
                    x: fromElement.position.x + fromElement.size.width / 2,
                    y: fromElement.position.y + fromElement.size.height * 0.75
                  }
                }
              } else {
                // Aller vers le haut
                if (isSourceLastPanel && hasBackupForSource) {
                  // Badge RY → partir du milieu bas
                  newFromPoint = {
                    x: fromElement.position.x + fromElement.size.width / 2,
                    y: fromElement.position.y + fromElement.size.height * 0.75
                  }
                } else {
                  // Badge PX → partir du milieu haut
                  newFromPoint = {
                    x: fromElement.position.x + fromElement.size.width / 2,
                    y: fromElement.position.y + fromElement.size.height * 0.25
                  }
                }
              }
            }
            needsUpdate = true
          }
        }
      }
      // NOUVEAU : Recalculer le point d'arrivée si l'élément source a bougé et que l'élément destination a un badge
      if (movedFromElement && !movedToElement) {
        const toElement = workspaceElements.find(el => el.id === cable.to)
        if (toElement) {
          // Gérer les câbles Power (armoire → panneau)
          if (cable.type === 'power' && movedFromElement.type === 'cabinet') {
            const powerConn = powerConnections.find(conn => conn.panelId === toElement.id)
            if (powerConn) {
              const allOutletConnections = powerConnections.filter(conn =>
                conn.cabinetId === powerConn.cabinetId &&
                conn.outlet === powerConn.outlet
              ).sort((a, b) => a.order - b.order)

              const isFirstPanelInChain = allOutletConnections[0]?.panelId === toElement.id

              if (isFirstPanelInChain) {
                // Calculer la nouvelle direction du câble armoire → panneau
                const cableDeltaX = toElement.position.x - movedFromElement.position.x
                const cableDeltaY = toElement.position.y - movedFromElement.position.y
                const isCableHorizontal = Math.abs(cableDeltaX) > Math.abs(cableDeltaY)

                if (isCableHorizontal) {
                  // Câble horizontal : arriver du côté d'où vient le câble
                  newToPoint = {
                    x: cableDeltaX > 0
                      ? toElement.position.x + toElement.size.width * 0.25 // Câble vient de la gauche → arriver à gauche
                      : toElement.position.x + toElement.size.width * 0.75, // Câble vient de la droite → arriver à droite
                    y: toElement.position.y + toElement.size.height / 2
                  }
                } else {
                  // Câble vertical : ajuster selon d'où vient le câble
                  if (cableDeltaY > 0) {
                    // Câble vient du haut → arriver au milieu bas
                    newToPoint = {
                      x: toElement.position.x + toElement.size.width / 2,
                      y: toElement.position.y + toElement.size.height * 0.75
                    }
                  } else {
                    // Câble vient du bas → arriver au milieu haut
                    newToPoint = {
                      x: toElement.position.x + toElement.size.width / 2,
                      y: toElement.position.y + toElement.size.height * 0.25
                    }
                  }
                }
                needsUpdate = true
              } else {
                // Panneau non-premier dans la chaîne Power
                newToPoint = {
                  x: toElement.position.x + toElement.size.width / 2,
                  y: toElement.position.y + toElement.size.height / 2
                }
                needsUpdate = true
              }
            }
          } else {
            // Gérer les câbles Data
            // Récupérer la connexion du panneau destination pour obtenir le port et processeur
            const destConnection = panelConnections.find(conn => conn.panelId === toElement.id)

            const allPortConnections = panelConnections.filter(conn =>
              conn.processorId === destConnection?.processorId &&
              conn.port === destConnection?.port
            ).sort((a, b) => a.order - b.order)

            const isDestFirstPanel = allPortConnections[0]?.panelId === toElement.id
            const isDestLastPanel = allPortConnections[allPortConnections.length - 1]?.panelId === toElement.id
            const hasBackupForDest = destConnection ? getBackupForPort(destConnection.port, destConnection.processorId) : null

            if ((isDestFirstPanel) || (isDestLastPanel && hasBackupForDest)) {
              // Calculer la nouvelle direction du câble
              const cableDeltaX = toElement.position.x - movedFromElement.position.x
              const cableDeltaY = toElement.position.y - movedFromElement.position.y
              const isCableHorizontal = Math.abs(cableDeltaX) > Math.abs(cableDeltaY)

              if (isCableHorizontal) {
                // Câble horizontal : arriver du côté d'où vient le câble
                newToPoint = {
                  x: cableDeltaX > 0
                    ? toElement.position.x + toElement.size.width * 0.25 // Câble vient de la gauche → arriver à gauche
                    : toElement.position.x + toElement.size.width * 0.75, // Câble vient de la droite → arriver à droite
                  y: toElement.position.y + toElement.size.height / 2
                }
              } else {
                // Câble vertical : ajuster selon d'où vient le câble et le type de badge
                if (cableDeltaY > 0) {
                  // Câble vient du haut
                  if (isDestLastPanel && hasBackupForDest) {
                    // Badge RY → arriver au milieu haut (logique inversée)
                    newToPoint = {
                      x: toElement.position.x + toElement.size.width / 2,
                      y: toElement.position.y + toElement.size.height * 0.25
                    }
                  } else {
                    // Badge PX → arriver au milieu bas
                    newToPoint = {
                      x: toElement.position.x + toElement.size.width / 2,
                      y: toElement.position.y + toElement.size.height * 0.75
                    }
                  }
                } else {
                  // Câble vient du bas
                  if (isDestLastPanel && hasBackupForDest) {
                    // Badge RY → arriver au milieu bas
                    newToPoint = {
                      x: toElement.position.x + toElement.size.width / 2,
                      y: toElement.position.y + toElement.size.height * 0.75
                    }
                  } else {
                    // Badge PX → arriver au milieu haut
                    newToPoint = {
                      x: toElement.position.x + toElement.size.width / 2,
                      y: toElement.position.y + toElement.size.height * 0.25
                    }
                  }
                }
              }
              needsUpdate = true
            }
          }
        }
      }

      // NOUVEAU : Recalculer le point de départ du processeur si le premier panneau a bougé
      if (!movedFromElement && movedToElement) {
        const fromElement = workspaceElements.find(el => el.id === cable.from)
        if (fromElement && fromElement.type === 'processor') {
          const allPortConnections = panelConnections.filter(conn =>
            conn.processorId === selectedProcessorForConnection &&
            conn.port === selectedPort
          ).sort((a, b) => a.order - b.order)

          const isDestFirstPanel = allPortConnections[0]?.panelId === movedToElement.id

          if (isDestFirstPanel && selectedPort) {
            // Calculer la nouvelle direction du câble processeur → premier panneau
            const cableDeltaX = movedToElement.position.x - fromElement.position.x
            const cableDeltaY = movedToElement.position.y - fromElement.position.y
            const isCableHorizontal = Math.abs(cableDeltaX) > Math.abs(cableDeltaY)

            if (isCableHorizontal) {
              // Câble horizontal : partir du côté approprié du processeur
              newFromPoint = {
                x: cableDeltaX > 0
                  ? fromElement.position.x + fromElement.size.width * 0.75 // Aller vers la droite → partir de droite
                  : fromElement.position.x + fromElement.size.width * 0.25, // Aller vers la gauche → partir de gauche
                y: fromElement.position.y + fromElement.size.height / 2
              }
            } else {
              // Câble vertical : partir du côté approprié du processeur
              if (cableDeltaY > 0) {
                // Aller vers le bas → partir du bas
                newFromPoint = {
                  x: fromElement.position.x + fromElement.size.width / 2,
                  y: fromElement.position.y + fromElement.size.height * 0.75
                }
              } else {
                // Aller vers le haut → partir du haut
                newFromPoint = {
                  x: fromElement.position.x + fromElement.size.width / 2,
                  y: fromElement.position.y + fromElement.size.height * 0.25
                }
              }
            }
            needsUpdate = true
          }
        }
      }

      return needsUpdate ? {
        ...cable,
        points: [newFromPoint, newToPoint]
      } : cable
    }))
  }

  // Calculer l'ordre serpentin des panneaux
  const calculateSerpentineOrder = (panels: WorkspaceElement[], countX: number, countY: number, isHorizontalFirst: boolean) => {
    if (isHorizontalFirst) {
      // Serpentin de gauche à droite
      const serpentineOrder: WorkspaceElement[] = []
      for (let row = 0; row < countY; row++) {
        if (row % 2 === 0) {
          // Ligne paire : gauche à droite
          for (let col = 0; col < countX; col++) {
            const index = row * countX + col
            if (index < panels.length) {
              serpentineOrder.push(panels[index])
            }
          }
        } else {
          // Ligne impaire : droite à gauche
          for (let col = countX - 1; col >= 0; col--) {
            const index = row * countX + col
            if (index < panels.length) {
              serpentineOrder.push(panels[index])
            }
          }
        }
      }
      return serpentineOrder
    } else {
      // Serpentin de haut en bas
      const serpentineOrder: WorkspaceElement[] = []
      for (let col = 0; col < countX; col++) {
        if (col % 2 === 0) {
          // Colonne paire : haut en bas
          for (let row = 0; row < countY; row++) {
            const index = row * countX + col
            if (index < panels.length) {
              serpentineOrder.push(panels[index])
            }
          }
        } else {
          // Colonne impaire : bas en haut
          for (let row = countY - 1; row >= 0; row--) {
            const index = row * countX + col
            if (index < panels.length) {
              serpentineOrder.push(panels[index])
            }
          }
        }
      }
      return serpentineOrder
    }
  }
  // Fonction utilitaire pour contraindre une position dans la zone A3
  const constrainPositionToA3 = (x: number, y: number, width: number, height: number) => {
    const minX = -450
    const maxX = 450
    const minY = -320
    const maxY = 320

    let constrainedX = x
    let constrainedY = y

    if (constrainedX < minX) constrainedX = minX
    if (constrainedX + width > maxX) constrainedX = maxX - width
    if (constrainedY < minY) constrainedY = minY
    if (constrainedY + height > maxY) constrainedY = maxY - height

    return { x: constrainedX, y: constrainedY }
  }
  // Placer plusieurs panneaux en ligne/colonne/grille
  const placeMultiplePanels = (start: { x: number, y: number }, end: { x: number, y: number }) => {
    if (!selectedPanelForPlacement) return

    const deltaX = end.x - start.x
    const deltaY = end.y - start.y

    const panelWidth = selectedPanelForPlacement.dimensions.width
    const panelHeight = selectedPanelForPlacement.dimensions.height

    // Calculer combien de panneaux on peut placer dans chaque direction
    let countX = Math.max(1, Math.floor(Math.abs(deltaX) / panelWidth) + 1)
    let countY = Math.max(1, Math.floor(Math.abs(deltaY) / panelHeight) + 1)

    // LIMITE : Maximum de panneaux pour le drag & drop
    const existingConnections = panelConnections.filter(
      conn => conn.processorId === selectedProcessorForConnection && conn.port === selectedPort
    ).length

    const totalPanelsToPlace = countX * countY
    const remainingSlots = calculatedMaxDaisyChain - existingConnections

    if (totalPanelsToPlace > remainingSlots) {

      // Ajuster le nombre de panneaux pour respecter la limite
      if (remainingSlots <= 0) {
        return
      }

      // Calculer la meilleure répartition pour utiliser tous les emplacements disponibles
      // en respectant le ratio d'aspect du geste de l'utilisateur
      const ratio = Math.abs(deltaX / deltaY)
      const isHorizontalGesture = Math.abs(deltaX) > Math.abs(deltaY)

      if (isHorizontalGesture) {
        // Privilégier la largeur, ajuster la hauteur
        countX = Math.min(countX, remainingSlots)
        countY = Math.max(1, Math.floor(remainingSlots / countX))
        // Recalculer countX si nécessaire pour ne pas dépasser la limite
        if (countX * countY > remainingSlots) {
          countX = Math.floor(remainingSlots / countY)
        }
      } else {
        // Privilégier la hauteur, ajuster la largeur
        countY = Math.min(countY, remainingSlots)
        countX = Math.max(1, Math.floor(remainingSlots / countY))
        // Recalculer countY si nécessaire pour ne pas dépasser la limite
        if (countX * countY > remainingSlots) {
          countY = Math.floor(remainingSlots / countX)
        }
      }

      // S'assurer que le total ne dépasse pas la limite
      const finalTotal = countX * countY
      if (finalTotal > remainingSlots) {
        if (isHorizontalGesture) {
          countX = remainingSlots
          countY = 1
        } else {
          countY = remainingSlots
          countX = 1
        }
      }

    }

    // Déterminer la direction principale (pour l'utilisation en dehors du bloc if)
    const isHorizontalGesture = Math.abs(deltaX) > Math.abs(deltaY)

    // Détecter le type de placement basé sur les comptes finaux
    const isPureHorizontal = countY === 1 && countX > 1 // Une seule ligne
    const isPureVertical = countX === 1 && countY > 1   // Une seule colonne

    let panels: WorkspaceElement[] = []

    if (isPureHorizontal) {
      // Placement en ligne horizontale
      const directionX = deltaX > 0 ? 1 : -1
      for (let i = 0; i < countX; i++) {
        const rawX = start.x + (i * panelWidth * directionX)
        const rawY = start.y
        const constrainedPos = constrainPositionToA3(rawX, rawY, panelWidth, panelHeight)

        panels.push({
          id: `panel-${Date.now()}-${i}`,
          type: 'panel',
          position: constrainedPos,
          size: { width: panelWidth, height: panelHeight },
          data: {
            ...selectedPanelForPlacement,
            reference: generatePanelReference()
          }
        })
      }
    } else if (isPureVertical) {
      // Placement en ligne verticale
      const directionY = deltaY > 0 ? 1 : -1
      for (let i = 0; i < countY; i++) {
        const rawX = start.x
        const rawY = start.y + (i * panelHeight * directionY)
        const constrainedPos = constrainPositionToA3(rawX, rawY, panelWidth, panelHeight)

        panels.push({
          id: `panel-${Date.now()}-${i}`,
          type: 'panel',
          position: constrainedPos,
          size: { width: panelWidth, height: panelHeight },
          data: {
            ...selectedPanelForPlacement,
            reference: generatePanelReference()
          }
        })
      }
    } else {
      // Placement en grille
      const directionX = deltaX > 0 ? 1 : -1
      const directionY = deltaY > 0 ? 1 : -1

      for (let row = 0; row < countY; row++) {
        for (let col = 0; col < countX; col++) {
          const rawX = start.x + (col * panelWidth * directionX)
          const rawY = start.y + (row * panelHeight * directionY)
          const constrainedPos = constrainPositionToA3(rawX, rawY, panelWidth, panelHeight)

          panels.push({
            id: `panel-${Date.now()}-${row}-${col}`,
            type: 'panel',
            position: constrainedPos,
            size: { width: panelWidth, height: panelHeight },
            data: {
              ...selectedPanelForPlacement,
              reference: generatePanelReference()
            }
          })
        }
      }
    }

    // Déterminer l'ordre de câblage (serpentin pour les grilles)
    let cableOrder = panels

    // Détecter si c'est une grille basé sur les comptes finaux (après ajustement de limite)
    const isGrid = countX > 1 && countY > 1             // Grille multi-lignes/colonnes

    if (isGrid) {
      // C'est une grille : utiliser l'ordre serpentin
      const isHorizontalFirst = Math.abs(deltaX) > Math.abs(deltaY)
      cableOrder = calculateSerpentineOrder(panels, countX, countY, isHorizontalFirst)
    }

    // Désactiver la sauvegarde automatique
    setSkipNextHistorySave(true)

    // Ajouter tous les panneaux d'abord
    setWorkspaceElements(prev => {
      const newElements = [...prev, ...panels]

      // Créer le câblage daisy chain séquentiel dans l'ordre serpentin
      // Utiliser la fonction de rappel pour s'assurer que l'état est à jour
      setTimeout(() => {
        createMultiplePanelsDaisyChainWithElements(cableOrder, newElements)

        // Sauvegarder après la création des connexions
        setTimeout(() => {
          setSkipNextHistorySave(false)
          const count = panels.length
          saveToHistoryImmediate(`${count} panneau${count > 1 ? 'x' : ''} placé${count > 1 ? 's' : ''}`)
        }, 100)
      }, 50) // Augmenter le délai pour plus de sécurité

      return newElements
    })
  }

  // ✅ OPTIMISATION : Détecter les grandes mires pour utiliser le rendu simplifié
  // Au-delà de 200 panneaux, utiliser SimplePanelElement au lieu de PanelElement
  const isLargeMire = useMemo(() => {
    const panelCount = workspaceElements.filter(el => el.type === 'panel').length
    return panelCount > 200
  }, [workspaceElements])

  // ========== FONCTIONS POUR LA GÉNÉRATION AUTOMATIQUE (extracted to hooks/useAutoGeneratePanels.ts) ==========
  const {
    handleAutoGeneratePanelsWithImage,
  } = useAutoGeneratePanels({
    panels,
    panelConnections,
    workspaceElements,
    setWorkspaceElements,
    setElementGroups,
  })

  // ========== Save/Load .vgsyn (extracted to hooks/useSaveLoad.ts) ==========
  const {
    collectSynopticData,
    saveToLocalFile,
    handleExportSynopticFile,
    handleSaveSynoptic,
    handleSaveAsSynoptic,
    handleSaveAsConfirm,
    restoreSynopticData,
    handleImportSynopticFile,
  } = useSaveLoad({
    currentProject,
    projectName,
    currentStep,
    hasVisitedPowerStep,
    importedLEDConfig,
    dataPages,
    currentDataPageId,
    powerPages,
    currentPowerPageId,
    exportViewMode,
    showCartouche,
    cartouche,
    backupPorts,
    backupProcessors,
    portColorOverrides,
    outletColorOverrides,
    savedMires,
    projectMires,
    mireBackgroundImage,
    mireBackgroundOpacity,
    wallTechnicalConfig,
    localFilePath,
    saveAsFileName,
    isTauriApp,
    isPro,
    isOnline,
    readOnly,
    authSession,
    saveProject,
    updateProjectName,
    debugLog,
    setProjectName,
    setCurrentStep,
    setHasVisitedPowerStep,
    setImportedLEDConfig,
    setDataPages,
    setCurrentDataPageId,
    setPowerPages,
    setCurrentPowerPageId,
    setExportViewMode,
    setShowCartouche,
    setCartouche,
    setBackupPorts,
    setBackupProcessors,
    setPortColorOverrides,
    setOutletColorOverrides,
    setSavedMires,
    setProjectMires,
    setMireBackgroundImage,
    setMireBackgroundOpacity,
    setWallTechnicalConfig,
    setLocalFilePath,
    setIsSavingDebug,
    setSaveStartTime,
    setLastSaveTime,
    setShowSaveAsDialog,
    setSaveAsFileName,
  })

  // ========== Technical Report PDF (hooks/useReportGenerator.ts) ==========
  // Résout l'URL publique du QR : sauvegarde le projet (création si besoin)
  // puis garantit un report_token — le lien fonctionne même en privé.
  const resolveReportShareUrl = useCallback(async (): Promise<string | null> => {
    if (readOnly) return null
    try {
      const synopticData = collectSynopticData()
      let projectId = currentProject?.id ?? null
      if (!projectId) {
        const created = await createProject(projectName || 'Nouveau Synoptique', synopticData)
        projectId = created.id
      } else {
        await saveProject(synopticData, { debounce: false })
      }
      const token = await ensureReportToken(projectId)
      if (!token) return null
      const baseUrl = isTauriApp ? 'https://visualgrid.app' : window.location.origin
      return `${baseUrl}/synoptique/share/${token}`
    } catch {
      return null
    }
  }, [readOnly, collectSynopticData, currentProject, createProject, saveProject, ensureReportToken, projectName, isTauriApp])

  const { generateReport } = useReportGenerator({
    projectName: currentProject?.name || projectName,
    cartouche,
    dataPages,
    powerPages,
    backupPorts,
    backupProcessors,
    resolveShareUrl: resolveReportShareUrl,
    setIsExporting,
    setExportProgress,
    setShowExportSuccess,
    createExportSvgForDataPage,
    createExportSvgForPowerPage,
  })

  // Fonction pour supprimer une mire du projet avec confirmation
  const handleDeleteProjectMire = useCallback((mireId: string) => {
    const mire = projectMires.find(m => m.id === mireId)
    if (!mire) return
    setMireToDelete({ id: mireId, name: mire.name })
    setIsDeleteMireDialogOpen(true)
  }, [projectMires])

  const handleConfirmDeleteMire = useCallback(() => {
    if (!mireToDelete) return
    setProjectMires(prev => prev.filter(m => m.id !== mireToDelete.id))
    toast.success('Mire supprimée du projet')
    setIsDeleteMireDialogOpen(false)
    setMireToDelete(null)
  }, [mireToDelete])

  // Gestionnaire des étapes du workflow
  const handleStepChange = (step: WorkflowStep) => {
    setCurrentStep(step)

    // Clear selection when changing steps to avoid showing properties from previous step
    setSelectedElements([])
    setSelectedItemForProperties(null)

    // Logique contextuelle par étape
    if (step === "testPattern") {
      // Étape Générateur de Mires - pas d'équipements visibles
      setShowProcessors(false)
      setShowPanels(false)
      setShowCabinets(false)
      setShowCabling(false)
      setSelectedCableType(null)
    } else if (step === "data") {
      // Étape Data - afficher processeurs et panneaux pour les connexions
      setShowProcessors(true)
      setShowPanels(true)
      setShowCabinets(false)
      setShowCabling(false) // Data se concentre sur les connexions données
      setSelectedCableType('data')
    } else if (step === "cabling") {
      // Étape Power pour le câblage électrique
      setShowProcessors(false)
      setShowPanels(false)
      setShowCabinets(true) // Afficher les armoires/coffrets
      setShowCabling(true)
      setSelectedCableType('power')

      // ✅ Copie automatique des pages Data vers Power lors du premier accès
      if (!hasVisitedPowerStep && dataPages.length > 0) {

        // Créer une page Power pour chaque page Data
        const newPowerPages: PowerPage[] = dataPages.map((dataPage, index) => {
          // Filtrer pour ne garder QUE les panneaux (pas les processeurs)
          const elementsWithoutProcessors = dataPage.workspaceElements.filter(el => el.type !== 'processor')

          // Filtrer les câbles pour ne garder que ceux entre panneaux (pas ceux connectés aux processeurs)
          const cablesWithoutProcessors = dataPage.cableRoutes.filter(cable => {
            // Vérifier si le câble est connecté à un processeur
            const fromIsProcessor = dataPage.workspaceElements.find(el => el.id === cable.from && el.type === 'processor')
            const toIsProcessor = dataPage.workspaceElements.find(el => el.id === cable.to && el.type === 'processor')
            return !fromIsProcessor && !toIsProcessor
          })

          return {
            id: `power-auto-${Date.now()}-${index}`,
            name: dataPage.name.startsWith('Data') ? dataPage.name.replace('Data', 'Power') : `Power ${dataPage.name}`,
            workspaceElements: elementsWithoutProcessors,
            cableRoutes: cablesWithoutProcessors,
            powerConnections: [],
            mirePowerConnections: []  // NEW: Required field
          }
        })

        // Remplacer toutes les pages Power par les nouvelles
        setPowerPages(newPowerPages)
        setCurrentPowerPageId(newPowerPages[0].id)

        toast.success(t('synoptic.toast.pagesCopied'), {
          description: (t('synoptic.toast.pagesCopiedDesc') || `${newPowerPages.length} page(s) Data copiée(s) vers Power (sans processeurs)`).replace('{count}', String(newPowerPages.length)),
          duration: 4000
        })

      }

      setHasVisitedPowerStep(true)
    } else if (step === "export") {
      // En mode export, masquer les panneaux latéraux (sauf cartouche) et afficher tous les câbles
      setShowProcessors(false)
      setShowPanels(false)
      setShowCabinets(false)
      setShowCabling(true)
      setSelectedCableType(null)
      // Réinitialiser les sélections qui pourraient filtrer les câbles
      setSelectedPort(null)
      setSelectedOutlet(null)
      setSelectedProcessorForConnection(null)
      setSelectedCabinetForConnection(null)
      // Définir exportViewMode en fonction du mode précédent
      // Si on vient du mode cabling, afficher les pages Power, sinon Data
      if (currentStep === 'cabling') {
        setExportViewMode('power')
      } else {
        setExportViewMode('data')
      }
    }
  }

  // Gérer la navigation back intelligente
  const handleGoBack = () => {
    if (typeof window !== 'undefined' && window.location?.protocol === 'file:') {
      const pathname = window.location.pathname || '';
      const outIdx = pathname.lastIndexOf('/out/');
      const base = outIdx >= 0 ? pathname.substring(0, outIdx + '/out/'.length) : pathname;
      window.location.href = base + 'index.html';
    } else {
      if (window.history.length > 1) window.history.back();
      else window.location.href = '/';
    }
  }

  // Fonctions de navigation canvas - OPTIMISÉ avec useCallback
  // Doit être défini AVANT handleDrop pour éviter l'erreur de référence
  const screenToSVG = useCallback((screenX: number, screenY: number) => {
    if (!workspaceRef.current) return { x: 0, y: 0 }

    const rect = workspaceRef.current.getBoundingClientRect()

    // Convertir les coordonnées écran en coordonnées normalisées (0-1)
    const normalizedX = (screenX - rect.left) / rect.width
    const normalizedY = (screenY - rect.top) / rect.height

    // Convertir en coordonnées SVG basées sur le viewBox actuel
    const svgX = viewBox.x + normalizedX * viewBox.width
    const svgY = viewBox.y + normalizedY * viewBox.height

    return { x: svgX, y: svgY }
  }, [viewBox])

  // Convertir un delta en pixels écran en delta en unités SVG (pour le drag fluide)
  const screenDeltaToSVGDelta = useCallback((deltaX: number, deltaY: number) => {
    if (!workspaceRef.current) return { x: 0, y: 0 }

    const rect = workspaceRef.current.getBoundingClientRect()

    // Convertir le delta en proportion du SVG
    const svgDeltaX = (deltaX / rect.width) * viewBox.width
    const svgDeltaY = (deltaY / rect.height) * viewBox.height

    return { x: svgDeltaX, y: svgDeltaY }
  }, [viewBox])

  // Fonctions de drag & drop - OPTIMISÉ avec useCallback
  const handleDragStart = useCallback((type: 'processor' | 'panel' | 'cabinet' | 'image', data: LEDProcessor | LEDPanel | PowerCabinet | WorkspaceElement, event?: React.DragEvent | React.MouseEvent) => {
    setDraggedItem({ type, data })
    setIsDraggingFromList(true)

    // Notify onboarding that we're dragging (for workspace highlight)
    if (onboarding.state.isActive && (type === 'processor' || type === 'cabinet')) {
      onboarding.setDragging(true, type)
    }

    if (isTauri && event) {
      // Pour Tauri, utiliser les événements de souris
      setDragMousePosition({ x: event.clientX, y: event.clientY })
    } else if (event && 'dataTransfer' in event && event.dataTransfer) {
      // Pour Web, utiliser le drag HTML5 natif
      event.dataTransfer.effectAllowed = 'copy'
      event.dataTransfer.setData('text/plain', `${type}-${data.id || Date.now()}`)
      // Créer une image de drag personnalisée (vide pour utiliser notre overlay)
      const dragImage = document.createElement('div')
      dragImage.style.position = 'absolute'
      dragImage.style.top = '-1000px'
      dragImage.style.width = '1px'
      dragImage.style.height = '1px'
      document.body.appendChild(dragImage)
      event.dataTransfer.setDragImage(dragImage, 0, 0)
      setTimeout(() => document.body.removeChild(dragImage), 0)
    }
  }, [isTauri, onboarding.state.isActive, onboarding.setDragging])

  // Suivre la position de la souris pendant le drag (uniquement pour Tauri)
  useEffect(() => {
    if (!isTauri || !draggedItem || !isDraggingFromList) return

    // Empêcher la sélection de texte pendant le drag
    const preventSelection = (e: Event) => {
      e.preventDefault()
    }

    // Désactiver la sélection de texte sur tout le document
    setUserSelect(document.body, 'none')

    // Empêcher les événements de sélection
    document.addEventListener('selectstart', preventSelection)
    document.addEventListener('dragstart', preventSelection)

    const handleMouseMove = (e: MouseEvent) => {
      setDragMousePosition({ x: e.clientX, y: e.clientY })
    }

    const handleMouseUp = (e: MouseEvent) => {
      // Si on relâche la souris sur le workspace, créer l'élément
      if (workspaceRef.current && draggedItem) {
        const rect = workspaceRef.current.getBoundingClientRect()
        // Vérifier si la souris est dans le workspace
        if (e.clientX >= rect.left && e.clientX <= rect.right &&
          e.clientY >= rect.top && e.clientY <= rect.bottom) {
          const svgPoint = screenToSVG(e.clientX, e.clientY)
          let x = svgPoint.x
          let y = svgPoint.y

          // Limites A3
          x = Math.max(-450, Math.min(450, x))
          y = Math.max(-320, Math.min(320, y))

          // Vérifier les limites démo pour processeurs et armoires
          if (demoMode.state.isDemo && !isPro) {
            if (draggedItem.type === 'processor') {
              const currentProcessors = workspaceElements.filter(el => el.type === 'processor').length
              if (!demoMode.checkProcessorLimit(currentProcessors)) {
                setDemoLimitDialog({ open: true, limitType: 'processor', currentCount: currentProcessors, maxCount: demoMode.limits.maxProcessors })
                setDraggedItem(null); setDragMousePosition(null); setIsDraggingFromList(false); onboarding.setDragging(false)
                return
              }
            }
            if (draggedItem.type === 'cabinet') {
              const currentCabinets = workspaceElements.filter(el => el.type === 'cabinet').length
              if (!demoMode.checkCabinetLimit(currentCabinets)) {
                setDemoLimitDialog({ open: true, limitType: 'cabinet', currentCount: currentCabinets, maxCount: demoMode.limits.maxCabinets })
                setDraggedItem(null); setDragMousePosition(null); setIsDraggingFromList(false); onboarding.setDragging(false)
                return
              }
            }
          }

          // Traitement spécial mire : avant la construction de newElement pour éviter le crash sur LEDPanel.dimensions
          if (draggedItem.type === 'image') {
            const mireEl = draggedItem.data as WorkspaceElement
            const mireWidth = mireEl.size?.width || 160
            const mireHeight = mireEl.size?.height || 120
            const newMireEl: WorkspaceElement = {
              ...mireEl,
              id: `image-${Date.now()}`,
              position: { x: x - mireWidth / 2, y: y - mireHeight / 2 },
              size: { width: mireWidth, height: mireHeight },
              data: {
                ...mireEl.data,
                sourceImageId: mireEl.id,
                imageData: mireEl.data?.imageData || mireEl.data?.capturedImage,
                isPendingConversion: false,
                savedCableRoutes: undefined,
                savedPanelConnections: undefined,
                savedPowerConnections: undefined
              }
            }
            setPowerPages(prev => prev.map(p =>
              p.id === currentPowerPageId
                ? { ...p, workspaceElements: [...p.workspaceElements, newMireEl] }
                : p
            ))
            saveToHistoryImmediate('Mire ajoutée au Power')
          } else if (draggedItem.type === 'panel' && currentStep === 'data') {
            // Étape data : panels uniquement sur mires, pas sur le workspace vide
            const panel = draggedItem.data as LEDPanel
            const mireAtPosition = workspaceElements.find(el => {
              if (el.type !== 'image' || !el.data?.mireConfig?.ledWallMode) return false
              return x >= el.position.x && x <= el.position.x + el.size.width &&
                     y >= el.position.y && y <= el.position.y + el.size.height
            })

            if (mireAtPosition) {
              const config = mireAtPosition.data.mireConfig
              if (!config?.ledWallMode) return
              const { columns, rows } = config.ledWallMode
              setWorkspaceElements(prev => prev.map(el => {
                if (el.id === mireAtPosition.id) {
                  return {
                    ...el,
                    data: {
                      ...el.data,
                      linkedPanel: panel,
                      name: `${el.data.name?.split(' - ')[0] || 'Mire'} - ${panel.brand} ${panel.name}`,
                      mireConfig: {
                        ...config,
                        ledWallMode: { ...config.ledWallMode, panelWidth: panel.resolution.width, panelHeight: panel.resolution.height },
                        totalResolution: { width: columns * panel.resolution.width, height: rows * panel.resolution.height },
                        totalPower: columns * rows * panel.powerConsumption
                      }
                    }
                  }
                }
                return el
              }))
              toast.success(t('synoptic.toast.panelLinkedToMire').replace('{name}', panel.name), { description: `${t('synoptic.resolution')}: ${columns * panel.resolution.width}×${rows * panel.resolution.height}px` })
              saveToHistoryImmediate('Panneau lié à la mire')
            }
            // Sinon : pas de mire sous le curseur → ignorer silencieusement
          } else {
            // Créer l'élément (processeur, armoire, ou panneau hors étape data)
            const newElement: WorkspaceElement = {
              id: `${draggedItem.type}-${Date.now()}`,
              type: draggedItem.type,
              data: draggedItem.data,
              position: { x, y },
              size: draggedItem.type === 'processor'
                ? (draggedItem.data as LEDProcessor).imageDimensions
                  ? { width: (draggedItem.data as LEDProcessor).imageDimensions!.width * 0.5, height: (draggedItem.data as LEDProcessor).imageDimensions!.height * 0.5 }
                  : { width: 160, height: 120 }
                : draggedItem.type === 'cabinet'
                  ? { width: (draggedItem.data as PowerCabinet).dimensions.width * 0.75, height: (draggedItem.data as PowerCabinet).dimensions.height * 0.75 }
                  : { width: (draggedItem.data as LEDPanel).dimensions.width * 0.5, height: (draggedItem.data as LEDPanel).dimensions.height * 0.5 }
            }
            const elementType = draggedItem.type === 'processor' ? 'Processeur' :
              draggedItem.type === 'panel' ? 'Panneau' : 'Armoire'
            setWorkspaceElements(prev => [...prev, newElement])
            saveToHistoryImmediate(`${elementType} ajouté`)
            
            // Onboarding: détecter le placement d'un processeur ou d'une armoire (Tauri)
            if (onboarding.state.isActive) {
              if (draggedItem.type === 'processor') {
                onboarding.completeAction('processor-placed')
              } else if (draggedItem.type === 'cabinet') {
                onboarding.completeAction('cabinet-placed')
              }
            }
          }
        }
      }

      // Nettoyer
      setDraggedItem(null)
      setDragMousePosition(null)
      setIsDraggingFromList(false)
      onboarding.setDragging(false)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
      document.removeEventListener('selectstart', preventSelection)
      document.removeEventListener('dragstart', preventSelection)
      setUserSelect(document.body, '')
    }
  }, [isTauri, draggedItem, isDraggingFromList, screenToSVG, saveToHistoryImmediate, demoMode, isPro, workspaceElements.length, onboarding, currentPowerPageId])

  // Fonction helper pour créer une relation backup entre deux ports
  const createPortBackup = useCallback((sourcePort: number, targetPort: number) => {
    if (!selectedProcessorForConnection || sourcePort === targetPort) return

    // Désactiver la sauvegarde automatique
    setSkipNextHistorySave(true)

    // Mettre à jour backupPorts pour ce processeur
    const newBackupPorts = { ...backupPorts }
    const processorBackups = { ...(newBackupPorts[selectedProcessorForConnection] || {}) }

    // Libérer le port draggé de toute relation précédente
    Object.keys(processorBackups).forEach(primary => {
      if (processorBackups[Number(primary)] === sourcePort) {
        delete processorBackups[Number(primary)]
      }
    })

    // Supprimer l'ancien backup du port cible
    delete processorBackups[targetPort]

    // Associer le port draggé comme backup du port cible
    processorBackups[targetPort] = sourcePort

    newBackupPorts[selectedProcessorForConnection] = processorBackups
    setBackupPorts(newBackupPorts)

    // Générer une description pour l'historique
    const description = `Port ${sourcePort} configuré comme backup du port ${targetPort}`

    // Recalculer immédiatement les câbles du port PRIMAIRE (pas le backup)
    // Les connexions sont stockées sur le port primaire (targetPort), pas sur le backup (sourcePort)
    const connections = panelConnections.filter(conn =>
      conn.processorId === selectedProcessorForConnection &&
      conn.port === targetPort // Port PRIMAIRE
    ).sort((a, b) => a.order - b.order)

    if (connections.length > 0) {
      // Recalculer tous les câbles concernés
      setCableRoutes(prev => prev.map(cable => {
        // Trouver si ce câble est concerné par le port backup
        const fromConnection = connections.find(c => c.panelId === cable.from)
        const toConnection = connections.find(c => c.panelId === cable.to)

        if (!fromConnection && !toConnection) {
          return cable // Ce câble n'est pas concerné
        }

        // Ce câble est concerné - recalculer ses points
        const fromElement = workspaceElements.find(el => el.id === cable.from)
        const toElement = workspaceElements.find(el => el.id === cable.to)

        if (!fromElement || !toElement) return cable
        if (!hasValidCablePoints(cable)) return cable

        // Déterminer si le panneau destination est le dernier (aura badge RY)
        const isDestLastPanel = connections[connections.length - 1]?.panelId === toElement.id
        const hasBackup = sourcePort // Le port qui devient backup

        // Calculer la direction du câble
        const deltaX = toElement.position.x - fromElement.position.x
        const deltaY = toElement.position.y - fromElement.position.y
        const isHorizontal = Math.abs(deltaX) > Math.abs(deltaY)

        let newToPoint = { ...cable.points[1] }

        // Si c'est le dernier panneau avec backup, ajuster le point d'arrivée
        if (isDestLastPanel && hasBackup) {
          if (isHorizontal) {
            // Câble horizontal : arriver du côté d'où vient le câble
            newToPoint = {
              x: deltaX > 0
                ? toElement.position.x + toElement.size.width * 0.25
                : toElement.position.x + toElement.size.width * 0.75,
              y: toElement.position.y + toElement.size.height / 2
            }
          } else {
            // Câble vertical
            if (deltaY > 0) {
              newToPoint = {
                x: toElement.position.x + toElement.size.width / 2,
                y: toElement.position.y + toElement.size.height * 0.25
              }
            } else {
              newToPoint = {
                x: toElement.position.x + toElement.size.width / 2,
                y: toElement.position.y + toElement.size.height * 0.75
              }
            }
          }
        }

        return {
          ...cable,
          points: [cable.points[0], newToPoint]
        }
      }))
    }

    // Sauvegarder immédiatement avec description
    setTimeout(() => {
      setSkipNextHistorySave(false)
      saveToHistoryImmediate(description)
    }, 100)

    // Onboarding: détecter la création de redondance
    if (onboarding.state.isActive) {
      onboarding.completeAction('redundancy-created')
    }

    setDraggedPort(null)
  }, [selectedProcessorForConnection, backupPorts, panelConnections, workspaceElements, saveToHistoryImmediate, onboarding])

  // Ref stable pour createPortBackup (évite re-trigger du useEffect drag)
  const createPortBackupRef = useRef(createPortBackup)
  createPortBackupRef.current = createPortBackup

  // Suivre le drag de port avec la souris (Web + Tauri)
  useEffect(() => {
    if (!draggedPort || !selectedProcessorForConnection || currentStep !== 'data') return

    // Empêcher la sélection de texte pendant le drag
    const preventSelection = (e: Event) => {
      e.preventDefault()
    }

    // Désactiver la sélection de texte sur tout le document
    setUserSelect(document.body, 'none')

    // Empêcher les événements de sélection
    document.addEventListener('selectstart', preventSelection)
    document.addEventListener('dragstart', preventSelection)

    const handleMouseMove = (e: MouseEvent) => {
      // Suivre la position de la souris pour l'overlay visuel
      setPortDragMousePosition({ x: e.clientX, y: e.clientY })
      
      // Trouver l'élément sous la souris
      const elementUnderMouse = document.elementFromPoint(e.clientX, e.clientY)
      if (elementUnderMouse) {
        // Chercher le bouton de port parent
        const portButton = elementUnderMouse.closest('button[data-port-number]') as HTMLElement
        if (portButton) {
          const targetPort = parseInt(portButton.getAttribute('data-port-number') || '0')
          if (targetPort && targetPort !== draggedPort) {
            setPortDragTarget(targetPort)
          } else {
            setPortDragTarget(null)
          }
        } else {
          setPortDragTarget(null)
        }
      } else {
        setPortDragTarget(null)
      }
    }

    const handleMouseUp = (e: MouseEvent) => {
      // Trouver l'élément sous la souris au moment du relâchement
      const elementUnderMouse = document.elementFromPoint(e.clientX, e.clientY)
      let targetPort: number | null = null
      
      if (elementUnderMouse) {
        // Chercher le bouton de port parent
        const portButton = elementUnderMouse.closest('button[data-port-number]') as HTMLElement
        if (portButton) {
          targetPort = parseInt(portButton.getAttribute('data-port-number') || '0')
        }
      }
      
      // Si on relâche la souris sur un autre port, créer la relation backup
      if (targetPort && targetPort !== draggedPort) {
        createPortBackupRef.current(draggedPort, targetPort)
      } else {
        setDraggedPort(null)
      }
      setPortDragTarget(null)
      setPortDragMousePosition(null)
    }

    // Utiliser mousemove/mouseup pour les deux plateformes (pas de HTML5 drag)
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
      document.removeEventListener('selectstart', preventSelection)
      document.removeEventListener('dragstart', preventSelection)
      setUserSelect(document.body, '')
      setPortDragTarget(null)
      setPortDragMousePosition(null)
    }
  }, [draggedPort, selectedProcessorForConnection, currentStep])

  // Suivre le resize de la bulle de mire
  useEffect(() => {
    if (!resizingBubble) return

    const handleMouseMove = (e: MouseEvent) => {
      const dx = e.clientX - resizingBubble.startX
      const dy = e.clientY - resizingBubble.startY
      const newWidth = Math.max(120, Math.min(500, resizingBubble.startWidth + dx))
      const newHeight = Math.max(30, Math.min(150, resizingBubble.startHeight + dy))
      setWorkspaceElements(prev => prev.map(el => {
        if (el.id === resizingBubble.elementId) {
          return { ...el, data: { ...el.data, bubbleConfig: { ...(el.data?.bubbleConfig || {}), width: Math.round(newWidth), height: Math.round(newHeight) } } }
        }
        return el
      }))
    }

    const handleMouseUp = () => {
      setResizingBubble(null)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [resizingBubble])

  // Suivre le drag du bouton backup avec la souris
  useEffect(() => {
    if (!isDraggingBackupButton || !processorForBackup || currentStep !== 'data') return

    // Empêcher la sélection de texte pendant le drag
    const preventSelection = (e: Event) => {
      e.preventDefault()
    }

    // Désactiver la sélection de texte sur tout le document
    setUserSelect(document.body, 'none')

    // Empêcher les événements de sélection
    document.addEventListener('selectstart', preventSelection)
    document.addEventListener('dragstart', preventSelection)

    const handleMouseMove = (e: MouseEvent) => {
      // Suivre la position de la souris pour l'overlay visuel
      setBackupButtonDragMousePosition({ x: e.clientX, y: e.clientY })
      
      // Trouver l'élément sous la souris
      const elementUnderMouse = document.elementFromPoint(e.clientX, e.clientY)
      if (elementUnderMouse) {
        // Chercher le processeur parent (via SVG ou élément)
        const svgElement = elementUnderMouse.closest('svg')
        if (svgElement) {
          // Convertir les coordonnées de la souris en coordonnées SVG
          const svgPoint = svgElement.createSVGPoint()
          svgPoint.x = e.clientX
          svgPoint.y = e.clientY
          const ctm = svgElement.getScreenCTM()
          if (ctm) {
            const svgCoords = svgPoint.matrixTransform(ctm.inverse())
            
            // Trouver le processeur sous la souris
            const processorUnderMouse = workspaceElements.find(el => {
              if (el.type !== 'processor') return false
              const bounds = (el.data as LEDProcessor).image
                ? getActualImageBounds(el)
                : {
                    x: el.position.x,
                    y: el.position.y,
                    width: el.size.width,
                    height: el.size.height
                  }
              return svgCoords.x >= bounds.x && 
                     svgCoords.x <= bounds.x + bounds.width &&
                     svgCoords.y >= bounds.y && 
                     svgCoords.y <= bounds.y + bounds.height
            })
            
            if (processorUnderMouse && processorUnderMouse.id !== processorForBackup) {
              setBackupButtonDragTarget(processorUnderMouse.id)
            } else {
              setBackupButtonDragTarget(null)
            }
          } else {
            setBackupButtonDragTarget(null)
          }
        } else {
          setBackupButtonDragTarget(null)
        }
      } else {
        setBackupButtonDragTarget(null)
      }
    }

    const handleMouseUp = (e: MouseEvent) => {
      if (backupButtonDragTarget && processorForBackup) {
        // Créer la relation backup
        setBackupProcessor(backupButtonDragTarget, processorForBackup)
        toast.success(t('synoptic.backupProcessorSet') || 'Processeur configuré comme backup')
      }
      
      // Réinitialiser
      setIsDraggingBackupButton(false)
      setBackupButtonDragMousePosition(null)
      setBackupButtonDragTarget(null)
      setProcessorForBackup(null)
      
      // Réactiver la sélection de texte
      setUserSelect(document.body, '')
      
      // Nettoyer les event listeners
      document.removeEventListener('selectstart', preventSelection)
      document.removeEventListener('dragstart', preventSelection)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      setUserSelect(document.body, '')
      document.removeEventListener('selectstart', preventSelection)
      document.removeEventListener('dragstart', preventSelection)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDraggingBackupButton, processorForBackup, currentStep, backupButtonDragTarget, workspaceElements, getActualImageBounds, setBackupProcessor, t, screenToSVG])

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    if (readOnly || isTauri || !draggedItem || !workspaceRef.current) return

    const svgPoint = screenToSVG(event.clientX, event.clientY)
    let x = svgPoint.x
    let y = svgPoint.y
    x = Math.max(-450, Math.min(450, x))
    y = Math.max(-320, Math.min(320, y))

    if (draggedItem.type === 'panel' && currentStep === 'data') {
      const panel = draggedItem.data as LEDPanel
      const mireAtPosition = workspaceElements.find(el => {
        if (el.type !== 'image' || !el.data?.mireConfig) return false
        return x >= el.position.x && x <= el.position.x + el.size.width &&
               y >= el.position.y && y <= el.position.y + el.size.height
      })

      if (mireAtPosition) {
        const config = mireAtPosition.data.mireConfig
        if (!config?.ledWallMode) {
          toast.error(t('synoptic.toast.invalidMireConfig'))
          return
        }
        const { columns, rows } = config.ledWallMode
        const totalPanels = columns * rows
        const totalPixels = totalPanels * panel.resolution.width * panel.resolution.height
        
        setWorkspaceElements(prev => prev.map(el => {
          if (el.id === mireAtPosition.id) {
            return {
              ...el,
              data: {
                ...el.data,
                linkedPanel: panel,
                name: `${el.data.name?.split(' - ')[0] || 'Mire'} - ${panel.brand} ${panel.name}`,
                mireConfig: {
                  ...config,
                  ledWallMode: { ...config.ledWallMode, panelWidth: panel.resolution.width, panelHeight: panel.resolution.height },
                  totalResolution: { width: columns * panel.resolution.width, height: rows * panel.resolution.height },
                  totalPower: columns * rows * panel.powerConsumption
                }
              }
            }
          }
          return el
        }))
        
        // Pré-remplir la configuration technique du mur
        setWallTechnicalConfig(prev => ({
          ...prev,
          panelResolution: panel.resolution || undefined,
          panelWeight: panel.panelWeight ? (() => {
            const parsed = parseFloat(panel.panelWeight)
            return isNaN(parsed) ? undefined : parsed
          })() : undefined,
          totalPanels,
          totalPixels
        }))
        
        toast.success(t('synoptic.toast.panelLinkedToMire').replace('{name}', panel.name), { description: `${t('synoptic.resolution')}: ${columns * panel.resolution.width}×${rows * panel.resolution.height}px` })
        setDraggedItem(null)
        setDragMousePosition(null)
        setIsDraggingFromList(false)
        saveToHistoryImmediate('Panneau lié à la mire')
        return
      }

      // Pas de mire sous le curseur → ignorer le drop (panels uniquement sur mires à l'étape data)
      setDraggedItem(null)
      setDragMousePosition(null)
      setIsDraggingFromList(false)
      return
    }

    // Vérifier les limites démo pour processeurs et armoires
    if (demoMode.state.isDemo && !isPro) {
      if (draggedItem.type === 'processor') {
        const currentProcessors = workspaceElements.filter(el => el.type === 'processor').length
        if (!demoMode.checkProcessorLimit(currentProcessors)) {
          setDemoLimitDialog({ open: true, limitType: 'processor', currentCount: currentProcessors, maxCount: demoMode.limits.maxProcessors })
          setDraggedItem(null); setDragMousePosition(null); setIsDraggingFromList(false)
          return
        }
      }
      if (draggedItem.type === 'cabinet') {
        const currentCabinets = workspaceElements.filter(el => el.type === 'cabinet').length
        if (!demoMode.checkCabinetLimit(currentCabinets)) {
          setDemoLimitDialog({ open: true, limitType: 'cabinet', currentCount: currentCabinets, maxCount: demoMode.limits.maxCabinets })
          setDraggedItem(null); setDragMousePosition(null); setIsDraggingFromList(false)
          return
        }
      }
    }

    // Traitement spécial mire : avant la construction de newElement pour éviter le crash sur LEDPanel.dimensions
    if (draggedItem.type === 'image') {
      const mireEl = draggedItem.data as WorkspaceElement
      const mireWidth = mireEl.size?.width || 160
      const mireHeight = mireEl.size?.height || 120
      const newMireEl: WorkspaceElement = {
        ...mireEl,
        id: `image-${Date.now()}`,
        position: { x: x - mireWidth / 2, y: y - mireHeight / 2 },
        size: { width: mireWidth, height: mireHeight },
        data: {
          ...mireEl.data,
          sourceImageId: mireEl.id,
          imageData: mireEl.data?.imageData || mireEl.data?.capturedImage,
          isPendingConversion: false,
          savedCableRoutes: undefined,
          savedPanelConnections: undefined,
          savedPowerConnections: undefined
        }
      }
      setWorkspaceElements(prev => [...prev, newMireEl])
      setDraggedItem(null)
      setDragMousePosition(null)
      setIsDraggingFromList(false)
      saveToHistoryImmediate(`Mire ajoutée au ${currentStep === 'data' ? 'Data' : 'Power'}`)
      return
    }

    const newElement: WorkspaceElement = {
      id: `${draggedItem.type}-${Date.now()}`,
      type: draggedItem.type,
      data: draggedItem.data,
      position: { x, y },
      size: draggedItem.type === 'processor'
        ? (draggedItem.data as LEDProcessor).imageDimensions
          ? { width: (draggedItem.data as LEDProcessor).imageDimensions!.width * 0.5, height: (draggedItem.data as LEDProcessor).imageDimensions!.height * 0.5 }
          : { width: 160, height: 120 }
        : draggedItem.type === 'cabinet'
          ? { width: (draggedItem.data as PowerCabinet).dimensions.width * 0.75, height: (draggedItem.data as PowerCabinet).dimensions.height * 0.75 }
          : { width: (draggedItem.data as LEDPanel).dimensions.width * 0.5, height: (draggedItem.data as LEDPanel).dimensions.height * 0.5 }
    }
    setWorkspaceElements(prev => [...prev, newElement])
    setDraggedItem(null)
    setDragMousePosition(null)
    setIsDraggingFromList(false)
    saveToHistoryImmediate(`${draggedItem.type === 'processor' ? 'Processeur' : draggedItem.type === 'panel' ? 'Panneau' : 'Armoire'} ajouté`)

    // Onboarding: détecter le placement d'un processeur ou d'une armoire
    if (onboarding.state.isActive) {
      if (draggedItem.type === 'processor') {
        onboarding.completeAction('processor-placed')
      } else if (draggedItem.type === 'cabinet') {
        onboarding.completeAction('cabinet-placed')
      }
    }
  }, [draggedItem, screenToSVG, saveToHistoryImmediate, isTauri, currentStep, workspaceElements, demoMode, isPro, onboarding, currentPowerPageId])

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy'
    if (draggedItem) setDragMousePosition({ x: event.clientX, y: event.clientY })
  }, [draggedItem])

  /**
   * Gère le clic sur un panneau pour créer une connexion d'alimentation
   * Mutation atomique : câble + connexion écrits dans une seule transition d'état,
   * avec dédup et ordre calculé sur le max de la chaîne (pas la longueur).
   */
  const handlePanelClickForPowerConnection = useCallback((
    elementId: string,
    element: WorkspaceElement
  ): boolean => {
    if (element.type !== 'panel' || currentStep !== 'cabling' || !selectedCabinetForConnection || selectedOutlet === null) {
      return false
    }

    // Vérifier si le panneau est déjà alimenté (check UX — l'invariant est
    // aussi garanti par l'updater atomique sur l'état frais)
    const isAlreadyPowered = powerConnections.some(conn => conn.panelId === elementId)
    if (isAlreadyPowered) {
      alert(t("alert.panelAlreadyPowered"))
      return true
    }

    const cabinetId = selectedCabinetForConnection
    const outlet = selectedOutlet
    applyPowerPageWiring(page => appendPanelPowerConnection(page, {
      targetPanelId: elementId,
      cabinetId,
      outlet,
      color: getOutletColor(outlet)
    }))
    setCurrentPanelForCablingNow(elementId)

    // Désactiver la sauvegarde automatique et sauvegarder immédiatement
    setSkipNextHistorySave(true)
    setTimeout(() => {
      setSkipNextHistorySave(false)
      saveToHistoryImmediate('Panneau alimenté')
    }, 100)

    return true
  }, [
    currentStep,
    selectedCabinetForConnection,
    selectedOutlet,
    powerConnections,
    applyPowerPageWiring,
    getOutletColor,
    setCurrentPanelForCablingNow,
    saveToHistoryImmediate,
    t
  ])

  /**
   * Mode liaison armoire → armoire : clic sur l'armoire cible pour créer le lien
   */
  const handleCabinetClickForLink = useCallback((
    elementId: string,
    element: WorkspaceElement
  ): boolean => {
    if (!cabinetLinkMode || currentStep !== 'cabling' || element.type !== 'cabinet' || !selectedCabinetForConnection) {
      return false
    }

    // Clic sur l'armoire source : annuler le mode liaison
    if (elementId === selectedCabinetForConnection) {
      setCabinetLinkMode(false)
      return true
    }

    // Pré-validation sur l'état courant (le patch réutilise le même validateur sur l'état frais)
    const rejection = validateCabinetPowerLink(
      currentPowerPage?.workspaceElements || [],
      currentPowerPage?.cableRoutes || [],
      selectedCabinetForConnection,
      elementId
    )
    setCabinetLinkMode(false)
    if (rejection) {
      toast.info(t('synoptic.cabinetLinkRejected'))
      return true
    }

    const sourceId = selectedCabinetForConnection
    applyPowerPageWiring(page => appendCabinetPowerLink(page, {
      sourceCabinetId: sourceId,
      targetCabinetId: elementId,
      color: '#F97316'
    }))

    setSkipNextHistorySave(true)
    setTimeout(() => {
      setSkipNextHistorySave(false)
      saveToHistoryImmediate('Liaison armoire créée')
    }, 100)
    toast.success(t('synoptic.cabinetLinkCreated'))
    return true
  }, [
    cabinetLinkMode,
    currentStep,
    currentPowerPage,
    selectedCabinetForConnection,
    applyPowerPageWiring,
    setSkipNextHistorySave,
    saveToHistoryImmediate,
    t
  ])

  /** Supprime une liaison armoire → armoire (câble power entre deux armoires) */
  const removeCabinetLink = useCallback((cableId: string) => {
    applyPowerPageWiring(page => ({
      cableRoutes: (page.cableRoutes || []).filter(c => c.id !== cableId)
    }))
    setSkipNextHistorySave(true)
    setTimeout(() => {
      setSkipNextHistorySave(false)
      saveToHistoryImmediate('Liaison armoire supprimée')
    }, 100)
  }, [applyPowerPageWiring, setSkipNextHistorySave, saveToHistoryImmediate])

  /**
   * Gère le clic sur un panneau pour créer une connexion DATA (équivalent Power)
   */
  const handlePanelClickForDataConnection = useCallback((
    elementId: string,
    element: WorkspaceElement
  ): boolean => {
    if (element.type !== 'panel' || currentStep !== 'data' || !selectedProcessorForConnection || selectedPort === null) {
      return false
    }

    // Vérifier si le panneau est déjà connecté à ce port (check UX — l'invariant
    // est aussi garanti par l'updater atomique sur l'état frais)
    const isAlreadyConnected = panelConnections.some(
      conn => conn.panelId === elementId &&
        conn.processorId === selectedProcessorForConnection &&
        conn.port === selectedPort
    )
    if (isAlreadyConnected) {
      toast.info(t('synoptic.toast.panelAlreadyConnected'), {
        duration: 1500
      })
      return true
    }

    const processorId = selectedProcessorForConnection
    const port = selectedPort
    applyDataPageWiring(page => appendPanelDataConnection(page, {
      targetPanelId: elementId,
      processorId,
      port,
      color: getPortColor(port, processorId)
    }))
    setCurrentPanelForCablingNow(elementId)

    // Désactiver la sauvegarde automatique et sauvegarder immédiatement
    setSkipNextHistorySave(true)
    setTimeout(() => {
      setSkipNextHistorySave(false)
      saveToHistoryImmediate('Panneau connecté')
    }, 100)

    return true
  }, [
    currentStep,
    selectedProcessorForConnection,
    selectedPort,
    panelConnections,
    applyDataPageWiring,
    getPortColor,
    setCurrentPanelForCablingNow,
    saveToHistoryImmediate,
    t
  ])

const handlePanelContextMenu = useCallback((e: React.MouseEvent, element: WorkspaceElement, gridPosition?: { row: number, col: number }) => {
  e.preventDefault()
  e.stopPropagation()
  if (readOnly) return

  // Mode Power: clic droit pour tronquer chaîne power
  if (currentStep === 'cabling' && selectedCabinetForConnection && selectedOutlet !== null && element.type === 'panel') {
    const targetPanelId = element.id
    const connection = powerConnections.find((c: any) =>
      c.panelId === targetPanelId &&
      c.cabinetId === selectedCabinetForConnection &&
      c.outlet === selectedOutlet
    )

    if (!connection) {
      toast.info(t('synoptic.toast.panelNotConnected'), { duration: 2000 })
      return
    }

    const cutOrder = connection.order
    const connectionsOnOutlet = powerConnections.filter((c: any) =>
      c.cabinetId === selectedCabinetForConnection && c.outlet === selectedOutlet
    )
    const connectionsToRemove = connectionsOnOutlet.filter(c => c.order >= cutOrder)
    const panelIdsToRemove = new Set(connectionsToRemove.map(c => c.panelId))

    if (connectionsToRemove.length === 0) return

    const newPowerConnections = powerConnections.filter(c => !(
      c.cabinetId === selectedCabinetForConnection &&
      c.outlet === selectedOutlet &&
      panelIdsToRemove.has(c.panelId)
    ))
    const newCableRoutes = cableRoutes.filter(cable => {
      if (cable.type !== 'power') return true
      return !panelIdsToRemove.has(cable.to)
    })

    setPowerConnections(newPowerConnections)
    setCableRoutes(newCableRoutes)
    saveToHistoryImmediate(`Chaîne power prise ${selectedOutlet} tronquée`)
    return
  }

  // Mode Power: clic droit sur cellule mire pour tronquer chaîne power
  if (currentStep === 'cabling' && selectedCabinetForConnection && selectedOutlet !== null && element.type === 'image' && element.data?.mireConfig && gridPosition) {
    const connection = mirePowerConnections.find((c: any) =>
      c.imageId === element.id &&
      c.cabinetId === selectedCabinetForConnection &&
      c.outlet === selectedOutlet &&
      c.gridPosition.row === gridPosition.row &&
      c.gridPosition.col === gridPosition.col
    )

    if (!connection) {
      toast.info(t('synoptic.toast.cellNotConnectedOutlet'), { duration: 2000 })
      return
    }

    const cutOrder = connection.order
    const connectionsOnOutlet = mirePowerConnections.filter((c: any) =>
      c.imageId === element.id &&
      c.cabinetId === selectedCabinetForConnection &&
      c.outlet === selectedOutlet
    )
    const connectionsToRemove = connectionsOnOutlet.filter(c => c.order >= cutOrder)
    const cellsToRemove = new Set(
      connectionsToRemove.map(c => `${c.imageId}_cell_${c.gridPosition.row}_${c.gridPosition.col}`)
    )

    if (connectionsToRemove.length === 0) return

    const newMirePowerConnections = mirePowerConnections.filter(c => !(
      c.imageId === element.id &&
      c.cabinetId === selectedCabinetForConnection &&
      c.outlet === selectedOutlet &&
      c.order >= cutOrder
    ))
    const newCableRoutes = cableRoutes.filter(cable => {
      if (cable.type !== 'power') return true
      return !cellsToRemove.has(cable.from) && !cellsToRemove.has(cable.to)
    })

    setMirePowerConnections(newMirePowerConnections)
    setCableRoutes(newCableRoutes)
    saveToHistoryImmediate(`Chaîne power prise ${selectedOutlet} tronquée sur mire`)
    return
  }

  if (currentStep !== 'data' || !selectedProcessorForConnection || selectedPort === null) {
    return
  }

  // Cas des panneaux standards
  if (element.type === 'panel') {
    const targetPanelId = element.id;
    // console.log('handlePanelContextMenu: standard panel', { targetPanelId })

    const connection = panelConnections.find((c: any) => 
      c.panelId === targetPanelId && 
      c.processorId === selectedProcessorForConnection && 
      c.port === selectedPort
    );

    // console.log('handlePanelContextMenu: found connection for panel', connection)

    if (!connection) {
      // console.log('handlePanelContextMenu: no connection found for panel, showing error')
      toast.info(t('synoptic.toast.panelNotConnectedPort'), {
        duration: 2000
      })
      return;
    }

    // Tronquer la chaîne à partir de cette connexion (incluse)
    const cutOrder = connection.order;
    const connectionsOnPort = panelConnections.filter((c: any) =>
      c.processorId === selectedProcessorForConnection && c.port === selectedPort
    );

    const connectionsToRemove = connectionsOnPort.filter(c => c.order >= cutOrder);
    const panelIdsToRemove = new Set(connectionsToRemove.map(c => c.panelId));

    if (connectionsToRemove.length === 0) return;

    const newPanelConnections = panelConnections.filter(c => !panelIdsToRemove.has(c.panelId));

    const newCableRoutes = cableRoutes.filter(cable => {
      if (cable.type !== 'data') return true;
      return !panelIdsToRemove.has(cable.to);
    });

    setPanelConnections(newPanelConnections);
    setCableRoutes(newCableRoutes);

    saveToHistoryImmediate(`Chaîne du port ${selectedPort} tronquée`);
    return;
  }

  // Cas des cellules de mire
  if (element.type === 'image' && element.data?.mireConfig && gridPosition) {
    // console.log('handlePanelContextMenu: mire cell', { 
    //  imageId: element.id, 
    //  row: gridPosition.row, 
    //  col: gridPosition.col,
    //  selectedProcessorForConnection,
    // selectedPort
    // })

    // Chercher la connexion dans mireConnections
    const connection = mireConnections.find((c: any) =>
      c.imageId === element.id &&
      c.processorId === selectedProcessorForConnection &&
      c.port === selectedPort &&
      c.gridPosition.row === gridPosition.row &&
      c.gridPosition.col === gridPosition.col
    );

    // console.log('handlePanelContextMenu: found connection for mire cell', connection)

    if (!connection) {
      // console.log('handlePanelContextMenu: no connection found for mire cell, showing error')
      toast.info(t('synoptic.toast.cellNotConnectedPort'), {
        duration: 2000
      })
      return;
    }

    // Tronquer la chaîne à partir de cette connexion (incluse)
    const cutOrder = connection.order;
    const connectionsOnPort = mireConnections.filter((c: any) =>
      c.imageId === element.id &&
      c.processorId === selectedProcessorForConnection &&
      c.port === selectedPort
    );

    const connectionsToRemove = connectionsOnPort.filter(c => c.order >= cutOrder);
    
    // Créer l'ensemble des IDs de cellules à supprimer pour les câbles
    const cellsToRemove = new Set(
      connectionsToRemove.map(c => `${c.imageId}_cell_${c.gridPosition.row}_${c.gridPosition.col}`)
    );

    // console.log('handlePanelContextMenu: removing connections', { 
    //  connectionsToRemove: connectionsToRemove.length,
    //  cellsToRemove: Array.from(cellsToRemove)
    //})

    if (connectionsToRemove.length === 0) return;

    // Filtrer les mireConnections
    const newMireConnections = mireConnections.filter(c => !(
      c.imageId === element.id &&
      c.processorId === selectedProcessorForConnection &&
      c.port === selectedPort &&
      c.order >= cutOrder
    ));

    // Filtrer les cableRoutes
    const newCableRoutes = cableRoutes.filter(cable => {
      if (cable.type !== 'data') return true;
      return !cellsToRemove.has(cable.from) && !cellsToRemove.has(cable.to);
    });

    setMireConnections(newMireConnections);
    setCableRoutes(newCableRoutes);

    saveToHistoryImmediate(`Chaîne du port ${selectedPort} tronquée`);
    return;
  }

  return; // Type d'élément non géré

}, [currentStep, selectedProcessorForConnection, selectedPort, selectedCabinetForConnection, selectedOutlet, panelConnections, powerConnections, mireConnections, mirePowerConnections, cableRoutes, saveToHistoryImmediate]);

// Empêcher le menu contextuel natif sur toute la page synoptique
useEffect(() => {
  const handleContextMenu = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  document.addEventListener('contextmenu', handleContextMenu)
  return () => document.removeEventListener('contextmenu', handleContextMenu)
}, [])

// Écouteur de clavier pour la navigation avec les flèches lors du câblage (Power ET Data)
// Monté une seule fois : tout l'état est lu via refs → jamais de closure périmée,
// et les mutations atomiques empêchent les doublons même en key-repeat.
useEffect(() => {
  const directionForKey = (key: string): 'up' | 'down' | 'left' | 'right' | null => {
    switch (key) {
      case 'ArrowUp': return 'up'
      case 'ArrowDown': return 'down'
      case 'ArrowLeft': return 'left'
      case 'ArrowRight': return 'right'
      default: return null
    }
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    // Ignorer si on est dans un input ou textarea
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
      return
    }
    if (event.key !== 'Escape' && !directionForKey(event.key)) return

    const nav = wiringNavRef.current
    const sel = wiringSelectionRef.current
    const currentPanelId = currentPanelForCablingRef.current
    const isPowerModeActive = nav.currentStep === 'cabling' && currentPanelId &&
      sel.cabinetId !== null && sel.outlet !== null
    const isDataModeActive = nav.currentStep === 'data' && currentPanelId &&
      sel.processorId !== null && sel.port !== null

    if (!isPowerModeActive && !isDataModeActive) return

    if (event.key === 'Escape') {
      event.preventDefault()
      setCurrentPanelForCablingNow(null)
      return
    }

    const currentPanel = nav.workspaceElements.find(e => e.id === currentPanelId && e.type === 'panel')
    if (!currentPanel) return

    const direction = directionForKey(event.key)
    if (!direction) return
    event.preventDefault()

    // Panneau le plus proche dans la direction demandée
    let targetPanel: WorkspaceElement | null = null
    let minDistance = Infinity
    nav.workspaceElements.forEach(panel => {
      if (panel.type !== 'panel' || panel.id === currentPanelId) return
      const dx = panel.position.x - currentPanel.position.x
      const dy = panel.position.y - currentPanel.position.y
      const distance = Math.sqrt(dx * dx + dy * dy)
      const isInDirection =
        (direction === 'right' && dx > 0 && Math.abs(dy) < Math.abs(dx)) ||
        (direction === 'left' && dx < 0 && Math.abs(dy) < Math.abs(dx)) ||
        (direction === 'down' && dy > 0 && Math.abs(dx) < Math.abs(dy)) ||
        (direction === 'up' && dy < 0 && Math.abs(dx) < Math.abs(dy))
      if (isInDirection && distance < minDistance) {
        targetPanel = panel
        minDistance = distance
      }
    })

    if (!targetPanel) return
    const target: WorkspaceElement = targetPanel

    if (isPowerModeActive) {
      const outlet = sel.outlet!
      const cabinetId = sel.cabinetId!
      applyPowerPageWiring(page => appendPanelPowerConnection(page, {
        targetPanelId: target.id,
        cabinetId,
        outlet,
        color: getOutletColor(outlet)
      }))
      setCurrentPanelForCablingNow(target.id)
    } else if (isDataModeActive) {
      const port = sel.port!
      const processorId = sel.processorId!
      applyDataPageWiring(page => appendPanelDataConnection(page, {
        targetPanelId: target.id,
        processorId,
        port,
        color: getPortColor(port, processorId)
      }))
      setCurrentPanelForCablingNow(target.id)
    }
  }

  document.addEventListener('keydown', handleKeyDown)
  return () => document.removeEventListener('keydown', handleKeyDown)
}, [applyDataPageWiring, applyPowerPageWiring, getPortColor, getOutletColor, setCurrentPanelForCablingNow])

  // ========== Navigation clavier des cellules mire ==========
  // Monté une seule fois : état lu via refs (cellule courante, sélections,
  // connexions) et handlers via ref → les appuis rapides en key-repeat ne
  // peuvent plus lire un état périmé ni créer de doublons (dedup atomique).
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return

      const nav = wiringNavRef.current
      const sel = wiringSelectionRef.current
      const cell = currentMireCellRef.current
      if (!cell) return

      const isDataModeActive = nav.currentStep === 'data' &&
        sel.processorId !== null && sel.port !== null
      const isPowerModeActive = nav.currentStep === 'cabling' &&
        sel.cabinetId !== null && sel.outlet !== null

      if (!isDataModeActive && !isPowerModeActive) return

      const mode = isDataModeActive ? 'data' : 'power'
      const connections = mode === 'data' ? nav.mireConnections : nav.mirePowerConnections

      if (event.key === 'Escape') {
        event.preventDefault()
        setCurrentMireCellNow(null)
        toast.info(t('synoptic.toast.navigationCancelled'))
        return
      }

      const imageElement = nav.workspaceElements.find(el => el.id === cell.imageId && el.type === 'image')
      if (!imageElement) return
      const allCells = getVirtualCellsFromImage(imageElement)
      const isConnected = (imgId: string, r: number, c: number) =>
        connections.some(conn =>
          conn.imageId === imgId && conn.gridPosition.row === r && conn.gridPosition.col === c
        )

      let targetCell: { row: number; col: number } | null = null
      switch (event.key) {
        case 'ArrowUp':    event.preventDefault(); targetCell = findClosestMireCellInDirection(cell, 'up', allCells, isConnected); break
        case 'ArrowDown':  event.preventDefault(); targetCell = findClosestMireCellInDirection(cell, 'down', allCells, isConnected); break
        case 'ArrowLeft':  event.preventDefault(); targetCell = findClosestMireCellInDirection(cell, 'left', allCells, isConnected); break
        case 'ArrowRight': event.preventDefault(); targetCell = findClosestMireCellInDirection(cell, 'right', allCells, isConnected); break
        default: return
      }

      if (targetCell) {
        if (isDataModeActive) mireHandlersRef.current.handleMireCellClickForData(cell.imageId, targetCell.row, targetCell.col)
        else mireHandlersRef.current.handleMireCellClickForPower(cell.imageId, targetCell.row, targetCell.col)
      } else {
        toast.warning(t('synoptic.toast.noCellInDirection'))
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [setCurrentMireCellNow, t])


  /**
   * Update cable positions when image moves or resizes
   */
  useEffect(() => {
    setCableRoutes(prev => prev.map(cable => {
      // Check if this cable involves mire cells
      const fromIsCell = cable.from.includes('_cell_')
      const toIsCell = cable.to.includes('_cell_')

      if (!fromIsCell && !toIsCell) {
        // Regular cable, no update needed
        return cable
      }

      // Câble cellule sans points valides : ne pas propager d'undefined
      if (!hasValidCablePoints(cable)) return cable

      let fromPoint = cable.points[0]
      let toPoint = cable.points[cable.points.length - 1]

      // Update FROM point if it's a mire cell
      if (fromIsCell) {
        // Parse cell ID: "imageId_cell_row_col"
        const parts = cable.from.split('_')
        const cellPart = parts.indexOf('cell')
        if (cellPart !== -1 && cellPart + 2 < parts.length) {
          const imageId = parts.slice(0, cellPart).join('_')
          const row = parseInt(parts[cellPart + 1])
          const col = parseInt(parts[cellPart + 2])

          const imageElement = workspaceElements.find(el => el.id === imageId)
          if (imageElement) {
            const cell = getVirtualCell(imageElement, row, col)
            if (cell) {
              fromPoint = { x: cell.centerX, y: cell.centerY }
            }
          }
        }
      } else {
        // Regular element (processor, cabinet, etc.)
        const fromElement = workspaceElements.find(el => el.id === cable.from)
        if (fromElement) {
          fromPoint = {
            x: fromElement.position.x + fromElement.size.width / 2,
            y: fromElement.position.y + fromElement.size.height / 2
          }
        }
      }

      // Update TO point if it's a mire cell
      if (toIsCell) {
        // Parse cell ID: "imageId_cell_row_col"
        const parts = cable.to.split('_')
        const cellPart = parts.indexOf('cell')
        if (cellPart !== -1 && cellPart + 2 < parts.length) {
          const imageId = parts.slice(0, cellPart).join('_')
          const row = parseInt(parts[cellPart + 1])
          const col = parseInt(parts[cellPart + 2])

          const imageElement = workspaceElements.find(el => el.id === imageId)
          if (imageElement) {
            const cell = getVirtualCell(imageElement, row, col)
            if (cell) {
              toPoint = { x: cell.centerX, y: cell.centerY }
            }
          }
        }
      } else {
        // Regular element
        const toElement = workspaceElements.find(el => el.id === cable.to)
        if (toElement) {
          toPoint = {
            x: toElement.position.x + toElement.size.width / 2,
            y: toElement.position.y + toElement.size.height / 2
          }
        }
      }

      // Return updated cable with new points
      return {
        ...cable,
        points: [fromPoint, toPoint]
      }
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceElements])

  /**
   * Gère la sélection d'éléments avec support de groupes et multi-sélection
   */
  const handleElementSelection = useCallback((
    elementId: string,
    element: WorkspaceElement,
    event: React.MouseEvent
  ) => {
    // Mode liaison armoire → armoire : le clic sur l'armoire cible crée le lien
    if (handleCabinetClickForLink(elementId, element)) {
      return; // Liaison gérée, on arrête là
    }
    // Gérer le clic pour le câblage POWER
    if (handlePanelClickForPowerConnection(elementId, element)) {
      return; // Câblage géré, on arrête là
    }
    // Gérer le clic pour le câblage DATA
    if (handlePanelClickForDataConnection(elementId, element)) {
      return; // Câblage géré, on arrête là
    }

    // Si c'est un panneau sur l'étape panels, sélectionner son port et processeur
    if (element.type === 'panel' && currentStep === 'data') {
      const connection = panelConnections.find(conn => conn.panelId === elementId)
      if (connection) {
        setSelectedProcessorForConnection(connection.processorId)
        setSelectedPort(connection.port)
      }
    }

    // Si c'est une armoire sur l'étape cabling, la sélectionner
    if (element.type === 'cabinet' && currentStep === 'cabling') {
      setSelectedCabinetForConnection(element.id)
      setSelectedOutlet(null)
    }

    // Gestion sélection multiple avec Ctrl
    if (event.ctrlKey) {
      const groupId = getGroupForElement(elementId)
      if (groupId) {
        const group = elementGroups[groupId]
        const isGroupSelected = selectedElements.some(id => group.elementIds.includes(id))
        setSelectedElements(prev =>
          isGroupSelected
            ? prev.filter(id => !group.elementIds.includes(id))
            : [...prev, ...group.elementIds]
        )
      } else {
        setSelectedElements(prev =>
          prev.includes(elementId)
            ? prev.filter(id => id !== elementId)
            : [...prev, elementId]
        )
      }
    } else {
      // Sélection simple ou début de drag
      if (!selectedElements.includes(elementId)) {
        const groupId = getGroupForElement(elementId)
        if (groupId) {
          const group = elementGroups[groupId]
          setSelectedElements(group.elementIds)
        } else {
          setSelectedElements([elementId])
        }
      }

      // Démarrer le drag
      setIsDraggingElement(true)
      setDraggedElementId(elementId)

      const svgPoint = screenToSVG(event.clientX, event.clientY)
      setElementInitialOffset({
        x: svgPoint.x - element.position.x,
        y: svgPoint.y - element.position.y
      })
      setElementDragStart({ x: svgPoint.x, y: svgPoint.y })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentStep,
    handleCabinetClickForLink,
    handlePanelClickForPowerConnection,
    handlePanelClickForDataConnection,
    panelConnections,
    selectedElements,
    elementGroups,
    // getGroupForElement, // Stable function, not included to avoid dependency issues
    // screenToSVG, // Stable function, not included to avoid dependency issues
    setSelectedProcessorForConnection,
    setSelectedPort,
    setSelectedCabinetForConnection,
    setSelectedOutlet,
    setSelectedElements,
    setIsDraggingElement,
    setDraggedElementId,
    setElementInitialOffset,
    setElementDragStart
  ])

  // Handler pour le clic sur un élément du workspace (sélection + affichage propriétés)
  const handleElementClick = useCallback((element: WorkspaceElement) => {
    // Mode liaison armoire : filet de sécurité si le mousedown n'a pas traité le clic
    if (cabinetLinkMode && element.type === 'cabinet' && handleCabinetClickForLink(element.id, element)) {
      return
    }
    // Afficher les propriétés de l'élément cliqué
    if (element.type === 'processor' || element.type === 'panel' || element.type === 'cabinet') {
      setSelectedItemForProperties({ type: element.type, data: element.data })
    }

    // Sélectionner l'élément
    const groupId = getGroupForElement(element.id)
    if (groupId) {
      const group = elementGroups[groupId]
      setSelectedElements(group.elementIds)
    } else {
      setSelectedElements(prev =>
        prev.includes(element.id) ? prev : [element.id]
      )
    }

    // Sélection contextuelle selon l'étape
    if (element.type === 'panel' && currentStep === 'data') {
      const connection = panelConnections.find(conn => conn.panelId === element.id)
      if (connection) {
        setSelectedProcessorForConnection(connection.processorId)
        setSelectedPort(connection.port)
      }
    }
    if (element.type === 'cabinet' && currentStep === 'cabling') {
      setSelectedCabinetForConnection(element.id)
      setSelectedOutlet(null)
    }
    if (element.type === 'processor' && currentStep === 'data') {
      setSelectedProcessorForConnection(element.id)
    }

    // Onboarding: détecter la sélection d'un processeur ou d'une armoire
    if (onboarding.state.isActive) {
      if (element.type === 'processor') {
        onboarding.completeAction('processor-selected')
      } else if (element.type === 'cabinet') {
        onboarding.completeAction('cabinet-selected')
      }
    }
  }, [currentStep, panelConnections, elementGroups, selectedElements, onboarding, cabinetLinkMode, handleCabinetClickForLink])

  /**
   * Handler principal pour les événements mousedown
   * Optimisé avec useCallback et extraction de logiques complexes
   */
  const handleMouseDown = useCallback((event: React.MouseEvent) => {
    const target = event.target as Element
    const isOnElement = target.closest('.workspace-element')

    // CAS 1: Placement de panneaux (drag pour créer une grille)
    if (selectedPanelForPlacement && selectedProcessorForConnection && selectedPort !== null &&
      currentStep === 'data' && !isOnElement) {
      event.preventDefault()
      const svgPoint = screenToSVG(event.clientX, event.clientY)
      const snappedStartPoint = isSnappingEnabled
        ? calculateSnapGuides('panel-placement-start', svgPoint)
        : svgPoint

      setIsPlacingPanels(true)
      setIsDraggingPlacement(true)
      setPlacementStart({ x: snappedStartPoint.x, y: snappedStartPoint.y })
      return
    }

    // CAS 2: Clic sur un élément en mode sélection
    if (isOnElement && cursorMode === 'select') {
      const elementId = isOnElement.getAttribute('data-element-id')
      if (!elementId) return

      const element = workspaceElements.find(el => el.id === elementId)
      if (!element) return

      // Validation du clic sur les processeurs avec image (bounds checking)
      if (element.type === 'processor' && (element.data as LEDProcessor).image) {
        const svgPoint = screenToSVG(event.clientX, event.clientY)
        const imageBounds = getActualImageBounds(element)

        const isInsideImage =
          svgPoint.x >= imageBounds.x &&
          svgPoint.x <= imageBounds.x + imageBounds.width &&
          svgPoint.y >= imageBounds.y &&
          svgPoint.y <= imageBounds.y + imageBounds.height

        if (!isInsideImage) return
      }

      event.preventDefault()

      // Gérer la sélection et le drag
      handleElementSelection(elementId, element, event)
    }
    // CAS 3: Mode pan ou clic dans le vide
    else {
      // Pan avec clic milieu ou mode pan
      if ((cursorMode === 'pan' && event.button === 0) || event.button === 1) {
        event.preventDefault()
        setIsPanning(true)
        setPanStart({ x: event.clientX, y: event.clientY })
      }
      // Sélection rectangulaire
      else if (cursorMode === 'select' && !isOnElement && event.button === 0) {
        // Désélectionner les éléments existants si pas de Ctrl
        if (!event.ctrlKey) {
          setSelectedElements([])
          setSelectedProcessorForConnection(null)
          setSelectedCabinetForConnection(null)
          setCabinetLinkMode(false)

          if (currentStep === 'data' && selectedPort !== null) {
            setSelectedPort(null)
          }
        }

        // Commencer la sélection rectangulaire
        event.preventDefault()
        const svgPoint = screenToSVG(event.clientX, event.clientY)
        setIsRubberBandSelection(true)
        setRubberBandStart({ x: svgPoint.x, y: svgPoint.y })
        setRubberBandEnd({ x: svgPoint.x, y: svgPoint.y })
        setIsCtrlPressed(event.ctrlKey)
        setPreviewSelectedElements([])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedPanelForPlacement,
    selectedProcessorForConnection,
    selectedPort,
    currentStep,
    cursorMode,
    workspaceElements,
    isSnappingEnabled,
    // screenToSVG, // Stable function
    // calculateSnapGuides, // Stable function
    // getActualImageBounds, // Stable function
    handleElementSelection,
    setIsPlacingPanels,
    setIsDraggingPlacement,
    setPlacementStart,
    setIsPanning,
    setPanStart,
    setSelectedElements,
    setSelectedProcessorForConnection,
    setSelectedCabinetForConnection,
    setSelectedPort,
    setIsRubberBandSelection,
    setRubberBandStart,
    setRubberBandEnd,
    setIsCtrlPressed,
    setPreviewSelectedElements
  ])

  /**
   * Handler principal pour les événements mousemove
   * Optimisé avec useCallback pour éviter les re-créations inutiles
   * Gère: position souris, previews, sélection rectangulaire, redimensionnement, pan
   */
  const handleMouseMove = useCallback((event: React.MouseEvent) => {
    if (!workspaceRef.current) return

    const svgPoint = screenToSVG(event.clientX, event.clientY)

    // Toujours mettre à jour la position de la souris pour le cursor preview
    setMousePosition({ x: svgPoint.x, y: svgPoint.y })

    // CAS 1: Preview pendant le placement de panneaux en grille
    if (isDraggingPlacement && isPlacingPanels && selectedProcessorForConnection && selectedPort !== null) {
      // Appliquer le snapping sur la position de fin
      const snappedEndPosition = isSnappingEnabled
        ? calculateSnapGuides('panel-placement', svgPoint)
        : svgPoint

      // Calculer les prévisualisations de panneaux
      const previews = calculatePreviewPanels(placementStart, snappedEndPosition)
      setPreviewPanels(previews)

      // Calculer et afficher les câbles de preview
      const cables = calculatePreviewCables(previews, placementStart, snappedEndPosition)
      setPreviewCables(cables)
      return
    }

    // CAS 2: Mise à jour du rectangle de sélection
    if (isRubberBandSelection) {
      setRubberBandEnd({ x: svgPoint.x, y: svgPoint.y })
      // Calculer les éléments dans le rectangle pour la prévisualisation
      const elementsInPreview = getElementsInRectangle(rubberBandStart, { x: svgPoint.x, y: svgPoint.y })
      setPreviewSelectedElements(elementsInPreview)
      return
    }

    // CAS 3: Redimensionnement d'élément ou groupe
    if (isResizing && resizedElementId) {
      let newWidth: number, newHeight: number

      if (resizingGroupId && initialGroupBounds) {
        // Redimensionnement de groupe basé sur la bounding box
        newWidth = Math.max(svgPoint.x - initialGroupBounds.x, 60)
        newHeight = Math.max(svgPoint.y - initialGroupBounds.y, 60)
      } else {
        // Redimensionnement d'élément seul
        newWidth = Math.max(svgPoint.x - initialPosition.x, 40)
        newHeight = Math.max(svgPoint.y - initialPosition.y, 40)
      }

      handleElementResize(resizedElementId, { width: newWidth, height: newHeight })
      return
    }

    // CAS 5: Pan du workspace (le drag d'éléments est géré par @dnd-kit)
    if (isPanning) {
      const deltaX = event.clientX - panStart.x
      const deltaY = event.clientY - panStart.y

      // Convertir le déplacement écran en déplacement SVG
      const rect = workspaceRef.current.getBoundingClientRect()
      const svgDeltaX = (deltaX / rect.width) * viewBox.width
      const svgDeltaY = (deltaY / rect.height) * viewBox.height

      setViewBox(prev => {
        // Calculer la nouvelle position
        let newX = prev.x - svgDeltaX
        let newY = prev.y - svgDeltaY

        // Limites de la zone A3 avec marge de 20px
        const minX = -450
        const maxX = 450
        const minY = -320
        const maxY = 320

        // Contraindre le viewBox pour ne pas sortir trop de la zone A3
        // Le centre du viewBox doit rester dans les limites
        const centerX = newX + prev.width / 2
        const centerY = newY + prev.height / 2

        if (centerX < minX) newX = minX - prev.width / 2
        if (centerX > maxX) newX = maxX - prev.width / 2
        if (centerY < minY) newY = minY - prev.height / 2
        if (centerY > maxY) newY = maxY - prev.height / 2

        return {
          x: newX,
          y: newY,
          width: prev.width,
          height: prev.height
        }
      })

      setPanStart({ x: event.clientX, y: event.clientY })
      return
    }

    // CAS 5: Aucune action en cours - réinitialiser les guides de snap
    setSnapGuides({ x: [], y: [] })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    workspaceRef,
    // screenToSVG, // Stable function
    setMousePosition,
    isDraggingPlacement,
    isPlacingPanels,
    selectedProcessorForConnection,
    selectedPort,
    isSnappingEnabled,
    // calculateSnapGuides, // Stable function
    placementStart,
    // calculatePreviewPanels, // Stable function
    setPreviewPanels,
    // calculatePreviewCables, // Stable function
    setPreviewCables,
    isRubberBandSelection,
    setRubberBandEnd,
    // getElementsInRectangle, // Stable function
    rubberBandStart,
    setPreviewSelectedElements,
    isResizing,
    resizedElementId,
    resizingGroupId,
    initialGroupBounds,
    initialPosition,
    // handleElementResize, // Stable function
    isPanning,
    panStart,
    viewBox,
    setViewBox,
    setPanStart,
    setSnapGuides
  ])

  const handleMouseUp = () => {
    // Gérer la fin du redimensionnement
    if (isResizing) {
      const resizedElement = workspaceElements.find(el => el.id === resizedElementId)
      let description = 'Élément redimensionné'

      if (resizedElement) {
        const elementType = resizedElement.type === 'processor' ? 'Processeur' :
          resizedElement.type === 'panel' ? 'Panneau' :
            resizedElement.type === 'cabinet' ? 'Armoire' :
              resizedElement.type === 'image' ? 'Mire' : 'Élément'
        description = `${elementType} redimensionné`
      }

      setIsResizing(false)
      setResizedElementId(null)
      setSnapGuides({ x: [], y: [] })
      setResizingGroupId(null)
      setInitialGroupBounds(null)
      setInitialGroupElements([])

      // Sauvegarder immédiatement avec description
      saveToHistoryImmediate(description)
    }

    // Gérer la fin du placement de panneaux (uniquement avec processeur et port sélectionnés)
    if (isPlacingPanels && selectedPanelForPlacement && selectedProcessorForConnection && selectedPort !== null) {
      // Appliquer le snapping à la position finale si activé
      const currentPos = isSnappingEnabled
        ? calculateSnapGuides('panel-placement', mousePosition)
        : mousePosition

      const deltaX = currentPos.x - placementStart.x
      const deltaY = currentPos.y - placementStart.y

      // Si pas de déplacement significatif, placer un seul panneau avec snapping
      const moved = Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5

      if (!moved) {
        // Appliquer le snapping aussi au point de départ pour un placement unique
        const snappedStartPos = isSnappingEnabled
          ? calculateSnapGuides('panel-placement', placementStart)
          : placementStart
        placeSinglePanel(snappedStartPos.x, snappedStartPos.y)
      } else {
        placeMultiplePanels(placementStart, currentPos)
      }

      setIsPlacingPanels(false)
      setIsDraggingPlacement(false)
      setPreviewPanels([])
      setPreviewCables([])
    }

    // Finaliser la sélection rectangulaire
    if (isRubberBandSelection) {
      const elementsInRectangle = getElementsInRectangle(rubberBandStart, rubberBandEnd)

      if (elementsInRectangle.length > 0) {
        // Si Ctrl était maintenu au début de la sélection, ajouter à la sélection existante, sinon remplacer
        setSelectedElements(prev => {
          if (isCtrlPressed) {
            // Ajouter les nouveaux éléments sans doublons
            const newElements = elementsInRectangle.filter(id => !prev.includes(id))
            return [...prev, ...newElements]
          } else {
            return elementsInRectangle
          }
        })
      }

      setIsRubberBandSelection(false)
      setRubberBandStart({ x: 0, y: 0 })
      setRubberBandEnd({ x: 0, y: 0 })
      setIsCtrlPressed(false)
      setPreviewSelectedElements([])
    }

    setIsPanning(false)
    setIsDraggingElement(false)
    setDraggedElementId(null)
  }

  // Fonctions de copier-coller
  const copySelectedElements = () => {
    if (selectedElements.length === 0) return

    const elementsToCopy = workspaceElements.filter(e => selectedElements.includes(e.id))

    // Vérifier si on copie uniquement des mires (images)
    const onlyMires = elementsToCopy.every(e => e.type === 'image' && e.data?.mireConfig)

    // Si on copie uniquement des mires, ne pas copier les connexions
    if (onlyMires) {
      setClipboard({
        elements: elementsToCopy,
        cables: [],
        panelConnections: [],
        powerConnections: []
      })
      return
    }

    // Copier aussi les câbles qui connectent les éléments sélectionnés
    const cablesToCopy = cableRoutes.filter(cable =>
      selectedElements.includes(cable.from) && selectedElements.includes(cable.to)
    )

    // Copier les connexions des panneaux sélectionnés
    const panelConnectionsToCopy = panelConnections.filter(conn =>
      selectedElements.includes(conn.panelId) || selectedElements.includes(conn.processorId)
    )

    // Copier les connexions d'alimentation des panneaux sélectionnés
    const powerConnectionsToCopy = powerConnections.filter(conn =>
      selectedElements.includes(conn.panelId) || selectedElements.includes(conn.cabinetId)
    )

    setClipboard({
      elements: elementsToCopy,
      cables: cablesToCopy,
      panelConnections: panelConnectionsToCopy,
      powerConnections: powerConnectionsToCopy
    })

  }

  const pasteElements = () => {
    if (!clipboard || clipboard.elements.length === 0) return

    // Décalage pour que les éléments collés ne soient pas exactement au même endroit
    const offset = 50

    // Créer de nouveaux IDs pour les éléments
    const oldToNewIdMap = new Map<string, string>()

    const newElements = clipboard.elements.map(elem => {
      const newId = `${elem.type}-${Date.now()}-${Math.random()}`
      oldToNewIdMap.set(elem.id, newId)

      const newData = { ...elem.data }
      // Si c'est une mire, désactiver countPowerConsumption sur la copie
      if (elem.type === 'image' && newData?.bubbleConfig) {
        newData.bubbleConfig = {
          ...newData.bubbleConfig,
          countPowerConsumption: false
        }
      }

      return {
        ...elem,
        id: newId,
        data: newData,
        position: {
          x: elem.position.x + offset,
          y: elem.position.y + offset
        }
      }
    })

    // Créer de nouveaux câbles avec les nouveaux IDs
    const newCables = clipboard.cables.map(cable => ({
      ...cable,
      id: `cable-${Date.now()}-${Math.random()}`,
      from: oldToNewIdMap.get(cable.from) || cable.from,
      to: oldToNewIdMap.get(cable.to) || cable.to,
      points: cable.points.map(p => ({
        x: p.x + offset,
        y: p.y + offset
      }))
    }))

    // Créer de nouvelles connexions de panneaux avec les nouveaux IDs
    const newPanelConnections = clipboard.panelConnections
      .filter(conn =>
        oldToNewIdMap.has(conn.panelId) && oldToNewIdMap.has(conn.processorId)
      )
      .map(conn => ({
        ...conn,
        panelId: oldToNewIdMap.get(conn.panelId) || conn.panelId,
        processorId: oldToNewIdMap.get(conn.processorId) || conn.processorId
      }))

    // Créer de nouvelles connexions d'alimentation avec les nouveaux IDs
    const newPowerConnections = clipboard.powerConnections
      .filter(conn =>
        oldToNewIdMap.has(conn.panelId) && oldToNewIdMap.has(conn.cabinetId)
      )
      .map(conn => ({
        ...conn,
        panelId: oldToNewIdMap.get(conn.panelId) || conn.panelId,
        cabinetId: oldToNewIdMap.get(conn.cabinetId) || conn.cabinetId
      }))

    // Ajouter les nouveaux éléments à la page courante
    setWorkspaceElements(prev => [...prev, ...newElements])
    setCableRoutes(prev => [...prev, ...newCables])
    setPanelConnections(prev => [...prev, ...newPanelConnections])
    setPowerConnections(prev => [...prev, ...newPowerConnections])

    // Sélectionner les éléments collés
    setSelectedElements(newElements.map(e => e.id))

    // Générer une description pour l'historique
    const description = newElements.length === 1
      ? `Élément collé`
      : `${newElements.length} éléments collés`

    // Sauvegarder immédiatement avec description
    saveToHistoryImmediate(description)
  }

  // Fonction pour centrer la vue sur la zone A3 avec le viewBox
  const centerOnA3 = (newZoomPercent: number, source = 'unknown') => {
    if (!workspaceRef.current) return

    // Le centre de la zone A3 est (0, 0) dans les coordonnées SVG
    const targetX = 0
    const targetY = 0

    // Nouveau système : 873% = 100%
    const newScale = newZoomPercent / 100 // Convertir pourcentage en scale

    // Calculer les nouvelles dimensions du viewBox basées sur le nouveau système
    const svgRect = workspaceRef.current.getBoundingClientRect()
    const aspectRatio = svgRect.width / svgRect.height

    // Dans le nouveau système : 100% = référence de 873% (8.73x)
    let viewBoxWidth = (10000 / referenceZoom) / newScale
    let viewBoxHeight = viewBoxWidth / aspectRatio

    // Ajuster pour garder le ratio
    if (viewBoxHeight > (10000 / referenceZoom) / newScale) {
      viewBoxHeight = (10000 / referenceZoom) / newScale
      viewBoxWidth = viewBoxHeight * aspectRatio
    }

    // Centrer le viewBox sur le point (0, 0) avec contraintes
    let newX = targetX - viewBoxWidth / 2
    let newY = targetY - viewBoxHeight / 2

    // Limites de la zone A3 avec marge de 20px
    const minX = -450
    const maxX = 450
    const minY = -320
    const maxY = 320

    // Contraindre le viewBox pour ne pas sortir trop de la zone A3
    const centerX = newX + viewBoxWidth / 2
    const centerY = newY + viewBoxHeight / 2

    if (centerX < minX) newX = minX - viewBoxWidth / 2
    if (centerX > maxX) newX = maxX - viewBoxWidth / 2
    if (centerY < minY) newY = minY - viewBoxHeight / 2
    if (centerY > maxY) newY = maxY - viewBoxHeight / 2

    const newViewBox = {
      x: newX,
      y: newY,
      width: viewBoxWidth,
      height: viewBoxHeight
    }

    setViewBox(newViewBox)
  }

  // Centrer sur la zone A3 au démarrage
  useEffect(() => {
    if (mounted && !isViewBoxInitialized) {
      // Attendre que le layout soit complètement fini, y compris le bandeau du bas
      const initializeViewBox = () => {
        if (workspaceRef.current) {
          const rect = workspaceRef.current.getBoundingClientRect()
          if (rect.width > 0 && rect.height > 0) {
            // Vérifier que les dimensions sont stables (pas en cours de changement)
            setTimeout(() => {
              const newRect = workspaceRef.current?.getBoundingClientRect()
              if (newRect && Math.abs(newRect.height - rect.height) < 5) {
                // Les dimensions sont stables, on peut initialiser
                centerOnA3(100, 'initialization') // Centrer sur la zone A3 au zoom 100% du nouveau système
                setIsViewBoxInitialized(true) // Marquer le viewBox comme initialisé
              } else {
                // Les dimensions changent encore, réessayer
                setTimeout(initializeViewBox, 100)
              }
            }, 50)
          } else {
            // Si les dimensions ne sont pas encore prêtes, réessayer
            setTimeout(initializeViewBox, 50)
          }
        } else {
          // Si la ref n'est pas encore prête, réessayer
          setTimeout(initializeViewBox, 50)
        }
      }

      // Attendre que tous les éléments UI soient rendus (bandeau inclus)
      requestAnimationFrame(() => {
        setTimeout(initializeViewBox, 300) // Délai plus long pour le bandeau
      })
    }
  }, [mounted, isViewBoxInitialized])

  // Mettre à jour l'affichage des panneaux selon l'étape actuelle
  useEffect(() => {
    if (currentStep === 'data') {
      setShowProcessors(true)
      setShowPanels(true)
      setShowCabinets(false)
      setShowCabling(false) // Data se concentre sur les connexions données
      setSelectedCableType('data')
    } else if (currentStep === 'cabling') {
      setShowProcessors(false)
      setShowPanels(false)
      setShowCabinets(true) // Afficher les armoires/coffrets pour le Power
      setShowCabling(true) // Power affiche le câblage électrique
      setSelectedCableType('power')
    } else if (currentStep === 'export') {
      // En mode export, masquer les panneaux latéraux (sauf cartouche) et afficher tous les câbles
      setShowProcessors(false)
      setShowPanels(false)
      setShowCabinets(false)
      setShowCabling(true)
      setSelectedCableType(null)
      // Réinitialiser les sélections qui pourraient filtrer les câbles
      setSelectedPort(null)
      setSelectedOutlet(null)
      setSelectedProcessorForConnection(null)
      setSelectedCabinetForConnection(null)
      // Note: exportViewMode est déjà défini par l'autre fonction handleStepChange
    }

    // Réinitialiser les options d'affichage des détails selon l'étape
    setShowProcessorSpecs(currentStep === 'data' ? false : showProcessorSpecs)
    setShowPanelSpecs((currentStep === 'data' || currentStep === 'cabling') ? false : showPanelSpecs)
    setShowCabinetSpecs(currentStep === 'cabling' ? false : showCabinetSpecs)

    // Recalculer le viewBox après changement d'étape car le layout peut changer (bandeau)
    if (isViewBoxInitialized && workspaceRef.current) {
      setTimeout(() => {
        if (workspaceRef.current) {
          const rect = workspaceRef.current.getBoundingClientRect()
          if (rect.width > 0 && rect.height > 0) {
            // Zoom à 81% pour les étapes avec bandeau du bas (panels, cabling)
            centerOnA3((currentStep === 'data' || currentStep === 'cabling') ? 81 : 100, `step-change-${currentStep}`)
          }
        }
      }, 100) // Délai pour que le bandeau s'affiche/se cache
    }
  }, [currentStep, isViewBoxInitialized])

  // Mettre à jour les dimensions des processeurs et panneaux existants
  useEffect(() => {
    setWorkspaceElements(prevElements =>
      prevElements.map(element => {
        if (element.type === 'processor' && element.data.imageDimensions) {
          return {
            ...element,
            size: {
              width: element.data.imageDimensions.width,
              height: element.data.imageDimensions.height
            }
          }
        } else if (element.type === 'panel' && element.data.dimensions) {
          return {
            ...element,
            size: {
              width: element.data.dimensions.width,
              height: element.data.dimensions.height
            }
          }
        }
        return element
      })
    )
  }, []) // Exécuté une seule fois au montage
  // ✅ OPTIMISATION Phase 2 : useCallback pour éviter re-création
  const handleWheel = useCallback((event: React.WheelEvent) => {
    if (event.ctrlKey && workspaceRef.current) {
      event.preventDefault()

      // Zoom centré sur la position de la souris
      const rect = workspaceRef.current.getBoundingClientRect()
      const mouseX = event.clientX - rect.left
      const mouseY = event.clientY - rect.top

      // Convertir les coordonnées souris en coordonnées SVG
      const svgWidth = rect.width
      const svgHeight = rect.height
      const svgX = (mouseX / svgWidth) * viewBox.width + viewBox.x
      const svgY = (mouseY / svgHeight) * viewBox.height + viewBox.y

      // Nouveau système : 873% = 100%
      const referenceZoom = 8.73 // 873% = 8.73x
      const currentZoomPercent = (10000 / viewBox.width) / referenceZoom * 100
      const zoomFactor = event.deltaY > 0 ? 0.9 : 1.1
      const newZoomPercent = Math.max(50, Math.min(2000, currentZoomPercent * zoomFactor))

      // Calculer le nouveau viewBox
      const aspectRatio = svgWidth / svgHeight
      const newScale = newZoomPercent / 100

      let viewBoxWidth = (10000 / referenceZoom) / newScale
      let viewBoxHeight = viewBoxWidth / aspectRatio

      // Ajuster pour garder le ratio
      if (viewBoxHeight > (10000 / referenceZoom) / newScale) {
        viewBoxHeight = (10000 / referenceZoom) / newScale
        viewBoxWidth = viewBoxHeight * aspectRatio
      }

      // Calculer la nouvelle position
      let newX = svgX - viewBoxWidth / 2
      let newY = svgY - viewBoxHeight / 2

      // Limites de la zone A3 avec marge de 20px
      const minX = -450
      const maxX = 450
      const minY = -320
      const maxY = 320

      // Contraindre le viewBox pour ne pas sortir trop de la zone A3
      const centerX = newX + viewBoxWidth / 2
      const centerY = newY + viewBoxHeight / 2

      if (centerX < minX) newX = minX - viewBoxWidth / 2
      if (centerX > maxX) newX = maxX - viewBoxWidth / 2
      if (centerY < minY) newY = minY - viewBoxHeight / 2
      if (centerY > maxY) newY = maxY - viewBoxHeight / 2

      const newViewBox = {
        x: newX,
        y: newY,
        width: viewBoxWidth,
        height: viewBoxHeight
      }

      setViewBox(newViewBox)
    }
  }, [viewBox])

  const handleElementDrag = useCallback((elementId: string, newPosition: { x: number, y: number }) => {
    const draggedElement = workspaceElements.find(el => el.id === elementId)
    if (!draggedElement) return

    // Calculer le déplacement relatif
    const deltaX = newPosition.x - draggedElement.position.x
    const deltaY = newPosition.y - draggedElement.position.y

    // Marquer qu'un mouvement a eu lieu
    if (Math.abs(deltaX) > 1 || Math.abs(deltaY) > 1) {
      setHasMoved(true)
    }

    // Obtenir les éléments à déplacer (sélectionnés ou juste l'élément principal)
    const elementsToMove = selectedElements.length > 0 && selectedElements.includes(elementId)
      ? selectedElements
      : [elementId]

    setWorkspaceElements(prev => {
      const movedElementsData: { id: string, position: { x: number, y: number }, size: { width: number, height: number }, type: string }[] = []

      const updatedElements = prev.map(el => {
        if (elementsToMove.includes(el.id)) {
          let newX = el.position.x + deltaX
          let newY = el.position.y + deltaY

          // Limites de la zone A3 avec marge de 20px
          const minX = -450
          const maxX = 450
          const minY = -320
          const maxY = 320

          // Contraindre la position pour que l'élément reste dans la zone A3
          // Prendre en compte la taille de l'élément
          const elementWidth = el.size?.width || 0
          const elementHeight = el.size?.height || 0

          // Contraindre en X (le bord gauche et droit de l'élément)
          if (newX < minX) newX = minX
          if (newX + elementWidth > maxX) newX = maxX - elementWidth

          // Contraindre en Y (le bord haut et bas de l'élément)
          if (newY < minY) newY = minY
          if (newY + elementHeight > maxY) newY = maxY - elementHeight

          const newPos = { x: newX, y: newY }

          // Collecter les données des éléments déplacés
          movedElementsData.push({
            id: el.id,
            position: newPos,
            size: el.size,
            type: el.type
          })

          return { ...el, position: newPos }
        }
        return el
      })

      // Stocker les données des éléments déplacés dans le ref
      movedElementsRef.current = movedElementsData

      // Planifier la mise à jour des câbles si pas déjà planifiée
      if (movedElementsData.length > 0 && !cableUpdateScheduled.current) {
        cableUpdateScheduled.current = true
        // Utiliser RAF pour batching les mises à jour de câbles
        requestAnimationFrame(() => {
          if (movedElementsRef.current.length > 0) {
            updateCablePositions(movedElementsRef.current)
            movedElementsRef.current = []
          }
          cableUpdateScheduled.current = false
        })
      }

      return updatedElements
    })
  }, [workspaceElements, selectedElements])

  // Configuration @dnd-kit pour le déplacement fluide des éléments
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Minimum 5px de mouvement pour activer le drag (plus réactif)
      },
    })
  )

  // Configuration optimisée pour le measuring de @dnd-kit
  const measuring = useMemo(() => ({
    droppable: {
      strategy: MeasuringStrategy.Always,
    },
  }), [])

  // Hook pour gérer le déplacement fluide avec @dnd-kit
  const dndWorkspace = useDraggableWorkspace({
    workspaceElements,
    selectedElements,
    elementGroups,
    readOnly,
    onElementDrag: handleElementDrag,
    onDragStart: (elementId) => {
      setIsDraggingElement(true)
      setDraggedElementId(elementId)
      setHasMoved(false)
    },
    onDragEnd: (updates) => {
      // Générer une description pour l'historique
      let description = 'Élément déplacé'
      if (updates.length === 1) {
        const movedElement = workspaceElements.find(el => el.id === updates[0].elementId)
        if (movedElement) {
          const elementType = movedElement.type === 'processor' ? 'Processeur' :
            movedElement.type === 'panel' ? 'Panneau' :
              movedElement.type === 'cabinet' ? 'Armoire' :
                movedElement.type === 'image' ? 'Mire' : 'Élément'
          description = `${elementType} déplacé`
        }
      } else if (updates.length > 1) {
        description = `${updates.length} éléments déplacés`
      }

      // Mettre à jour toutes les positions directement sans recalculer le delta
      setWorkspaceElements(prev => {
        const updatedElements = prev.map(el => {
          const update = updates.find(u => u.elementId === el.id)
          if (update) {
            return { ...el, position: update.finalPosition }
          }
          return el
        })

        // Collecter les données des éléments déplacés pour mettre à jour les câbles
        const movedElementsData = updates.map(update => {
          const element = updatedElements.find(el => el.id === update.elementId)
          return element ? {
            id: element.id,
            position: update.finalPosition,
            size: element.size,
            type: element.type
          } : null
        }).filter((el): el is { id: string, position: { x: number, y: number }, size: { width: number, height: number }, type: string } => el !== null)

        // Mettre à jour les câbles immédiatement
        if (movedElementsData.length > 0) {
          requestAnimationFrame(() => {
            updateCablePositions(movedElementsData)
          })
        }

        return updatedElements
      })

      setIsDraggingElement(false)
      setDraggedElementId(null)
      setSnapGuides({ x: [], y: [] })

      // Sauvegarder immédiatement avec description
      saveToHistoryImmediate(description)
    },
    screenDeltaToSVGDelta,
    snapPosition: calculateSnapGuides
  })

  const handleElementResize = (elementId: string, newSize: { width: number, height: number }) => {
    const resizedElement = workspaceElements.find(el => el.id === elementId)
    if (!resizedElement) return

    // Vérifier si on redimensionne un groupe
    if (resizingGroupId && initialGroupBounds && initialGroupElements.length > 0) {
      // Redimensionnement de groupe avec proportions préservées
      const scaleX = newSize.width / initialGroupBounds.width
      const scaleY = newSize.height / initialGroupBounds.height

      // Utiliser un scale uniforme pour préserver les proportions du groupe
      const uniformScale = Math.min(scaleX, scaleY)

      setWorkspaceElements(prev => {
        const updatedElements = prev.map(el => {
          const initialEl = initialGroupElements.find(initEl => initEl.id === el.id)
          if (!initialEl) return el

          // Calculer la nouvelle position et taille relative à la bounding box du groupe
          const relativeX = initialEl.position.x - initialGroupBounds.x
          const relativeY = initialEl.position.y - initialGroupBounds.y

          const newX = initialGroupBounds.x + relativeX * uniformScale
          const newY = initialGroupBounds.y + relativeY * uniformScale
          const newWidth = initialEl.size.width * uniformScale
          const newHeight = initialEl.size.height * uniformScale

          return {
            ...el,
            position: { x: newX, y: newY },
            size: { width: newWidth, height: newHeight }
          }
        })

        // Mettre à jour les câbles pour tous les éléments du groupe
        const groupElementIds = initialGroupElements.map(el => el.id)
        const movedElements = updatedElements.filter(el => groupElementIds.includes(el.id))
        movedElements.forEach(el => {
          updateCablePositions([{
            id: el.id,
            position: el.position,
            size: el.size,
            type: el.type
          }])
        })

        return updatedElements
      })
    } else {
      // Redimensionnement d'un seul élément
      const MIN_SIZE = {
        processor: 40,
        panel: 30,
        cabinet: 40,
        hub: 30
      }

      const minSize = MIN_SIZE[resizedElement.type as keyof typeof MIN_SIZE] || 60
      const initialRatio = initialSize.width / initialSize.height

      let finalWidth = Math.max(newSize.width, minSize)
      let finalHeight = finalWidth / initialRatio

      if (finalHeight < minSize) {
        finalHeight = minSize
        finalWidth = finalHeight * initialRatio
      }

      setWorkspaceElements(prev => {
        const updatedElements = prev.map(el => {
          if (el.id === elementId) {
            return {
              ...el,
              size: {
                width: finalWidth,
                height: finalHeight
              }
            }
          }
          return el
        })

        const resizedElementData = updatedElements.find(el => el.id === elementId)
        if (resizedElementData) {
          updateCablePositions([{
            id: resizedElementData.id,
            position: resizedElementData.position,
            size: resizedElementData.size,
            type: resizedElementData.type
          }])
        }

        return updatedElements
      })
    }
  }
  // Fonctions de gestion des groupes (extracted to hooks/useGroupManagement.ts)

  // Vérifier si on doit afficher le handle de redimensionnement - OPTIMISÉ avec useCallback
  const shouldShowResizeHandle = useCallback((elementId: string): boolean => {
    // Vérifier si l'élément est sélectionné
    if (!selectedElements.includes(elementId)) return false

    // Vérifier si l'élément fait partie d'un groupe
    const groupId = getGroupForElement(elementId)
    if (!groupId) return true // Pas de groupe, afficher la poignée

    const group = elementGroups[groupId]

    // Si le groupe est permanent, ne jamais afficher les handles individuels
    if (group?.permanent) return false

    // Vérifier si plusieurs éléments du groupe sont sélectionnés
    const selectedGroupElements = selectedElements.filter(id => group.elementIds.includes(id))
    if (selectedGroupElements.length <= 1) return true // Un seul élément du groupe sélectionné

    // Trouver l'élément le plus en bas à droite du groupe
    const groupElements = workspaceElements.filter(el => selectedGroupElements.includes(el.id))
    const bottomRightElement = groupElements.reduce((bottomRight, current) => {
      const bottomRightCorner = bottomRight.position.y + bottomRight.size.height + bottomRight.position.x + bottomRight.size.width
      const currentCorner = current.position.y + current.size.height + current.position.x + current.size.width
      return currentCorner > bottomRightCorner ? current : bottomRight
    })

    return bottomRightElement.id === elementId
  }, [selectedElements, elementGroups, workspaceElements, getGroupForElement])

  if (!mounted) {
    return null // Prevent hydration issues
  }

  // ========== ReadOnly mode: loading/error states ==========
  // ⚠️ Évalué AVANT le check isMobile : les liens partagés/QR doivent
  // fonctionner sur téléphone (visionneuse dédiée, pas l'éditeur).
  if (readOnly) {
    if (readOnlyLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
            <p className="text-muted-foreground">{t('synoptic.share.loading')}</p>
          </div>
        </div>
      )
    }
    if (!sharedProject) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="flex flex-col items-center gap-4 text-center max-w-md">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
              <X className="h-8 w-8 text-destructive" />
            </div>
            <h2 className="text-xl font-semibold">{t('synoptic.share.projectNotFound')}</h2>
            <p className="text-muted-foreground">{t('synoptic.share.projectNotFoundDesc')}</p>
            <SmartLink href="/">
              <Button variant="outline" className="gap-2">
                <Home className="h-4 w-4" />
                VisualGrid
              </Button>
            </SmartLink>
          </div>
        </div>
      )
    }
    // Mobile + consultation → visionneuse dédiée (zoom/scroll, pas d'édition)
    if (isMobile) {
      return (
        <MobileSynopticViewer
          projectName={sharedProject.name || projectName}
          authorName={sharedProject.author_name}
          dataPages={dataPages}
          powerPages={powerPages}
          svgHostRef={workspaceRef}
          createExportSvgForDataPage={createExportSvgForDataPage}
          createExportSvgForPowerPage={createExportSvgForPowerPage}
        />
      )
    }
    // ReadOnly mode: skip all auth/demo/projectHub checks, go directly to workspace render
  }

  if (isMobile) {
    return (
      <div className="min-h-screen bg-background text-foreground p-4 flex items-center justify-center">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <Grid3X3 className="h-16 w-16 mx-auto mb-4 text-primary" />
            <CardTitle>{t('synoptic.smallScreenTitle')}</CardTitle>
            <CardDescription>
              {t('synoptic.smallScreenDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleGoBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('synoptic.backToHome')}
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // ========== Animation de chargement pour projet ouvert via URL ==========
  if (isLoadingFromUrl) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center">
        <div className="flex flex-col items-center gap-6 animate-in fade-in zoom-in duration-300">
          {/* Cloud animation */}
          <div className="relative">
            <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl animate-pulse" />
            <div className="relative bg-gradient-to-br from-primary/10 to-primary/5 p-8 rounded-full border border-primary/20">
              <Cloud className="h-16 w-16 text-primary animate-bounce" />
            </div>
          </div>
          
          {/* Loading text */}
          <div className="text-center space-y-2">
            <h2 className="text-xl font-semibold text-foreground">
              {t('projectHub.openingFromCloud') || 'Ouverture depuis VisualCloud...'}
            </h2>
            <p className="text-muted-foreground">
              {loadingProjectName || 'Projet'}
            </p>
          </div>
          
          {/* Progress bar */}
          <div className="w-64 h-1.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full animate-progress" 
                 style={{ 
                   animation: 'progress 0.8s ease-out forwards',
                 }} 
            />
          </div>
        </div>
        <style jsx>{`
          @keyframes progress {
            from { width: 0%; }
            to { width: 100%; }
          }
          .animate-progress {
            animation: progress 0.8s ease-out forwards;
          }
        `}</style>
      </div>
    )
  }

  // Show loading or demo mode if not premium (only when Supabase is configured)
  if (!readOnly && supabaseConfigured) {
    if (authLoading) {
      // Show loading state while checking auth
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      );
    }

    // Non-authenticated or non-premium users: show demo mode
    if (!user || !isPro) {
      // Demo ended overlay
      if (demoMode.state.hasEnded) {
        return (
          <DemoEndedOverlay
            canRestartDemo={demoMode.state.canRestartDemo}
            cooldownRemaining={demoMode.state.cooldownRemaining}
            onRestartDemo={() => { onboarding.resetOnboarding(); demoMode.startDemo(resetDemoWorkspace) }}
          />
        );
      }

      // Demo not started: show welcome/onboarding
      if (!demoMode.state.isDemo) {
        return (
          <DemoWelcome
            onStartDemo={() => { onboarding.resetOnboarding(); demoMode.startDemo(resetDemoWorkspace) }}
            canRestartDemo={demoMode.state.canRestartDemo}
            cooldownRemaining={demoMode.state.cooldownRemaining}
            formatTime={demoMode.formatTime}
          />
        );
      }
      // If demo is active, continue to render the synoptic (with limitations applied below)
    }
  }

  // Afficher le hub de projets si aucun projet n'est sélectionné (only for Pro users)
  if (!readOnly && isPro && !currentProject) {
    return <ProjectHub onProjectOpen={(projectId) => loadProject(projectId)} />
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Overlay de progression d'export */}
      {isExporting && (
        <div className="fixed inset-0 z-[9999] bg-background/80 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-card border rounded-xl shadow-2xl p-8 flex flex-col items-center space-y-4 min-w-[300px]">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary border-t-transparent" />
            <p className="text-lg font-semibold">{exportProgress || 'Export en cours...'}</p>
            <p className="text-sm text-muted-foreground">{t('synoptic.pleaseWait')}</p>
          </div>
        </div>
      )}

      {/* Overlay de succès d'export */}
      {showExportSuccess && (
        <div className="fixed inset-0 z-[9999] bg-background/80 backdrop-blur-sm flex items-center justify-center animate-in fade-in duration-300">
          <div className="bg-card border rounded-xl shadow-2xl p-8 flex flex-col items-center space-y-4 min-w-[300px] animate-in zoom-in-95 duration-300">
            <div className="relative">
              <div className="absolute inset-0 bg-green-500/20 rounded-full blur-xl animate-pulse" />
              <CheckCircle2 className="h-16 w-16 text-green-500 relative animate-in zoom-in duration-500" />
            </div>
            <p className="text-lg font-semibold text-green-600 dark:text-green-400">{t('synoptic.toast.exportSuccess')}</p>
          </div>
        </div>
      )}
      {/* Demo Timer supprimé - pas de limite de temps */}

      {/* Demo Limit Reached Dialog */}
      <DemoLimitReached
        open={demoLimitDialog.open}
        onOpenChange={(open) => setDemoLimitDialog(prev => ({ ...prev, open }))}
        limitType={demoLimitDialog.limitType}
        currentCount={demoLimitDialog.currentCount}
        maxCount={demoLimitDialog.maxCount}
      />

      {/* Onboarding Overlay for demo users */}
      {demoMode.state.isDemo && !isPro && (
        <OnboardingOverlay
          isActive={onboarding.state.isActive}
          currentStep={onboarding.currentStep}
          currentIndex={onboarding.state.currentStepIndex}
          totalSteps={onboarding.steps.length}
          onNext={onboarding.nextStep}
          onPrev={onboarding.prevStep}
          onSkip={onboarding.skipOnboarding}
          onStepChange={handleStepChange}
          currentWorkflowStep={currentStep}
          cameFromBack={onboarding.state.cameFromBack}
          isDragging={onboarding.isDragging}
          draggingType={onboarding.draggingType}
        />
      )}

      {/* Floating Onboarding Start Button for demo users */}
      {demoMode.state.isDemo && !isPro && !onboarding.state.isActive && (
        <div className="fixed bottom-4 left-4 z-[9990]">
          <OnboardingStartButton
            onStart={onboarding.startOnboarding}
            hasCompleted={onboarding.hasCompletedOnboarding}
            variant="compact"
            className="shadow-lg"
          />
        </div>
      )}

      {/* Header (extracted to components/SynoptiqueHeader.tsx) */}
      <SynoptiqueHeader
        readOnly={readOnly}
        projectName={projectName}
        setProjectName={setProjectName}
        currentProject={currentProject}
        isPro={isPro}
        rawIsPro={rawIsPro}
        tempProjectName={tempProjectName}
        setTempProjectName={setTempProjectName}
        updateProjectName={updateProjectName}
        currentStep={currentStep}
        handleStepChange={handleStepChange}
        showShareDialog={showShareDialog}
        setShowShareDialog={setShowShareDialog}
        saveProject={saveProject}
        dataPages={dataPages}
        powerPages={powerPages}
        collectSynopticData={collectSynopticData}
        isSavingDebug={isSavingDebug}
        lastSaveTime={lastSaveTime}
        saveStartTime={saveStartTime}
        timeSinceLastSave={timeSinceLastSave}
        isOnline={isOnline}
        handleSaveSynoptic={handleSaveSynoptic}
        handleSaveAsSynoptic={handleSaveAsSynoptic}
        handleImportSynopticFile={handleImportSynopticFile}
        handleExportSynopticFile={handleExportSynopticFile}
        setShowProjectPicker={setShowProjectPicker}
        supabaseConfigured={supabaseConfigured}
        demoMode={demoMode}
        getSaveButtonColors={getSaveButtonColors}
      />
      

      {/* Layout principal */}
      <div className={`flex overflow-hidden ${currentStep === 'testPattern' ? 'h-[calc(100vh-65px)]' :
        currentStep === 'data' && isBottomPanelOpen
          ? 'h-[calc(100vh-65px-160px)]'
          : currentStep === 'data' && !isBottomPanelOpen
            ? 'h-[calc(100vh-65px-32px)]'
            : currentStep === 'cabling' && isBottomPanelOpen
              ? 'h-[calc(100vh-65px-160px)]'
              : currentStep === 'cabling' && !isBottomPanelOpen
                ? 'h-[calc(100vh-65px-32px)]'
                : 'h-[calc(100vh-65px)]'
        }`}>

        {/* NOUVEAU: Affichage conditionnel pour l'étape testPattern */}
        {currentStep === 'testPattern' ? (
          <div className="flex-1 flex flex-col">
            {/* iFrame du générateur de mires */}
            <div className="flex-1 relative bg-muted/20">
              <iframe
                src={typeof window !== 'undefined' ? `${window.location.origin}/grid/?embed=synoptic` : '/grid/?embed=synoptic'}
                className="w-full h-full border-0"
                title={t('title.mireGenerator')}
                allow="fullscreen"
              />
            </div>

            {/* Pied de page avec gestion des mires */}
            <div className="bg-card border-t shadow-lg">
              <div className="max-w-7xl mx-auto p-4">
                {/* ✅ Bannière d'aide si aucune mire n'est détectée (ni en attente, ni dans la liste) */}
                {!hasMireGenerated && !pendingLEDConfig && savedMires.length === 0 && (
                  <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="flex items-start gap-3">
                      <div className="bg-blue-500 text-white p-2 rounded-lg flex-shrink-0">
                        <Grid3X3 className="h-5 w-5" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
                          {t('synoptic.howToCreateMire')}
                        </h3>
                        <ol className="text-sm text-blue-800 dark:text-blue-200 space-y-1 list-decimal list-inside">
                          <li dangerouslySetInnerHTML={{ __html: t('synoptic.howToMireStep1') }} />
                          <li dangerouslySetInnerHTML={{ __html: t('synoptic.howToMireStep2') }} />
                          <li>{t('synoptic.howToMireStep3')}</li>
                          <li dangerouslySetInnerHTML={{ __html: t('synoptic.howToMireStep4') }} />
                        </ol>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between gap-4">
                  {/* Section gauche : Ajouter une mire */}
                  <div className="flex items-center gap-3" data-onboarding="add-to-list-section">
                    {hasMireGenerated && pendingLEDConfig && (
                      <>
                        <Input
                          placeholder={t("placeholder.patternNameOptional")}
                          value={currentMireName}
                          onChange={(e) => setCurrentMireName(e.target.value)}
                          className="w-48"
                          data-onboarding="mire-name-input"
                        />
                        {editingMireIndex === null ? (
                          <Button
                            variant="outline"
                            onClick={() => handleSaveMireToList('append')}
                            className="gap-2"
                            data-onboarding="add-to-list-btn"
                          >
                            <Plus className="h-4 w-4" />
                            {t('synoptic.addToList')}
                          </Button>
                        ) : (
                          <>
                            <Button
                              onClick={() => handleSaveMireToList('overwrite')}
                              className="gap-2"
                              title={t('synoptic.updateMireTitle')}
                            >
                              <Save className="h-4 w-4" />
                              {t('synoptic.updateMire')}
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => handleSaveMireToList('append')}
                              className="gap-2"
                              title={t('synoptic.saveAsNewMireTitle')}
                            >
                              <Copy className="h-4 w-4" />
                              {t('synoptic.saveAsNewMire')}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setEditingMireIndex(null)}
                              title={t('synoptic.cancelEditMire')}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        )}

                        {savedMires.length > 0 && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span>{t('synoptic.mireCount').replace('{count}', String(savedMires.length)).replace(/\{plural\}/g, savedMires.length > 1 ? 's' : '')}</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Section droite : Bouton continuer */}
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <Button
                        data-onboarding="use-pattern-btn"
                        onClick={async () => {
                          const miresToSend: any[] = []

                          // ✅ UTILISER UNIQUEMENT LES MIRES DE LA LISTE
                          // Si des mires sont dans la liste, on n'utilise QUE celles-là
                          if (savedMires.length > 0) {
                            miresToSend.push(...savedMires.filter((m): m is NonNullable<typeof m> => m != null))
                          } else if (hasMireGenerated && pendingLEDConfig) {
                            // Seulement si aucune mire dans la liste, utiliser la mire en attente
                            try {
                              const mireImage = await captureMireImage()

                              const configWithImage = {
                                ...pendingLEDConfig,
                                name: currentMireName.trim() || `Mire 1`,
                                capturedImage: mireImage
                              }

                              miresToSend.push(configWithImage)
                            } catch (error) {
                              toast.error(t('synoptic.toast.mireCaptureError'))
                              return
                            }
                          }

                          // ✅ ENVOYER TOUTES LES MIRES AU WORKSPACE
                          if (miresToSend.length > 0) {
                            try {

                              // Calculer l'espacement entre les mires (en mm)
                              const cascadeOffsetX = 50
                              const cascadeOffsetY = 50

                              // Créer des éléments image pour chaque mire
                              const mireElements: WorkspaceElement[] = []

                              for (let i = 0; i < miresToSend.length; i++) {
                                const mire = miresToSend[i]
                                if (!mire?.ledWallMode) continue
                                const { columns, rows, panelWidth, panelHeight } = mire.ledWallMode
                                // Respecter le ratio réel des panneaux
                                const BASE_PANEL_SIZE = 40
                                const panelAspect = panelWidth / panelHeight
                                const workspacePanelWidth = panelAspect >= 1 ? BASE_PANEL_SIZE : BASE_PANEL_SIZE * panelAspect
                                const workspacePanelHeight = panelAspect >= 1 ? BASE_PANEL_SIZE / panelAspect : BASE_PANEL_SIZE
                                const totalWidth = columns * workspacePanelWidth
                                const totalHeight = rows * workspacePanelHeight

                                // Créer un nom détaillé pour la mire
                                const mireName = `${mire.name || 'Grille'} - ${panelWidth}×${panelHeight}px - ${columns}×${rows} panneaux`

                                // Calculer la position en cascade (diagonale)
                                const offsetX = i * cascadeOffsetX
                                const offsetY = i * cascadeOffsetY

                                // Créer un élément image pour cette mire
                                const mireImageElement: WorkspaceElement = {
                                  id: `mire_image_${Date.now()}_${i}_${Math.random()}`,
                                  type: 'image',
                                  data: {
                                    name: mireName,
                                    mireConfig: mire,
                                    isPendingConversion: true,
                                    imageData: mire.capturedImage
                                  },
                                  position: {
                                    x: -totalWidth / 2 + offsetX,
                                    y: -totalHeight / 2 + offsetY
                                  },
                                  size: {
                                    width: totalWidth,
                                    height: totalHeight
                                  }
                                }

                                mireElements.push(mireImageElement)
                              }

                              // Ajouter toutes les mires au workspace (sur la page Data active)
                              setDataPages(prevPages => {
                                return prevPages.map(page => {
                                  if (page.id === currentDataPageId) {
                                    return {
                                      ...page,
                                      workspaceElements: [...page.workspaceElements, ...mireElements]
                                    }
                                  }
                                  return page
                                })
                              })

                              // Vider la liste et la mire en attente après envoi
                              setSavedMires([])
                              setCurrentMireName('')
                              setEditingMireIndex(null)

                              // Ajouter les mires au projet (persistantes) uniquement lors de l'import depuis l'étape 1
                              for (let i = 0; i < miresToSend.length; i++) {
                                const mire = miresToSend[i]
                                const projectMire: ProjectMire = {
                                  id: `project_mire_${Date.now()}_${i}`,
                                  name: mire.name || `Mire ${i + 1}`,
                                  config: mire,
                                  linkedPanel: undefined,
                                  createdAt: new Date().toISOString()
                                }
                                setProjectMires(prev => {
                                  // Vérifier si la mire n'existe pas déjà (par timestamp)
                                  if (prev.some(pm => pm.config.timestamp === mire.timestamp)) {
                                    return prev
                                  }
                                  return [...prev, projectMire]
                                })
                              }

                              // Notifier l'onboarding
                              if (onboarding.state.isActive) {
                                onboarding.completeAction('mire-used');
                              }

                              toast.success(t("toast.patternsAdded", { count: miresToSend.length, plural: miresToSend.length > 1 ? 's' : '' }), {
                                description: 'Vous pouvez les convertir en panneaux plus tard'
                              })


                              // ✅ Changer d'étape APRÈS l'envoi réussi
                              handleStepChange("data")
                            } catch (error) {
                              toast.error(t('synoptic.toast.mireSendError'))
                              return
                            }
                          } else {
                            toast.warning(t('synoptic.toast.noMireToSend'), {
                              description: t('synoptic.toast.noMireToSendDesc')
                            })
                          }
                        }}
                        disabled={savedMires.length === 0 && !pendingLEDConfig}
                        size="lg"
                        className="gap-2 shadow-md hover:shadow-lg transition-shadow"
                      >
                        {savedMires.length > 1 ? t('synoptic.useMires') : (savedMires.length === 1 || pendingLEDConfig) ? t('synoptic.useMire') : t('synoptic.createMire')}
                        <ChevronRight className="h-5 w-5" />
                      </Button>
                      {!hasMireGenerated && (
                        <div className="absolute -top-12 right-0 bg-gray-800 text-white text-xs px-3 py-2 rounded shadow-lg whitespace-nowrap">
                          {t('synoptic.modifyAndAdd')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Liste des mires enregistrées */}
                {savedMires.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="text-sm font-medium mb-2">{t('synoptic.savedMires')}</div>
                    <div className="flex flex-wrap gap-2">
                      {savedMires.filter((mire): mire is NonNullable<typeof mire> => mire != null).map((mire, index) => (
                        <div
                          key={index}
                          onClick={() => loadMireForEditing(index)}
                          className={`flex items-center gap-2 px-3 py-1 rounded-md text-sm cursor-pointer transition-colors ${
                            editingMireIndex === index
                              ? 'bg-primary/10 ring-2 ring-primary'
                              : 'bg-muted hover:bg-accent'
                          }`}
                          title={t('synoptic.clickMireToEdit')}
                        >
                          <Grid3X3 className="h-3 w-3" />
                          {editingMireIndex === index && <Pencil className="h-3 w-3 text-primary" />}
                          <span className="font-medium">{mire.name || `Mire ${index + 1}`}</span>
                          <span className="text-muted-foreground">
                            ({mire.ledWallMode?.columns ?? '?'}×{mire.ledWallMode?.rows ?? '?'})
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSavedMires(prev => prev.filter((_, i) => i !== index));
                              setEditingMireIndex(prev => prev === null ? null : index === prev ? null : index < prev ? prev - 1 : prev);
                              toast.info(t("toast.patternRemoved", { name: mire.name || `Mire ${index + 1}` }));
                            }}
                            className="ml-2 text-muted-foreground hover:text-destructive transition-colors"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Sidebar (extracted to components/SynoptiqueSidebar.tsx) */}
            <SynoptiqueSidebar
              isLeftPanelOpen={isLeftPanelOpen}
              setIsLeftPanelOpen={setIsLeftPanelOpen}
              readOnly={readOnly}
              currentStep={currentStep}
              isPro={isPro}
              isTauri={isTauri}
              showProcessors={showProcessors}
              searchProcessor={searchProcessor}
              setSearchProcessor={setSearchProcessor}
              showProcessorSpecs={showProcessorSpecs}
              setShowProcessorSpecs={setShowProcessorSpecs}
              sortedBrands={sortedBrands}
              processorsByBrand={processorsByBrand}
              collapsedProcessorBrands={collapsedProcessorBrands}
              toggleProcessorBrandCollapse={toggleProcessorBrandCollapse}
              showPanels={showPanels}
              searchPanel={searchPanel}
              setSearchPanel={setSearchPanel}
              showPanelSpecs={showPanelSpecs}
              setShowPanelSpecs={setShowPanelSpecs}
              sortedPanelBrands={sortedPanelBrands}
              panelsByBrand={panelsByBrand}
              collapsedPanelBrands={collapsedPanelBrands}
              togglePanelBrandCollapse={togglePanelBrandCollapse}
              setIsCustomPanelDialogOpen={setIsCustomPanelDialogOpen}
              showCabinets={showCabinets}
              searchCabinet={searchCabinet}
              setSearchCabinet={setSearchCabinet}
              showCabinetSpecs={showCabinetSpecs}
              setShowCabinetSpecs={setShowCabinetSpecs}
              sortedCabinetBrands={sortedCabinetBrands}
              cabinetsByBrand={cabinetsByBrand}
              collapsedCabinetBrands={collapsedCabinetBrands}
              toggleCabinetBrandCollapse={toggleCabinetBrandCollapse}
              setIsCustomCabinetDialogOpen={setIsCustomCabinetDialogOpen}
              handleDragStart={handleDragStart}
              setDraggedItem={setDraggedItem}
              setDragMousePosition={setDragMousePosition}
              setIsDraggingFromList={setIsDraggingFromList}
              isDraggingFromList={isDraggingFromList}
              setSelectedProcessor={setSelectedProcessor}
              setSelectedPanel={setSelectedPanel}
              setSelectedCabinet={setSelectedCabinet}
              setSelectedItemForProperties={setSelectedItemForProperties}
              onboarding={onboarding}
              cartouche={cartouche}
              setCartouche={setCartouche}
              demoMode={demoMode}
              exportToPDF={exportToPDF}
              exportToImage={exportToImage}
              exportToSVG={exportToSVG}
              generateReport={generateReport}
              dataPages={dataPages}
              powerPages={powerPages}
              backupPorts={backupPorts}
              projectName={currentProject?.name || projectName}
              projectMires={projectMires}
              onDeleteProjectMire={handleDeleteProjectMire}
              formatResolution={formatResolution}
              formatNumber={formatNumber}
            />
            
            {/* Zone centrale - Workspace et Aperçu LED */}
            <div className="flex-1 flex flex-col">
              {/* Barre d'outils contextuelle (extracted to components/PageTabsToolbar.tsx) */}
              <PageTabsToolbar
                currentStep={currentStep}
                isDataMode={isDataMode}
                isPowerMode={isPowerMode}
                isExportMode={isExportMode}
                exportViewMode={exportViewMode}
                setExportViewMode={setExportViewMode}
                dataPages={dataPages}
                currentDataPageId={currentDataPageId}
                setCurrentDataPageId={setCurrentDataPageId}
                addDataPage={addDataPage}
                deleteDataPage={deleteDataPage}
                duplicateDataPage={duplicateDataPage}
                moveDataPage={moveDataPage}
                powerPages={powerPages}
                currentPowerPageId={currentPowerPageId}
                setCurrentPowerPageId={setCurrentPowerPageId}
                setCurrentStep={setCurrentStep}
                addPowerPage={addPowerPage}
                deletePowerPage={deletePowerPage}
                duplicatePowerPage={duplicatePowerPage}
                movePowerPage={movePowerPage}
                draggedTabId={draggedTabId}
                setDraggedTabId={setDraggedTabId}
                editingPageId={editingPageId}
                setEditingPageId={setEditingPageId}
                editingPageName={editingPageName}
                setEditingPageName={setEditingPageName}
                editingPageType={editingPageType}
                startEditingPage={startEditingPage}
                finishEditingPage={finishEditingPage}
                readOnly={readOnly}
                viewBox={viewBox}
                selectedElements={selectedElements}
                getEffectiveSelectionCount={getEffectiveSelectionCount}
                clipboard={clipboard}
                pasteElements={pasteElements}
              />
              

              {/* Workspace de conception */}
              <div
                className="flex-1 relative bg-muted/30"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                {/* Barre d'outils horizontale */}
                {/* Contrôles de gauche */}
                <div className="absolute top-4 left-4 z-10">
                  <div className="flex items-center gap-1 border rounded-md bg-background/90 p-1 backdrop-blur-sm shadow-lg">
                    <Button
                      variant={cursorMode === 'select' ? "secondary" : "ghost"}
                      size="sm"
                      onClick={() => setCursorMode('select')}
                      className="h-8 w-8 p-0"
                      title={t('title.selectionMode')}
                    >
                      <MousePointer className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={cursorMode === 'pan' ? "secondary" : "ghost"}
                      size="sm"
                      onClick={() => setCursorMode('pan')}
                      className="h-8 w-8 p-0"
                      title="Mode Navigation (H)"
                    >
                      <Hand className="h-4 w-4" />
                    </Button>
                    <Separator orientation="vertical" className="h-6 mx-1" />
                    <Button
                      variant={isSnappingEnabled ? "secondary" : "ghost"}
                      size="sm"
                      onClick={() => setIsSnappingEnabled(!isSnappingEnabled)}
                      className="h-8 w-8 p-0"
                      title={isSnappingEnabled ? t('title.disableSnapping') : t('title.enableSnapping')}
                    >
                      <Magnet className="h-4 w-4" />
                    </Button>
                  </div>
                </div>


                {/* Contrôles de zoom à droite */}
                <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
                  <div className="flex items-center gap-1 border rounded-md bg-background/90 p-1 backdrop-blur-sm shadow-lg">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const currentZoomPercent = (10000 / viewBox.width) / 8.73 * 100
                        const newZoomPercent = Math.max(50, currentZoomPercent * 0.9)
                        centerOnA3(newZoomPercent, 'zoom-out-button')
                      }}
                      className="h-8 w-8 p-0"
                      title="Zoom -"
                    >
                      <ZoomOut className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        centerOnA3(100.0, 'reset-button') // Reset au zoom 100% (qui correspond à l'ancien 873%)
                      }}
                      className="h-8 w-8 p-0"
                      title="Reset"
                    >
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const currentZoomPercent = (10000 / viewBox.width) / 8.73 * 100
                        const newZoomPercent = Math.min(2000, currentZoomPercent * 1.1)
                        centerOnA3(newZoomPercent, 'zoom-in-button')
                      }}
                      className="h-8 w-8 p-0"
                      title="Zoom +"
                    >
                      <ZoomIn className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="absolute inset-4 bg-background rounded-lg border-2 border-dashed border-muted-foreground/30">

                  <DndContext
                    sensors={sensors}
                    measuring={measuring}
                    onDragStart={dndWorkspace.handleDragStart}
                    onDragMove={dndWorkspace.handleDragMove}
                    onDragEnd={dndWorkspace.handleDragEnd}
                    onDragCancel={dndWorkspace.handleDragCancel}
                  >
                    <div
                      className="relative w-full h-full workspace-grid overflow-hidden"
                      onMouseDown={!dndWorkspace.activeId ? handleMouseDown : undefined}
                      onMouseMove={!dndWorkspace.activeId ? handleMouseMove : undefined}
                      onMouseUp={!dndWorkspace.activeId ? handleMouseUp : undefined}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      onMouseLeave={() => {
                        if (isDraggingElement && !dndWorkspace.activeId) {
                          setIsDraggingElement(false)
                          setDraggedElementId(null)
                          setElementInitialOffset({ x: 0, y: 0 })
                        }
                        if (isResizing) {
                          setIsResizing(false)
                          setResizedElementId(null)
                        }
                        if (isPanning) {
                          setIsPanning(false)
                        }
                        if (isRubberBandSelection) {
                          setIsRubberBandSelection(false)
                          setRubberBandStart({ x: 0, y: 0 })
                          setRubberBandEnd({ x: 0, y: 0 })
                          setIsCtrlPressed(false)
                          setPreviewSelectedElements([])
                        }
                        setSnapGuides({ x: [], y: [] })
                      }}
                      onWheel={handleWheel}
                      style={{
                        userSelect: isResizing || isDraggingElement ? 'none' : 'auto',
                        cursor: dndWorkspace.activeId ? 'grabbing' :
                          isDraggingElement ? 'move' :
                            isPanning ? 'grabbing' :
                              isRubberBandSelection ? 'crosshair' :
                                selectedPanelForPlacement && selectedProcessorForConnection && selectedPort !== null && currentStep === 'data' ? 'crosshair' :
                                  cursorMode === 'select' ? 'default' :
                                    cursorMode === 'pan' ? 'grab' : 'default'
                      }}
                    >
                      <svg
                        ref={workspaceRef}
                        className="w-full h-full"
                        data-onboarding="workspace"
                        viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`}
                      >
                        {/* Grille de fond désactivée */}

                        {/* Zone d'export A3 (860mm × 600mm) - Délimitation simple */}
                        <rect
                          x="-430"
                          y="-300"
                          width="860"
                          height="600"
                          fill="none"
                          stroke="white"
                          strokeWidth="1"
                          strokeDasharray="5,5"
                          opacity="0.6"
                        />

                        {/* Cartouche en bas de la zone d'export (extracted to SvgCartouche) */}
                        {currentStep === 'export' && showCartouche && (
                          <SvgCartouche cartouche={cartouche} t={t} />
                        )}

                        {/* Guides de snap pour l'alignement (extracted to SvgSnapGuides) */}
                        {isSnappingEnabled && <SvgSnapGuides snapGuides={snapGuides} />}

                        {/* Rectangle de sélection (rubber band) */}
                        {isRubberBandSelection && (
                          <g>
                            <rect
                              x={Math.min(rubberBandStart.x, rubberBandEnd.x)}
                              y={Math.min(rubberBandStart.y, rubberBandEnd.y)}
                              width={Math.abs(rubberBandEnd.x - rubberBandStart.x)}
                              height={Math.abs(rubberBandEnd.y - rubberBandStart.y)}
                              fill="hsl(var(--primary))"
                              fillOpacity="0.1"
                              stroke="hsl(var(--primary))"
                              strokeWidth="2"
                              strokeDasharray="5,5"
                              rx="4"
                              pointerEvents="none"
                              className="animate-pulse"
                            />
                          </g>
                        )}

                        {/* Preview des panneaux temporaires pendant le drag */}
                        {isDraggingPlacement && previewPanels.length > 0 && (
                          <g opacity="0.8">
                            {previewPanels.map((panel, index) => {
                              // Utiliser directement l'index + 1 comme numéro d'ordre de câblage
                              // L'ordre serpentin est déjà appliqué dans calculatePreviewCables
                              // et les panneaux sont générés dans le bon ordre dans calculatePreviewPanels
                              let displayNumber = index + 1;

                              return (
                                <g key={`preview-${index}`}>
                                  <rect
                                    x={panel.x}
                                    y={panel.y}
                                    width={panel.width}
                                    height={panel.height}
                                    fill="hsl(var(--led-panel))"
                                    stroke="hsl(var(--primary))"
                                    strokeWidth="2"
                                    strokeDasharray="6,3"
                                    rx="4"
                                    pointerEvents="none"
                                  />
                                  <foreignObject
                                    x={panel.x + 4}
                                    y={panel.y + 4}
                                    width={panel.width - 8}
                                    height={panel.height - 8}
                                    pointerEvents="none"
                                  >
                                    <div className="flex items-center justify-center h-full relative">
                                      {/* Badge PX couvrant tout le premier panneau en preview */}
                                      {index === 0 && selectedPort && (
                                        <div
                                          className="absolute inset-0 rounded flex flex-col items-center justify-center"
                                          style={{ backgroundColor: selectedPort ? getPortColor(selectedPort, selectedProcessorForConnection) : '#2563EB', opacity: 0.8 }}
                                        >
                                          <div className="text-white text-lg font-bold">
                                            P{selectedPort}
                                          </div>
                                          <div className="text-white text-lg font-bold mt-1">
                                            {displayNumber}
                                          </div>
                                        </div>
                                      )}

                                      {/* Badge RY couvrant tout le dernier panneau si backup configuré */}
                                      {index === previewPanels.length - 1 && selectedPort && selectedProcessorForConnection && getBackupForPort(selectedPort, selectedProcessorForConnection) && (
                                        <div
                                          className="absolute inset-0 rounded flex flex-col items-center justify-center"
                                          style={{ backgroundColor: getPortColor(getBackupForPort(selectedPort, selectedProcessorForConnection)!), opacity: 0.8 }}
                                        >
                                          <div className="text-white text-lg font-bold">
                                            R{getBackupForPort(selectedPort, selectedProcessorForConnection)}
                                          </div>
                                          <div className="text-white text-lg font-bold mt-1">
                                            {displayNumber}
                                          </div>
                                        </div>
                                      )}

                                      {/* Badge numéro normal pour les panneaux intermédiaires */}
                                      {!(index === 0 && selectedPort) && !(index === previewPanels.length - 1 && selectedPort && selectedProcessorForConnection && getBackupForPort(selectedPort, selectedProcessorForConnection)) && (
                                        <div className="bg-blue-600/90 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center border border-white/50 shadow-lg">
                                          {displayNumber}
                                        </div>
                                      )}
                                    </div>
                                  </foreignObject>
                                </g>
                              );
                            })}
                          </g>
                        )}

                        {/* Preview du panneau qui suit la souris */}
                        {selectedPanelForPlacement && currentStep === 'data' && !isDraggingElement && !isPanning && !isDraggingPlacement && (
                          <>
                            {/* Preview principal du panneau */}
                            <g opacity="0.8">
                              <rect
                                x={mousePosition.x - selectedPanelForPlacement.dimensions.width / 2}
                                y={mousePosition.y - selectedPanelForPlacement.dimensions.height / 2}
                                width={selectedPanelForPlacement.dimensions.width}
                                height={selectedPanelForPlacement.dimensions.height}
                                fill="hsl(var(--led-panel))"
                                stroke="hsl(var(--primary))"
                                strokeWidth="3"
                                strokeDasharray="8,4"
                                rx="4"
                                pointerEvents="none"
                                className="animate-pulse"
                              />
                            </g>

                            {/* Indicateur de coordonnées */}
                            <g>
                              <rect
                                x={mousePosition.x + selectedPanelForPlacement.dimensions.width / 2 + 10}
                                y={mousePosition.y - selectedPanelForPlacement.dimensions.height / 2}
                                width="120"
                                height="25"
                                fill="hsl(var(--background))"
                                stroke="hsl(var(--border))"
                                strokeWidth="1"
                                rx="4"
                                opacity="0.9"
                                pointerEvents="none"
                              />
                              <foreignObject
                                x={mousePosition.x + selectedPanelForPlacement.dimensions.width / 2 + 15}
                                y={mousePosition.y - selectedPanelForPlacement.dimensions.height / 2 + 3}
                                width="110"
                                height="20"
                                pointerEvents="none"
                              >
                                <div className="flex items-center justify-center h-full text-xs font-mono text-foreground">
                                  📐 X:{Math.round(mousePosition.x)} Y:{Math.round(mousePosition.y)}
                                </div>
                              </foreignObject>
                            </g>
                          </>
                        )}

                        {/* Guide de positionnement pendant le drag */}
                        {isDraggingElement && draggedElementId && (
                          <g opacity="0.3">
                            {workspaceElements
                              .filter(el => el.id === draggedElementId)
                              .map(element => {
                                // Pour les processeurs avec image, utiliser les dimensions réelles de l'image
                                const bounds = element.type === 'processor' && element.data.image
                                  ? getActualImageBounds(element)
                                  : {
                                    x: element.position.x,
                                    y: element.position.y,
                                    width: element.size.width,
                                    height: element.size.height
                                  }

                                return (
                                  <rect
                                    key={`ghost-${element.id}`}
                                    x={bounds.x}
                                    y={bounds.y}
                                    width={bounds.width}
                                    height={bounds.height}
                                    fill="none"
                                    stroke="hsl(var(--primary))"
                                    strokeWidth="2"
                                    strokeDasharray="5,5"
                                    rx={element.type === "processor" ? "8" : "4"}
                                  />
                                )
                              })}
                          </g>
                        )}

                        {/* Preview des câbles pendant le drag de placement multiple */}
                        {isDraggingPlacement && previewCables.length > 0 && (
                          <g opacity="0.7">
                            {previewCables.map((cable) => {
                              if (!hasValidCablePoints(cable)) return null
                              const portColor = selectedPort ? getPortColor(selectedPort, selectedProcessorForConnection) : "#2563EB"
                              return (
                                <line
                                  key={cable.id}
                                  x1={cable.points[0].x}
                                  y1={cable.points[0].y}
                                  x2={cable.points[1].x}
                                  y2={cable.points[1].y}
                                  stroke={portColor}
                                  strokeWidth="2"
                                  strokeDasharray="8,4"
                                  fill="none"
                                  pointerEvents="none"
                                  className="animate-pulse"
                                />
                              )
                            })}
                          </g>
                        )}

                        {/* Éléments du workspace */}
                        {workspaceElements.map((element) => {
                          const isBeingDragged = element.id === dndWorkspace.activeId
                          const isInDragGroup = dndWorkspace.draggedElements.includes(element.id) && !isBeingDragged

                          return (
                            <DraggableWorkspaceElement
                              key={element.id}
                              id={element.id}
                              disabled={cursorMode !== 'select'}
                              cursorMode={cursorMode}
                              screenDeltaToSVGDelta={screenDeltaToSVGDelta}
                              isInDragGroup={isInDragGroup}
                              currentDelta={dndWorkspace.currentDelta}
                            >
                              {/* Processeur - Composant optimisé avec React.memo */}
                              {element.type === "processor" && (
                                <ProcessorElement
                                  element={element}
                                  processor={element.data as LEDProcessor}
                                  isSelected={selectedElements.includes(element.id) || (isDraggingBackupButton && element.id !== processorForBackup && element.id === backupButtonDragTarget)}
                                  isPreviewSelected={previewSelectedElements.includes(element.id) || (isDraggingBackupButton && element.id === processorForBackup)}
                                    isDragging={isDraggingElement && draggedElementId === element.id}
                                    isSelectedForConnection={selectedProcessorForConnection === element.id}
                                    currentStep={currentStep}
                                    showResizeHandle={shouldShowResizeHandle(element.id)}
                                    panelConnections={panelConnections}
                                    mireConnections={mireConnections}
                                    backupPorts={backupPorts}
                                    backupProcessors={backupProcessors}
                                    getBackupProcessor={getBackupProcessor}
                                    getPrimaryProcessor={getPrimaryProcessor}
                                    isProcessorBackup={isProcessorBackup}
                                    getProcessorImageUrl={getProcessorImageUrl}
                                    getActualImageBounds={getActualImageBounds}
                                    onClick={handleElementClick}
                                  onResizeStart={(e, el) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    if (readOnly) return
                                    setIsResizing(true)
                                    setResizedElementId(el.id)
                                    setInitialSize({ width: el.size.width, height: el.size.height })
                                    setInitialPosition({ x: el.position.x, y: el.position.y })
                                    setResizeStart(screenToSVG(e.clientX, e.clientY))

                                    const groupId = getGroupForElement(el.id)
                                    if (groupId) {
                                      setResizingGroupId(groupId)
                                      const bounds = getGroupBounds(groupId)
                                      setInitialGroupBounds(bounds)
                                      const group = elementGroups[groupId]
                                      const groupElements = workspaceElements
                                        .filter(elem => group.elementIds.includes(elem.id))
                                        .map(elem => ({
                                          id: elem.id,
                                          position: { ...elem.position },
                                          size: { ...elem.size }
                                        }))
                                      setInitialGroupElements(groupElements)
                                    } else {
                                      setResizingGroupId(null)
                                      setInitialGroupBounds(null)
                                      setInitialGroupElements([])
                                    }
                                  }}
                                />
                              )}

                              {/* Panel - Composant optimisé avec React.memo */}
                              {/* ✅ OPTIMISATION : Utiliser SimplePanelElement pour grandes mires (> 200 panneaux) */}
                              {element.type === "panel" && (
                                isLargeMire ? (
                                  <SimplePanelElement
                                    element={element}
                                    panel={element.data as LEDPanel}
                                    isSelected={selectedElements.includes(element.id)}
                                    isDragging={isDraggingElement && draggedElementId === element.id}
                                    onClick={handleElementClick}
                                    onContextMenu={handlePanelContextMenu}
                                  />
                                ) : (
                                  <PanelElement
                                    element={element}
                                    panel={element.data as LEDPanel}
                                    isSelected={selectedElements.includes(element.id)}
                                    isDragging={isDraggingElement && draggedElementId === element.id}
                                    isCurrentForCabling={currentPanelForCabling === element.id}
                                    isGridNavigationSelected={false}
                                    currentStep={currentStep}
                                    exportViewMode={exportViewMode}
                                    selectedPort={selectedPort}
                                    selectedProcessorForConnection={selectedProcessorForConnection}
                                    panelConnections={panelConnections}
                                    powerConnections={powerConnections}
                                    backupPorts={backupPorts}
                                    cableRoutes={cableRoutes}
                                    workspaceElements={workspaceElements}
                                    getPortColor={getPortColor}
                                    getOutletColor={getOutletColor}
                                    getBackupForPort={getBackupForPort}
                                    onClick={handleElementClick}
                                    onContextMenu={handlePanelContextMenu}
                                    showResizeHandle={shouldShowResizeHandle(element.id)}
                                    onResizeStart={(e, el) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      if (readOnly) return
                                      setIsResizing(true)
                                      setResizedElementId(el.id)
                                      setInitialSize({ width: el.size.width, height: el.size.height })
                                      setInitialPosition({ x: el.position.x, y: el.position.y })
                                      setResizeStart(screenToSVG(e.clientX, e.clientY))

                                      const groupId = getGroupForElement(el.id)
                                      if (groupId) {
                                        setResizingGroupId(groupId)
                                        const bounds = getGroupBounds(groupId)
                                        setInitialGroupBounds(bounds)
                                        const group = elementGroups[groupId]
                                        const groupElements = workspaceElements
                                          .filter(elem => group.elementIds.includes(elem.id))
                                          .map(elem => ({
                                            id: elem.id,
                                            position: { ...elem.position },
                                            size: { ...elem.size }
                                          }))
                                        setInitialGroupElements(groupElements)
                                      } else {
                                        setResizingGroupId(null)
                                        setInitialGroupBounds(null)
                                        setInitialGroupElements([])
                                      }
                                    }}
                                  />
                                )
                              )}


                              {/* Cabinet - Composant optimisé avec React.memo */}
                              {element.type === "cabinet" && (
                                <CabinetElement
                                  element={element}
                                  cabinet={element.data as PowerCabinet}
                                  isSelected={selectedElements.includes(element.id)}
                                  isPreviewSelected={previewSelectedElements.includes(element.id)}
                                  isDragging={isDraggingElement && draggedElementId === element.id}
                                  isSelectedForConnection={selectedCabinetForConnection === element.id}
                                  onClick={handleElementClick}
                                  showResizeHandle={shouldShowResizeHandle(element.id)}
                                  onResizeStart={(e, el) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    if (readOnly) return
                                    setIsResizing(true)
                                    setResizedElementId(el.id)
                                    setInitialSize({ width: el.size.width, height: el.size.height })
                                    setInitialPosition({ x: el.position.x, y: el.position.y })
                                    setResizeStart(screenToSVG(e.clientX, e.clientY))

                                    const groupId = getGroupForElement(el.id)
                                    if (groupId) {
                                      setResizingGroupId(groupId)
                                      const bounds = getGroupBounds(groupId)
                                      setInitialGroupBounds(bounds)
                                      const group = elementGroups[groupId]
                                      const groupElements = workspaceElements
                                        .filter(elem => group.elementIds.includes(elem.id))
                                        .map(elem => ({
                                          id: elem.id,
                                          position: { ...elem.position },
                                          size: { ...elem.size }
                                        }))
                                      setInitialGroupElements(groupElements)
                                    } else {
                                      setResizingGroupId(null)
                                      setInitialGroupBounds(null)
                                      setInitialGroupElements([])
                                    }
                                  }}
                                />
                              )}

                              {/* NEW: Image mire avec grille virtuelle */}
                              {element.type === "image" && element.data?.mireConfig && (
                                <>
                                  {(() => {
                                    const config = element.data.mireConfig
                                    if (!config?.ledWallMode) return null
                                    const { columns, rows } = config.ledWallMode

                                    // Determine if grid should be visible
                                    const showDataGrid = ((currentStep === 'data' || (currentStep === 'export' && exportViewMode === 'data')) &&
                                      selectedProcessorForConnection &&
                                      selectedPort !== null)

                                    const showPowerGrid = ((currentStep === 'cabling' || (currentStep === 'export' && exportViewMode === 'power')) &&
                                      selectedCabinetForConnection &&
                                      selectedOutlet !== null)

                                    // NEW: Also show grid when nothing selected BUT there are connections (to show first/last numbering)
                                    const hasDataConnections = ((currentStep === 'data' || (currentStep === 'export' && exportViewMode === 'data')) &&
                                      mireConnections.some(conn => conn.imageId === element.id))
                                    const hasPowerConnections = ((currentStep === 'cabling' || (currentStep === 'export' && exportViewMode === 'power')) &&
                                      mirePowerConnections.some(conn => conn.imageId === element.id))

                                    const showGridForNumbering = (hasDataConnections && !selectedProcessorForConnection) ||
                                      (hasPowerConnections && !selectedCabinetForConnection)

                                    const showGrid = showDataGrid || showPowerGrid || showGridForNumbering
                                    const mode: 'data' | 'power' = (showDataGrid || (showGridForNumbering && hasDataConnections)) ? 'data' : 'power'

                                    // Calculate cell dimensions
                                    const cellWidth = element.size.width / columns
                                    const cellHeight = element.size.height / rows

                                    // Calculate responsive font and circle sizes
                                    const fontSize = Math.max(14, Math.min(cellWidth, cellHeight) * 0.4)
                                    const circleRadius = Math.max(15, Math.min(cellWidth, cellHeight) * 0.25)

                                    // Calculate mire info for display
                                    const panelWidth = config.ledWallMode?.panelWidth || 0
                                    const panelHeight = config.ledWallMode?.panelHeight || 0
                                    const totalResWidth = columns * panelWidth
                                    const totalResHeight = rows * panelHeight
                                    const mireName = element.data?.name?.split(' - ')[0] || 'Mire'
                                    const linkedPanel = element.data?.linkedPanel as LEDPanel | undefined

                                    return (
                                      <g key={`mire-${element.id}`}>
                                        {/* Base mire image */}
                                        <image
                                          href={element.data.imageData}
                                          x={element.position.x}
                                          y={element.position.y}
                                          width={element.size.width}
                                          height={element.size.height}
                                          preserveAspectRatio="none"
                                          opacity={1}
                                          style={{ pointerEvents: 'none' }}
                                        />

                                        {/* Mire info overlay - configurable bubble above the mire */}
                                        {(() => {
                                          // Get bubble config from mire data or use defaults
                                          const bubbleConfig = element.data?.bubbleConfig || {}
                                          if (bubbleConfig.visible === false) return null
                                          const mireWidth = config.width || 1920
                                          const mireHeight = config.height || 1080
                                          const bubbleWidth = bubbleConfig.width || 220
                                          const baseHeight = linkedPanel ? 52 : 40
                                          const bubbleHeight = bubbleConfig.height || baseHeight
                                          const bubbleX = element.position.x + element.size.width / 2 - bubbleWidth / 2
                                          const bubbleY = element.position.y - bubbleHeight - 8
                                          const padding = 12
                                          
                                          // Configurable colors and font sizes
                                          const bgColor = bubbleConfig.bgColor || 'white'
                                          const textColor = bubbleConfig.textColor || 'black'
                                          const titleFontSize = bubbleConfig.titleFontSize || 11
                                          const infoFontSize = bubbleConfig.infoFontSize || 9
                                          const panelFontSize = bubbleConfig.panelFontSize || 8
                                          
                                          // Calculate responsive font sizes based on bubble dimensions
                                          const scaleFactor = Math.min(bubbleWidth / 220, bubbleHeight / baseHeight)
                                          const scaledTitleFont = Math.max(8, titleFontSize * scaleFactor)
                                          const scaledInfoFont = Math.max(6, infoFontSize * scaleFactor)
                                          const scaledPanelFont = Math.max(5, panelFontSize * scaleFactor)
                                          
                                          // Calculate text positions based on bubble height
                                          const titleY = bubbleY + bubbleHeight * 0.35
                                          const infoY = bubbleY + bubbleHeight * 0.65
                                          const panelY = bubbleY + bubbleHeight * 0.85

                                          return (
                                            <>
                                            <g style={{ pointerEvents: 'none' }}>
                                              {/* Shadow for depth */}
                                              <rect
                                                x={bubbleX + 2}
                                                y={bubbleY + 2}
                                                width={bubbleWidth}
                                                height={bubbleHeight}
                                                fill="rgba(0,0,0,0.15)"
                                                rx={10}
                                              />
                                              {/* Main background */}
                                              <rect
                                                x={bubbleX}
                                                y={bubbleY}
                                                width={bubbleWidth}
                                                height={bubbleHeight}
                                                fill={bgColor}
                                                stroke="#e5e7eb"
                                                strokeWidth={1}
                                                rx={10}
                                              />
                                              {/* Mire name - centered, bold */}
                                              <text
                                                x={bubbleX + bubbleWidth / 2}
                                                y={titleY}
                                                fontSize={scaledTitleFont}
                                                fontWeight="bold"
                                                fill={textColor}
                                                textAnchor="middle"
                                                dominantBaseline="middle"
                                              >
                                                {bubbleConfig.title || mireName}
                                              </text>
                                              {/* Resolution (left) and panel count (right) */}
                                              <text
                                                x={bubbleX + padding}
                                                y={infoY}
                                                fontSize={scaledInfoFont}
                                                fill={textColor}
                                                textAnchor="start"
                                                dominantBaseline="middle"
                                              >
                                                {mireWidth}×{mireHeight}px
                                              </text>
                                              <text
                                                x={bubbleX + bubbleWidth - padding}
                                                y={infoY}
                                                fontSize={scaledInfoFont}
                                                fill={textColor}
                                                textAnchor="end"
                                                dominantBaseline="middle"
                                              >
                                                {columns}×{rows} panneaux
                                              </text>
                                              {/* Panel type if linked */}
                                              {linkedPanel && bubbleHeight >= 45 && (
                                                <text
                                                  x={bubbleX + bubbleWidth / 2}
                                                  y={panelY}
                                                  fontSize={scaledPanelFont}
                                                  fill={textColor}
                                                  textAnchor="middle"
                                                  dominantBaseline="middle"
                                                >
                                                  {linkedPanel.brand} {linkedPanel.name} (P{linkedPanel.pixelPitch})
                                                </text>
                                              )}
                                            </g>
                                              {/* Poignée de resize de la bulle */}
                                              <rect
                                                data-resize-handle="true"
                                                x={bubbleX + bubbleWidth - 5}
                                                y={bubbleY + bubbleHeight - 5}
                                                width="10"
                                                height="10"
                                                fill="hsl(var(--primary))"
                                                stroke="hsl(var(--background))"
                                                strokeWidth="1"
                                                rx="2"
                                                className="cursor-nwse-resize"
                                                style={{ pointerEvents: 'auto', opacity: selectedElements.includes(element.id) ? 1 : 0 }}
                                                onMouseDown={(e) => {
                                                  e.preventDefault()
                                                  e.stopPropagation()
                                                  setResizingBubble({
                                                    elementId: element.id,
                                                    startX: e.clientX,
                                                    startY: e.clientY,
                                                    startWidth: bubbleWidth,
                                                    startHeight: bubbleHeight
                                                  })
                                                }}
                                              />
                                            </>
                                          )
                                        })()}

                                        {/* Transparent clickable overlay for drag (only when grid not visible or only showing numbering) */}
                                        {(!showGrid || showGridForNumbering) && (
                                          <rect
                                            x={element.position.x}
                                            y={element.position.y}
                                            width={element.size.width}
                                            height={element.size.height}
                                            fill="transparent"
                                            stroke="none"
                                            className="cursor-move"
                                            style={{ pointerEvents: 'auto' }}
                                          />
                                        )}

                                        {/* Text elements from mire config */}
                                        {element.data?.mireConfig?.textElements?.map((textElement: any, index: number) => {
                                          const textX = element.position.x + (textElement.x / config.width) * element.size.width
                                          const textY = element.position.y + (textElement.y / config.height) * element.size.height
                                          const scaledFontSize = (textElement.fontSize / config.height) * element.size.height

                                          return (
                                            <text
                                              key={`text-${index}`}
                                              x={textX}
                                              y={textY}
                                              fontSize={scaledFontSize}
                                              fontFamily={textElement.fontFamily}
                                              fontWeight={textElement.fontWeight}
                                              fontStyle={textElement.fontStyle}
                                              textAnchor={textElement.textAlign}
                                              fill={textElement.color}
                                              style={{ pointerEvents: 'none' }}
                                            >
                                              {textElement.text}
                                            </text>
                                          )
                                        })}

                                        {/* Logo elements from mire config */}
                                        {element.data?.mireConfig?.logoElements?.map((logoElement: any, index: number) => {
                                          const logoX = element.position.x + (logoElement.x / config.width) * element.size.width
                                          const logoY = element.position.y + (logoElement.y / config.height) * element.size.height
                                          const logoWidth = (logoElement.width / config.width) * element.size.width
                                          const logoHeight = (logoElement.height / config.height) * element.size.height

                                          return (
                                            <image
                                              key={`logo-${index}`}
                                              href={logoElement.dataUrl}
                                              x={logoX}
                                              y={logoY}
                                              width={logoWidth}
                                              height={logoHeight}
                                              opacity={logoElement.opacity}
                                              preserveAspectRatio="xMidYMid meet"
                                              style={{ pointerEvents: 'none' }}
                                            />
                                          )
                                        })}

                                        {/* Virtual grid overlay (processor/cabinet selected OR showing first/last numbering) */}
                                        {showGrid && (
                                          <g key={`grid-${element.id}`}>
                                            {Array.from({ length: rows }).map((_, rowIdx) =>
                                              Array.from({ length: columns }).map((_, colIdx) => {
                                                const row = rowIdx + 1  // 1-indexed
                                                const col = colIdx + 1  // 1-indexed

                                                // Calculate cell position
                                                const x = element.position.x + colIdx * cellWidth
                                                const y = element.position.y + rowIdx * cellHeight

                                                // Get connection for this cell
                                                const connection = getCellConnection(element.id, row, col, mode)

                                                // En mode numérotation seule (rien de sélectionné), on saute les cellules non connectées
                                                if (showGridForNumbering && !connection) return null

                                                // Check states
                                                const isCurrent = currentMireCell?.imageId === element.id &&
                                                  currentMireCell?.row === row &&
                                                  currentMireCell?.col === col

                                                const isPreview = previewMireCell?.imageId === element.id &&
                                                  previewMireCell?.row === row &&
                                                  previewMireCell?.col === col

                                                // Determine color
                                                let cellColor = "rgba(255,255,255,0.15)"
                                                if (connection) {
                                                  if (mode === 'data') {
                                                    cellColor = getPortColor((connection as MireConnection).port, (connection as MireConnection).processorId)
                                                  } else {
                                                    cellColor = getOutletColor((connection as MirePowerConnection).outlet, (connection as MirePowerConnection).cabinetId)
                                                  }
                                                }

                                                return (
                                                  <g key={`cell-${row}-${col}`}>
                                                    {/* Clickable cell rectangle - visible en mode grille, transparent en mode numérotation seule mais toujours interactif si connexion */}
                                                    <rect
                                                      x={x}
                                                      y={y}
                                                      width={cellWidth}
                                                      height={cellHeight}
                                                      fill={showGridForNumbering ? "transparent" : (isPreview ? "rgba(255,255,255,0.15)" : "transparent")}
                                                      stroke={showGridForNumbering ? "none" : (connection ? cellColor : "rgba(255,255,255,0.2)")}
                                                      strokeWidth={showGridForNumbering ? 0 : (connection ? Math.max(1, Math.min(5, cellWidth * 0.05)) : Math.max(0.5, Math.min(2, cellWidth * 0.02)))}
                                                      className={showGridForNumbering ? (connection ? "cursor-context-menu" : "") : "cursor-pointer hover:fill-white/10 transition-all duration-200"}
                                                      onClick={(e) => {
                                                        if (showGridForNumbering) return
                                                        e.stopPropagation()
                                                        if (mode === 'data') {
                                                          handleMireCellClickForData(element.id, row, col)
                                                        } else {
                                                          handleMireCellClickForPower(element.id, row, col)
                                                        }
                                                      }}
                                                      onContextMenu={(e) => {
                                                        e.stopPropagation()
                                                        handlePanelContextMenu(e, element, { row, col })
                                                      }}
                                                      onMouseEnter={() => {
                                                        if (!showGridForNumbering) setPreviewMireCell({ imageId: element.id, row, col })
                                                      }}
                                                      onMouseLeave={() => {
                                                        if (!showGridForNumbering) setPreviewMireCell(null)
                                                      }}
                                                      style={{ pointerEvents: connection || !showGridForNumbering ? 'auto' : 'none' }}
                                                    />


                                                    {/* Connection badges and number - like panels */}
                                                    {connection && (() => {
                                                      // Determine if first or last in chain + check backup
                                                      let isFirstCell = false
                                                      let isLastCell = false
                                                      let hasBackup = null
                                                      let portOrOutlet = 0

                                                      if (mode === 'data') {
                                                        const dataConn = connection as MireConnection
                                                        portOrOutlet = dataConn.port

                                                        // Find all connections for this processor + port
                                                        const allConnections = mireConnections.filter(conn =>
                                                          conn.processorId === dataConn.processorId &&
                                                          conn.port === dataConn.port
                                                        ).sort((a, b) => a.order - b.order)

                                                        isFirstCell = allConnections[0]?.gridPosition.row === row && allConnections[0]?.gridPosition.col === col
                                                        isLastCell = allConnections[allConnections.length - 1]?.gridPosition.row === row && allConnections[allConnections.length - 1]?.gridPosition.col === col
                                                        hasBackup = getBackupForPort(dataConn.port, dataConn.processorId)
                                                      } else {
                                                        const powerConn = connection as MirePowerConnection
                                                        portOrOutlet = powerConn.outlet

                                                        // Find all connections for this cabinet + outlet
                                                        const allConnections = mirePowerConnections.filter(conn =>
                                                          conn.cabinetId === powerConn.cabinetId &&
                                                          conn.outlet === powerConn.outlet
                                                        ).sort((a, b) => a.order - b.order)

                                                        isFirstCell = allConnections[0]?.gridPosition.row === row && allConnections[0]?.gridPosition.col === col
                                                        isLastCell = allConnections[allConnections.length - 1]?.gridPosition.row === row && allConnections[allConnections.length - 1]?.gridPosition.col === col
                                                      }

                                                      // Calculate optimal position for number
                                                      const cellId = `${element.id}_cell_${row}_${col}`
                                                      const cables = cableRoutes.filter(cable =>
                                                        (cable.from === cellId || cable.to === cellId) &&
                                                        hasValidCablePoints(cable)
                                                      )

                                                      let numberX = x + cellWidth / 2
                                                      let numberY = y + cellHeight / 2
                                                      let textAnchor: 'start' | 'middle' | 'end' = 'middle'

                                                      if (cables.length > 0) {
                                                        const directions = { top: false, bottom: false, left: false, right: false }

                                                        cables.forEach(cable => {
                                                          const isIncoming = cable.to === cellId
                                                          const otherPoint = isIncoming ? cable.points[0] : cable.points[1]
                                                          const cellCenter = { x: x + cellWidth / 2, y: y + cellHeight / 2 }

                                                          const dx = otherPoint.x - cellCenter.x
                                                          const dy = otherPoint.y - cellCenter.y

                                                          if (Math.abs(dx) > Math.abs(dy)) {
                                                            if (dx > 0) directions.right = true
                                                            else directions.left = true
                                                          } else {
                                                            if (dy > 0) directions.bottom = true
                                                            else directions.top = true
                                                          }
                                                        })

                                                        const corners = [
                                                          { blocked: directions.top || directions.left, x: x + 8, y: y + 12, anchor: 'start' as const },
                                                          { blocked: directions.top || directions.right, x: x + cellWidth - 8, y: y + 12, anchor: 'end' as const },
                                                          { blocked: directions.bottom || directions.left, x: x + 8, y: y + cellHeight - 8, anchor: 'start' as const },
                                                          { blocked: directions.bottom || directions.right, x: x + cellWidth - 8, y: y + cellHeight - 8, anchor: 'end' as const }
                                                        ]

                                                        const bestCorner = corners.find(corner => !corner.blocked) || corners[0]
                                                        numberX = bestCorner.x
                                                        numberY = bestCorner.y
                                                        textAnchor = bestCorner.anchor
                                                      }

                                                      // Appliquer le filtre de numérotation mire
                                                      const showThisNumber = mireNumberingMode === 'all' ||
                                                        (mireNumberingMode === 'first-last' && (isFirstCell || isLastCell))

                                                      return (
                                                        <>
                                                          {/* Badge PX or PC for first cell */}
                                                          {isFirstCell && mireNumberingMode !== 'none' && (
                                                            <>
                                                              <rect
                                                                x={x}
                                                                y={y}
                                                                width={cellWidth}
                                                                height={cellHeight}
                                                                fill={cellColor}
                                                                fillOpacity="1"
                                                                rx="2"
                                                                style={{ pointerEvents: 'none' }}
                                                              />
                                                              <text
                                                                x={x + cellWidth / 2}
                                                                y={y + cellHeight / 2}
                                                                fontSize={Math.max(10, Math.min(14, cellWidth * 0.3))}
                                                                fontWeight="bold"
                                                                fill="white"
                                                                textAnchor="middle"
                                                                dominantBaseline="central"
                                                                style={{ pointerEvents: 'none' }}
                                                              >
                                                                {mode === 'data' ? `P${portOrOutlet}` : `PC${portOrOutlet}`}
                                                              </text>
                                                            </>
                                                          )}

                                                          {/* Badge RX for last cell with backup */}
                                                          {isLastCell && hasBackup && mode === 'data' && mireNumberingMode !== 'none' && (
                                                            <>
                                                              <rect
                                                                x={x}
                                                                y={y}
                                                                width={cellWidth}
                                                                height={cellHeight}
                                                                fill={getPortColor(hasBackup)}
                                                                fillOpacity="1"
                                                                rx="2"
                                                                style={{ pointerEvents: 'none' }}
                                                              />
                                                              <text
                                                                x={x + cellWidth / 2}
                                                                y={y + cellHeight / 2}
                                                                fontSize={Math.max(10, Math.min(14, cellWidth * 0.3))}
                                                                fontWeight="bold"
                                                                fill="white"
                                                                textAnchor="middle"
                                                                dominantBaseline="central"
                                                                style={{ pointerEvents: 'none' }}
                                                              >
                                                                R{hasBackup}
                                                              </text>
                                                            </>
                                                          )}

                                                          {/* Order number - filtered by mireNumberingMode */}
                                                          {showThisNumber && (
                                                            <text
                                                              x={numberX}
                                                              y={numberY}
                                                              fill={cellColor}
                                                              fontSize={Math.max(12, Math.min(16, cellWidth * 0.27))}
                                                              fontWeight="bold"
                                                              textAnchor={textAnchor}
                                                              dominantBaseline="middle"
                                                              style={{
                                                                pointerEvents: 'none',
                                                                textShadow: '0 0 3px rgba(0,0,0,0.8), 0 0 6px rgba(0,0,0,0.6)'
                                                              }}
                                                            >
                                                              {connection.order}
                                                            </text>
                                                          )}
                                                        </>
                                                      )
                                                    })()}
                                                  </g>
                                                )
                                              })
                                            )}
                                          </g>
                                        )}

                                        {/* Selection border */}
                                        {selectedElements.includes(element.id) && (
                                          <rect
                                            x={element.position.x - 2}
                                            y={element.position.y - 2}
                                            width={element.size.width + 4}
                                            height={element.size.height + 4}
                                            fill="none"
                                            stroke="hsl(var(--primary))"
                                            strokeWidth="2"
                                            strokeDasharray="5,3"
                                            style={{ pointerEvents: 'none' }}
                                          />
                                        )}
                                      </g>
                                    )
                                  })()}

                                  {/* Resize handle (always available for mire images) */}
                                  {selectedElements.includes(element.id) && shouldShowResizeHandle(element.id) && (
                                    <rect
                                      data-resize-handle="true"
                                      x={element.position.x + element.size.width - 5}
                                      y={element.position.y + element.size.height - 5}
                                      width={10}
                                      height={10}
                                      fill="white"
                                      stroke="#000"
                                      strokeWidth={1}
                                      style={{ cursor: 'se-resize', pointerEvents: 'all' }}
                                      onMouseDown={(e) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        if (readOnly) return
                                        setIsResizing(true)
                                        setResizedElementId(element.id)
                                        setInitialSize({ width: element.size.width, height: element.size.height })
                                        setInitialPosition({ x: element.position.x, y: element.position.y })
                                        setResizeStart(screenToSVG(e.clientX, e.clientY))
                                      }}
                                    />
                                  )}
                                </>
                              )}

                            </DraggableWorkspaceElement>
                          )
                        })}
                        {/* Définition des flèches */}
                        <defs>
                          {/* Flèche par défaut */}
                          <marker
                            id="arrowhead"
                            markerWidth="10"
                            markerHeight="7"
                            refX="9"
                            refY="3.5"
                            orient="auto"
                          >
                            <polygon
                              points="0 0, 10 3.5, 0 7"
                              fill="#2563EB"
                            />
                          </marker>

                          {/* Flèches pour chaque couleur de port (défaut) */}
                          {PORT_COLORS.map((color, index) => (
                            <marker
                              key={`arrow-port-${index + 1}`}
                              id={`arrowhead-port-${index + 1}`}
                              markerWidth="10"
                              markerHeight="7"
                              refX="9"
                              refY="3.5"
                              orient="auto"
                            >
                              <polygon
                                points="0 0, 10 3.5, 0 7"
                                fill={color}
                              />
                            </marker>
                          ))}
                          {/* Flèches dynamiques pour les couleurs de port personnalisées */}
                          {Object.entries(portColorOverrides).flatMap(([procId, ports]) =>
                            Object.entries(ports).map(([portNum, color]) => (
                              <marker
                                key={`arrow-override-${procId}-${portNum}`}
                                id={`arrowhead-override-${procId}-${portNum}`}
                                markerWidth="10"
                                markerHeight="7"
                                refX="9"
                                refY="3.5"
                                orient="auto"
                              >
                                <polygon points="0 0, 10 3.5, 0 7" fill={color} />
                              </marker>
                            ))
                          )}
                          {/* Flèches dynamiques pour les couleurs de prise personnalisées (Power) */}
                          {Object.entries(outletColorOverrides).flatMap(([cabId, outlets]) =>
                            Object.entries(outlets).map(([outletNum, color]) => (
                              <marker
                                key={`arrow-override-cab-${cabId}-${outletNum}`}
                                id={`arrowhead-override-cab-${cabId}-${outletNum}`}
                                markerWidth="10"
                                markerHeight="7"
                                refX="9"
                                refY="3.5"
                                orient="auto"
                              >
                                <polygon points="0 0, 10 3.5, 0 7" fill={color} />
                              </marker>
                            ))
                          )}
                        </defs>

                        {/* Câbles - Affichage conditionnel selon l'étape (masqués pendant drag pour performance) */}
                        {!dndWorkspace.activeId && cableRoutes
                          .filter(route => {
                            // Ignorer les câbles corrompus (points manquants/invalides) — jamais de crash au rendu
                            if (!hasValidCablePoints(route)) return false
                            // ✅ Valider les références : ignorer les câbles orphelins (copy-paste, undo, etc.)
                            const fromIsCell = route.from.includes('_cell_')
                            const toIsCell = route.to.includes('_cell_')

                            if (fromIsCell) {
                              const imageId = route.from.split('_cell_')[0]
                              if (!workspaceElements.some(el => el.id === imageId)) return false
                              // Vérifier qu'une connexion existe pour cette cellule
                              const connections = route.type === 'power' ? mirePowerConnections : mireConnections
                              const hasConn = connections.some((conn: any) => {
                                const cellId = `${conn.imageId}_cell_${conn.gridPosition.row}_${conn.gridPosition.col}`
                                return cellId === route.from
                              })
                              if (!hasConn) return false
                            } else {
                              if (!workspaceElements.some(el => el.id === route.from)) return false
                            }

                            if (toIsCell) {
                              const imageId = route.to.split('_cell_')[0]
                              if (!workspaceElements.some(el => el.id === imageId)) return false
                              const connections = route.type === 'power' ? mirePowerConnections : mireConnections
                              const hasConn = connections.some((conn: any) => {
                                const cellId = `${conn.imageId}_cell_${conn.gridPosition.row}_${conn.gridPosition.col}`
                                return cellId === route.to
                              })
                              if (!hasConn) return false
                            } else {
                              if (!workspaceElements.some(el => el.id === route.to)) return false
                            }

                            // Étape 2 (data) : afficher seulement les câbles Data
                            if (currentStep === 'data') {
                              if (route.type !== 'data') return false

                              // ✅ NE PAS afficher le câble solide Processeur → Premier Panneau/Cellule (order=1)
                              // Car il est déjà affiché en pointillés animé
                              const fromElement = workspaceElements.find(e => e.id === route.from)
                              if (fromElement && fromElement.type === 'processor') {
                                // Check regular panel connections
                                const panelConn = panelConnections.find(conn => conn.panelId === route.to && conn.processorId === route.from)
                                if (panelConn && panelConn.order === 1) {
                                  return false // Ne pas afficher le câble processeur → 1er panneau
                                }

                                // NEW: Check mire cell connections
                                const isMireCell = route.to.includes('_cell_')
                                if (isMireCell) {
                                  const mireConn = mireConnections.find(conn => {
                                    const cellId = `${conn.imageId}_cell_${conn.gridPosition.row}_${conn.gridPosition.col}`
                                    return cellId === route.to && conn.processorId === route.from && conn.order === 1
                                  })
                                  if (mireConn) {
                                    return false // Ne pas afficher le câble processeur → 1ère cellule mire
                                  }
                                }
                              }

                              return true
                            }
                            // Étape 3 (cabling/power) : afficher seulement les câbles Power
                            if (currentStep === 'cabling') {
                              if (route.type !== 'power') return false

                              // Ne pas afficher le câble solide entre armoire et premier panneau/cellule (order=1)
                              const fromElement = workspaceElements.find(e => e.id === route.from)
                              if (fromElement && fromElement.type === 'cabinet') {
                                // Check regular panel connections
                                const powerConn = powerConnections.find(conn => conn.panelId === route.to)
                                if (powerConn && powerConn.order === 1) {
                                  return false // Ne pas afficher le câble armoire → 1er panneau
                                }

                                // NEW: Check mire cell connections
                                const isMireCell = route.to.includes('_cell_')
                                if (isMireCell) {
                                  const mirePowerConn = mirePowerConnections.find(conn => {
                                    const cellId = `${conn.imageId}_cell_${conn.gridPosition.row}_${conn.gridPosition.col}`
                                    return cellId === route.to && conn.cabinetId === route.from && conn.order === 1
                                  })
                                  if (mirePowerConn) {
                                    return false // Ne pas afficher le câble armoire → 1ère cellule mire
                                  }
                                }
                              }

                              return true
                            }
                            // Étape 4 (export) : afficher selon le mode d'export actuel
                            if (currentStep === 'export') {
                              if (exportViewMode === 'data') {
                                if (route.type !== 'data') return false

                                // ✅ NE PAS afficher le câble solide Processeur → Premier Panneau/Cellule (order=1)
                                const fromElement = workspaceElements.find(e => e.id === route.from)
                                if (fromElement && fromElement.type === 'processor') {
                                  // Check regular panel connections
                                  const panelConn = panelConnections.find(conn => conn.panelId === route.to && conn.processorId === route.from)
                                  if (panelConn && panelConn.order === 1) {
                                    return false
                                  }

                                  // NEW: Check mire cell connections
                                  const isMireCell = route.to.includes('_cell_')
                                  if (isMireCell) {
                                    const mireConn = mireConnections.find(conn => {
                                      const cellId = `${conn.imageId}_cell_${conn.gridPosition.row}_${conn.gridPosition.col}`
                                      return cellId === route.to && conn.processorId === route.from && conn.order === 1
                                    })
                                    if (mireConn) {
                                      return false // Ne pas afficher le câble processeur → 1ère cellule mire
                                    }
                                  }
                                }

                                return true
                              } else if (exportViewMode === 'power') {
                                if (route.type !== 'power') return false

                                // Ne pas afficher le câble solide entre armoire et premier panneau/cellule (order=1)
                                const fromElement = workspaceElements.find(e => e.id === route.from)
                                if (fromElement && fromElement.type === 'cabinet') {
                                  // Check regular panel connections
                                  const powerConn = powerConnections.find(conn => conn.panelId === route.to)
                                  if (powerConn && powerConn.order === 1) {
                                    return false
                                  }

                                  // NEW: Check mire cell connections
                                  const isMireCell = route.to.includes('_cell_')
                                  if (isMireCell) {
                                    const mirePowerConn = mirePowerConnections.find(conn => {
                                      const cellId = `${conn.imageId}_cell_${conn.gridPosition.row}_${conn.gridPosition.col}`
                                      return cellId === route.to && conn.cabinetId === route.from && conn.order === 1
                                    })
                                    if (mirePowerConn) {
                                      return false // Ne pas afficher le câble armoire → 1ère cellule mire
                                    }
                                  }
                                }

                                return true
                              }
                            }
                            // Autres étapes : ne rien afficher
                            return false
                          })
                          .map((route) => {
                            let cableColor = "#2563EB" // Couleur par défaut
                            let markerEnd = "url(#arrowhead)"

                            // Pour les câbles Power, dériver la couleur depuis la connexion (plus fiable que route.color)
                            if (route.type === 'power') {
                              const powerConn = powerConnections.find(conn => conn.panelId === route.to)
                              if (powerConn) {
                                const cabinetId = powerConn.cabinetId
                                const outletNum = powerConn.outlet
                                cableColor = getOutletColor(outletNum, cabinetId)
                                const hasOutletOverride = cabinetId && outletColorOverrides[cabinetId]?.[outletNum]
                                markerEnd = hasOutletOverride
                                  ? `url(#arrowhead-override-cab-${cabinetId}-${outletNum})`
                                  : `url(#arrowhead-port-${outletNum})`
                              } else {
                                // Fallback: chercher via route.from (pour câbles mire)
                                const mirePowerConn = mirePowerConnections.find(conn => {
                                  const cellId = `${conn.imageId}_cell_${conn.gridPosition.row}_${conn.gridPosition.col}`
                                  return cellId === route.to || cellId === route.from
                                })
                                if (mirePowerConn) {
                                  const cabId = mirePowerConn.cabinetId
                                  const outletNum = mirePowerConn.outlet
                                  cableColor = getOutletColor(outletNum, cabId)
                                  const hasOutletOverride = cabId && outletColorOverrides[cabId]?.[outletNum]
                                  markerEnd = hasOutletOverride
                                    ? `url(#arrowhead-override-cab-${cabId}-${outletNum})`
                                    : `url(#arrowhead-port-${outletNum})`
                                } else {
                                  cableColor = route.color || cableColor
                                }
                              }
                            } else {
                              // Pour les câbles Data, chercher le port associé via les panelConnections ou mireConnections
                              const connectionFrom = panelConnections.find(conn => conn.panelId === route.from || conn.processorId === route.from)
                              const connectionTo = panelConnections.find(conn => conn.panelId === route.to || conn.processorId === route.to)

                              // NEW: Check mire connections for port color
                              let portNumber = connectionFrom?.port || connectionTo?.port
                              let orderNumber = 0

                              // If not found in panel connections, check mire connections
                              if (!portNumber) {
                                const isMireCellFrom = route.from.includes('_cell_')
                                const isMireCellTo = route.to.includes('_cell_')

                                if (isMireCellFrom || isMireCellTo) {
                                  const mireConn = mireConnections.find(conn => {
                                    const cellId = `${conn.imageId}_cell_${conn.gridPosition.row}_${conn.gridPosition.col}`
                                    return cellId === route.from || cellId === route.to
                                  })

                                  if (mireConn) {
                                    portNumber = mireConn.port
                                    orderNumber = mireConn.order
                                  }
                                }
                              }

                              // Utiliser le port de la connexion trouvée (inclure processorId depuis mireConnections)
                              let cableProcessorId = connectionFrom?.processorId || connectionTo?.processorId || null
                              if (!cableProcessorId && portNumber) {
                                const mireConn = mireConnections.find(conn => {
                                  const cellId = `${conn.imageId}_cell_${conn.gridPosition.row}_${conn.gridPosition.col}`
                                  return cellId === route.from || cellId === route.to
                                })
                                if (mireConn) cableProcessorId = mireConn.processorId
                              }
                              if (portNumber) {
                                cableColor = getPortColor(portNumber, cableProcessorId)
                                // Utiliser le marker de couleur personnalisée si disponible
                                const hasColorOverride = cableProcessorId && portColorOverrides[cableProcessorId]?.[portNumber]
                                const markerSuffix = hasColorOverride
                                  ? `override-${cableProcessorId}-${portNumber}`
                                  : `port-${portNumber}`
                                // NEW: Only show arrow every other connection (1 sur 2)
                                if (orderNumber > 0 && orderNumber % 2 === 0) {
                                  markerEnd = `url(#arrowhead-${markerSuffix})`
                                } else if (orderNumber === 0) {
                                  // For non-mire connections, always show arrow (backward compatibility)
                                  markerEnd = `url(#arrowhead-${markerSuffix})`
                                } else {
                                  markerEnd = "none"
                                }
                              }
                            }

                            // NEW: Calculate adaptive strokeWidth for mire cell cables
                            let strokeWidth = 2 // Default for normal panels
                            const isMireCableFrom = route.from.includes('_cell_')
                            const isMireCableTo = route.to.includes('_cell_')

                            if (isMireCableFrom || isMireCableTo) {
                              // Extract imageId from the cell ID (format: imageId_cell_row_col)
                              const cellId = isMireCableFrom ? route.from : route.to
                              const imageId = cellId.split('_cell_')[0]

                              // Find the mire image element
                              const mireImage = workspaceElements.find(e => e.id === imageId && e.type === 'image')

                              if (mireImage && mireImage.data?.mireConfig?.ledWallMode) {
                                const config = mireImage.data.mireConfig
                                const { columns, rows } = config.ledWallMode!
                                const cellWidth = mireImage.size.width / columns
                                const cellHeight = mireImage.size.height / rows
                                const avgCellSize = (cellWidth + cellHeight) / 2

                                // Adaptive strokeWidth: 5% of average cell size (min 1, max 6)
                                strokeWidth = Math.max(1, Math.min(6, avgCellSize * 0.05))
                              }
                            }

                            // Helper: centre d'une cellule mire (pour calcul de direction)
                            const getMireCellCenter = (cellId: string): { x: number, y: number, cw: number, ch: number } | null => {
                              const parts = cellId.split('_cell_')
                              if (parts.length !== 2) return null
                              const imageId = parts[0]
                              const [rowStr, colStr] = parts[1].split('_')
                              const row = parseInt(rowStr), col = parseInt(colStr)
                              const img = workspaceElements.find(e => e.id === imageId && e.type === 'image')
                              if (!img || !img.data?.mireConfig?.ledWallMode) return null
                              const { columns, rows: totalRows } = img.data.mireConfig.ledWallMode
                              const cw = img.size.width / columns
                              const ch = img.size.height / totalRows
                              return {
                                x: img.position.x + (col - 1) * cw + cw / 2,
                                y: img.position.y + (row - 1) * ch + ch / 2,
                                cw, ch
                              }
                            }

                            // Helper: bord d'une cellule mire dans la direction (dx,dy)
                            const getMireCellEdge = (cellId: string, dirX: number, dirY: number): { x: number, y: number } | null => {
                              const c = getMireCellCenter(cellId)
                              if (!c) return null
                              if (Math.abs(dirX) >= Math.abs(dirY)) {
                                return { x: dirX > 0 ? c.x + c.cw / 2 : c.x - c.cw / 2, y: c.y }
                              } else {
                                return { x: c.x, y: dirY > 0 ? c.y + c.ch / 2 : c.y - c.ch / 2 }
                              }
                            }

                            // Calcul des points d'ancrage : live pour panneaux et cellules mire
                            let x1 = route.points[0].x, y1 = route.points[0].y
                            let x2 = route.points[1].x, y2 = route.points[1].y

                            // Recalculer depuis positions live : centre pour cellules mire
                            if (isMireCableFrom) {
                              const center = getMireCellCenter(route.from)
                              if (center) { x1 = center.x; y1 = center.y }
                            }
                            if (isMireCableTo) {
                              const center = getMireCellCenter(route.to)
                              if (center) {
                                // Dernière cellule de la chaîne → arrivée au bord d'entrée
                                const isLastInChain = !cableRoutes.some(r => r.from === route.to && r.type === route.type)
                                if (isLastInChain) {
                                  const dx = center.x - x1, dy = center.y - y1
                                  const edge = getMireCellEdge(route.to, -dx, -dy)
                                  if (edge) { x2 = edge.x; y2 = edge.y }
                                  else { x2 = center.x; y2 = center.y }
                                } else {
                                  x2 = center.x; y2 = center.y
                                }
                              }
                            }

                            const fromEl = !isMireCableFrom ? workspaceElements.find(e => e.id === route.from) : undefined
                            const toEl = !isMireCableTo ? workspaceElements.find(e => e.id === route.to) : undefined
                            const fromIsPanel = fromEl?.type === 'panel'
                            const toIsPanel = toEl?.type === 'panel'

                            if (fromIsPanel || toIsPanel) {
                              // Direction entre les deux extrémités
                              const refFromX = fromEl ? fromEl.position.x + fromEl.size.width / 2 : x1
                              const refFromY = fromEl ? fromEl.position.y + fromEl.size.height / 2 : y1
                              const refToX = toEl ? toEl.position.x + toEl.size.width / 2 : x2
                              const refToY = toEl ? toEl.position.y + toEl.size.height / 2 : y2
                              const dx = refToX - refFromX, dy = refToY - refFromY
                              const dist = Math.sqrt(dx * dx + dy * dy) || 1

                              if (fromIsPanel) {
                                if (Math.abs(dx) >= Math.abs(dy)) {
                                  // Côté gauche ou droit : décaler Y le long du côté vers la destination
                                  const sideOffset = (dy / dist) * fromEl!.size.height * 0.25
                                  x1 = dx > 0 ? fromEl!.position.x + fromEl!.size.width : fromEl!.position.x
                                  y1 = refFromY + sideOffset
                                } else {
                                  // Côté haut ou bas : décaler X le long du côté vers la destination
                                  const sideOffset = (dx / dist) * fromEl!.size.width * 0.25
                                  x1 = refFromX + sideOffset
                                  y1 = dy > 0 ? fromEl!.position.y + fromEl!.size.height : fromEl!.position.y
                                }
                              }
                              if (toIsPanel) {
                                if (Math.abs(dx) >= Math.abs(dy)) {
                                  // Côté gauche ou droit : décaler Y le long du côté vers la destination
                                  const sideOffset = (dy / dist) * toEl!.size.height * 0.25
                                  x2 = dx > 0 ? toEl!.position.x : toEl!.position.x + toEl!.size.width
                                  y2 = refToY + sideOffset
                                } else {
                                  // Côté haut ou bas : décaler X le long du côté vers la destination
                                  const sideOffset = (dx / dist) * toEl!.size.width * 0.25
                                  x2 = refToX + sideOffset
                                  y2 = dy > 0 ? toEl!.position.y : toEl!.position.y + toEl!.size.height
                                }
                              }
                            }

                            // Détection mire et dernière cellule
                            const isMireCable = isMireCableFrom || isMireCableTo
                            const isLastMireCable = isMireCable && isMireCableTo && !cableRoutes.some(r => r.from === route.to && r.type === route.type)

                            // Liaison armoire → armoire : les deux extrémités sont des armoires
                            const isCabinetLink = route.type === 'power' && !isMireCable &&
                              fromEl?.type === 'cabinet' && toEl?.type === 'cabinet'
                            if (isCabinetLink) {
                              cableColor = route.color || cableColor
                              strokeWidth = 3
                            }

                            // Calculs communs pour les flèches
                            const mx = (x1 + x2) / 2, my = (y1 + y2) / 2
                            const angle = Math.atan2(y2 - y1, x2 - x1)

                            // Flèche non-mire (inchangé)
                            const hasNonMireArrow = markerEnd !== "none" && !isMireCable
                            const nonMireArrowLen = strokeWidth * 3
                            const nonMireArrowWid = strokeWidth * 2
                            const nonMireArrowPts = hasNonMireArrow ? `${mx + nonMireArrowLen * Math.cos(angle)},${my + nonMireArrowLen * Math.sin(angle)} ${mx - nonMireArrowLen * 0.5 * Math.cos(angle) + nonMireArrowWid * Math.sin(angle)},${my - nonMireArrowLen * 0.5 * Math.sin(angle) - nonMireArrowWid * Math.cos(angle)} ${mx - nonMireArrowLen * 0.5 * Math.cos(angle) - nonMireArrowWid * Math.sin(angle)},${my - nonMireArrowLen * 0.5 * Math.sin(angle) + nonMireArrowWid * Math.cos(angle)}` : ''

                            // Flèches mire : centrées au milieu (bordure entre cellules)
                            const mireArrowLen = strokeWidth * 3.75
                            const mireArrowWid = strokeWidth * 2.5
                            let mireArrowPts = ''
                            let mireLastArrowPts = ''
                            if (isMireCable) {
                              if (!isLastMireCable) {
                                // Flèche au milieu du segment (à cheval sur la bordure entre 2 cellules)
                                mireArrowPts = `${mx + mireArrowLen * Math.cos(angle)},${my + mireArrowLen * Math.sin(angle)} ${mx - mireArrowLen * 0.5 * Math.cos(angle) + mireArrowWid * Math.sin(angle)},${my - mireArrowLen * 0.5 * Math.sin(angle) - mireArrowWid * Math.cos(angle)} ${mx - mireArrowLen * 0.5 * Math.cos(angle) - mireArrowWid * Math.sin(angle)},${my - mireArrowLen * 0.5 * Math.sin(angle) + mireArrowWid * Math.cos(angle)}`
                              }
                              // Flèche finale sur la dernière cellule (avancée dans le panneau)
                              if (isLastMireCable) {
                                const pushIn = mireArrowLen * 0.6
                                const tipX = x2 + pushIn * Math.cos(angle)
                                const tipY = y2 + pushIn * Math.sin(angle)
                                mireLastArrowPts = `${tipX},${tipY} ${tipX - mireArrowLen * Math.cos(angle) + mireArrowWid * Math.sin(angle)},${tipY - mireArrowLen * Math.sin(angle) - mireArrowWid * Math.cos(angle)} ${tipX - mireArrowLen * Math.cos(angle) - mireArrowWid * Math.sin(angle)},${tipY - mireArrowLen * Math.sin(angle) + mireArrowWid * Math.cos(angle)}`
                              }
                            }

                            return (
                              <g key={route.id}>
                                <line
                                  x1={x1} y1={y1} x2={x2} y2={y2}
                                  stroke={cableColor}
                                  strokeWidth={strokeWidth}
                                  fill="none"
                                />
                                {hasNonMireArrow && (
                                  <polygon points={nonMireArrowPts} fill={cableColor} />
                                )}
                                {isMireCable && mireArrowPts && (
                                  <polygon points={mireArrowPts} fill={cableColor} />
                                )}
                                {isLastMireCable && mireLastArrowPts && (
                                  <polygon points={mireLastArrowPts} fill={cableColor} />
                                )}
                                {isCabinetLink && (() => {
                                  const linkLoadW = cabinetLoadInfo.total.get(route.to) || 0
                                  const sourceEl = workspaceElements.find(e => e.id === route.from)
                                  const sourceMaxW = sourceEl?.type === 'cabinet' ? (sourceEl.data as PowerCabinet).maxPower : 0
                                  const sourceTotalW = cabinetLoadInfo.total.get(route.from) || 0
                                  const overloaded = sourceMaxW > 0 && sourceTotalW > sourceMaxW
                                  const kw = (linkLoadW / 1000).toFixed(1)
                                  const label = `${kw} kW`
                                  return (
                                    <g transform={`translate(${mx}, ${my - 10})`} style={{ pointerEvents: 'none' }}>
                                      <rect
                                        x={-24} y={-8} width={48} height={16} rx={4}
                                        fill={overloaded ? '#DC2626' : '#1F2937'}
                                        stroke={cableColor} strokeWidth={1}
                                      />
                                      <text textAnchor="middle" dominantBaseline="central" y={0}
                                        fontSize={9} fontWeight={600} fill="#FFFFFF">
                                        {label}
                                      </text>
                                    </g>
                                  )
                                })()}
                              </g>
                            )
                          })}

                        {/* Lignes de liaison Processeur → Premier Panneau (masqué pendant drag pour performance) */}
                        {!dndWorkspace.activeId && showProcessorToFirstPanelLinks && (currentStep === 'data' || (currentStep === 'export' && exportViewMode === 'data')) && (() => {
                          // Récupérer tous les processeurs
                          const processors = workspaceElements.filter(e => e.type === 'processor')

                          return processors.map(processor => {
                            // Pour chaque port du processeur, trouver le premier panneau connecté
                            const processorData = processor.data as LEDProcessor
                            const links: React.ReactElement[] = []

                            const numPorts = typeof processorData.outputPorts === 'number' ? processorData.outputPorts : 0
                            for (let portNum = 1; portNum <= numPorts; portNum++) {
                              // Trouver le premier panneau de ce port (order = 1)
                              const firstConnection = panelConnections.find(
                                conn => conn.processorId === processor.id && conn.port === portNum && conn.order === 1
                              )

                              if (firstConnection) {
                                const firstPanel = workspaceElements.find(e => e.id === firstConnection.panelId)

                                if (firstPanel) {
                                  const portColor = getPortColor(portNum, processor.id)

                                  // Position du premier panneau (centre) pour le calcul initial
                                  const panelCenterX = firstPanel.position.x + firstPanel.size.width / 2
                                  const panelCenterY = firstPanel.position.y + firstPanel.size.height / 2

                                  // Calculer le meilleur point de connexion sur le processeur
                                  const connectionPoint = getBestConnectionPoint(processor, panelCenterX, panelCenterY)

                                  // Milieu du côté du panneau le plus proche du processeur
                                  const panelEdgePoint = getPanelMidSidePoint(connectionPoint.x, connectionPoint.y, firstPanel)

                                  // Générer un ID unique pour l'animation
                                  const linkId = `link-${processor.id}-${portNum}`

                                  // Utiliser CableElement optimisé
                                  links.push(
                                    <CableElement
                                      key={`proc-link-${processor.id}-port-${portNum}`}
                                      x1={connectionPoint.x}
                                      y1={connectionPoint.y}
                                      x2={panelEdgePoint.x}
                                      y2={panelEdgePoint.y}
                                      color={portColor}
                                      linkId={linkId}
                                      type="data"
                                    />
                                  )
                                }
                              }
                            }

                            return links
                          }).flat()
                        })()}

                        {/* Lignes de connexion entre processeurs Main-Backup */}
                        {!dndWorkspace.activeId && (currentStep === 'data' || (currentStep === 'export' && exportViewMode === 'data')) && (() => {
                          const connections: React.ReactElement[] = []
                          
                          Object.entries(backupProcessors).forEach(([primaryId, backupId]) => {
                            const primaryProcessor = workspaceElements.find(e => e.id === primaryId && e.type === 'processor')
                            const backupProcessor = workspaceElements.find(e => e.id === backupId && e.type === 'processor')
                            
                            if (primaryProcessor && backupProcessor) {
                              const primaryBounds = (primaryProcessor.data as LEDProcessor).image
                                ? getActualImageBounds(primaryProcessor)
                                : {
                                    x: primaryProcessor.position.x,
                                    y: primaryProcessor.position.y,
                                    width: primaryProcessor.size.width,
                                    height: primaryProcessor.size.height
                                  }
                              
                              const backupBounds = (backupProcessor.data as LEDProcessor).image
                                ? getActualImageBounds(backupProcessor)
                                : {
                                    x: backupProcessor.position.x,
                                    y: backupProcessor.position.y,
                                    width: backupProcessor.size.width,
                                    height: backupProcessor.size.height
                                  }
                              
                              // Points de connexion : centres des processeurs
                              const x1 = primaryBounds.x + primaryBounds.width / 2
                              const y1 = primaryBounds.y + primaryBounds.height / 2
                              const x2 = backupBounds.x + backupBounds.width / 2
                              const y2 = backupBounds.y + backupBounds.height / 2
                              
                              connections.push(
                                <line
                                  key={`backup-link-${primaryId}-${backupId}`}
                                  x1={x1}
                                  y1={y1}
                                  x2={x2}
                                  y2={y2}
                                  stroke="hsl(var(--primary))"
                                  strokeWidth="2"
                                  strokeDasharray="4,4"
                                  opacity="0.6"
                                  pointerEvents="none"
                                />
                              )
                            }
                          })
                          
                          return connections
                        })()}

                        {/* NEW: Lignes de liaison Processeur → Première Cellule Mire (pointillés animés) */}
                        {!dndWorkspace.activeId && showProcessorToFirstPanelLinks && (currentStep === 'data' || (currentStep === 'export' && exportViewMode === 'data')) && (() => {
                          const processors = workspaceElements.filter(e => e.type === 'processor')

                          return processors.map(processor => {
                            const processorData = processor.data as LEDProcessor
                            const links: React.ReactElement[] = []

                            const numPorts = typeof processorData.outputPorts === 'number' ? processorData.outputPorts : 0
                            for (let portNum = 1; portNum <= numPorts; portNum++) {
                              // Trouver la première cellule mire de ce port (order = 1)
                              const firstMireConnection = mireConnections.find(
                                conn => conn.processorId === processor.id && conn.port === portNum && conn.order === 1
                              )

                              if (firstMireConnection) {
                                // Trouver l'image mire
                                const mireImage = workspaceElements.find(e => e.id === firstMireConnection.imageId && e.type === 'image')

                                if (mireImage) {
                                  // Calculer la position de la première cellule
                                  const cell = getVirtualCell(mireImage, firstMireConnection.gridPosition.row, firstMireConnection.gridPosition.col)

                                  if (cell) {
                                    const portColor = getPortColor(portNum, processor.id)

                                    // Calculer le meilleur point de connexion sur le processeur
                                    const connectionPoint = getBestConnectionPoint(processor, cell.centerX, cell.centerY)

                                    // Générer un ID unique pour l'animation
                                    const linkId = `mire-link-${processor.id}-${portNum}`

                                    // Ligne pointillée animée vers la première cellule mire
                                    links.push(
                                      <CableElement
                                        key={`proc-mire-link-${processor.id}-port-${portNum}`}
                                        x1={connectionPoint.x}
                                        y1={connectionPoint.y}
                                        x2={cell.centerX}
                                        y2={cell.centerY}
                                        color={portColor}
                                        linkId={linkId}
                                        type="data"
                                      />
                                    )
                                  }
                                }
                              }
                            }

                            return links
                          }).flat()
                        })()}

                        {/* Lignes de liaison Armoire → Premier Panneau (masqué pendant drag pour performance) */}
                        {!dndWorkspace.activeId && showCabinetToFirstPanelLinks && (currentStep === 'cabling' || (currentStep === 'export' && exportViewMode === 'power')) && (() => {
                          const cabinets = selectedCabinetForConnection
                            ? workspaceElements.filter(e => e.type === 'cabinet' && e.id === selectedCabinetForConnection)
                            : workspaceElements.filter(e => e.type === 'cabinet')

                          return cabinets.map(cab => {
                            const cabinetData = cab.data as PowerCabinet
                            const links: React.ReactElement[] = []
                            for (let outlet = 1; outlet <= (cabinetData?.outlets || 12); outlet++) {
                              const firstPower = powerConnections.find(conn => conn.cabinetId === cab.id && conn.outlet === outlet && conn.order === 1)
                              if (!firstPower) continue
                              const panel = workspaceElements.find(e => e.id === firstPower.panelId)
                              if (!panel) continue
                              const color = getOutletColor(outlet, cab.id)
                              const pcx = panel.position.x + panel.size.width / 2
                              const pcy = panel.position.y + panel.size.height / 2
                              const start = getBestCabinetPoint(cab, pcx, pcy)
                              const end = getPanelMidSidePoint(start.x, start.y, panel)
                              const lid = `plink-${cab.id}-${outlet}`
                              // Utiliser CableElement optimisé
                              links.push(
                                <CableElement
                                  key={`cab-link-${cab.id}-outlet-${outlet}`}
                                  x1={start.x}
                                  y1={start.y}
                                  x2={end.x}
                                  y2={end.y}
                                  color={color}
                                  linkId={lid}
                                  type="power"
                                />
                              )
                            }
                            return links
                          }).flat()
                        })()}

                        {/* NEW: Lignes de liaison Armoire → Première Cellule Mire (pointillés animés) */}
                        {!dndWorkspace.activeId && showCabinetToFirstPanelLinks && (currentStep === 'cabling' || (currentStep === 'export' && exportViewMode === 'power')) && (() => {
                          const cabinets = selectedCabinetForConnection
                            ? workspaceElements.filter(e => e.type === 'cabinet' && e.id === selectedCabinetForConnection)
                            : workspaceElements.filter(e => e.type === 'cabinet')

                          return cabinets.map(cab => {
                            const cabinetData = cab.data as PowerCabinet
                            const links: React.ReactElement[] = []

                            for (let outlet = 1; outlet <= (cabinetData?.outlets || 12); outlet++) {
                              // Trouver la première cellule mire de cette sortie (order = 1)
                              const firstMirePowerConnection = mirePowerConnections.find(
                                conn => conn.cabinetId === cab.id && conn.outlet === outlet && conn.order === 1
                              )

                              if (firstMirePowerConnection) {
                                // Trouver l'image mire
                                const mireImage = workspaceElements.find(e => e.id === firstMirePowerConnection.imageId && e.type === 'image')

                                if (mireImage) {
                                  // Calculer la position de la première cellule
                                  const cell = getVirtualCell(mireImage, firstMirePowerConnection.gridPosition.row, firstMirePowerConnection.gridPosition.col)

                                  if (cell) {
                                    const outletColor = getOutletColor(outlet, cab.id)

                                    // Calculer le meilleur point de connexion sur l'armoire
                                    const start = getBestCabinetPoint(cab, cell.centerX, cell.centerY)

                                    // Générer un ID unique pour l'animation
                                    const linkId = `mire-plink-${cab.id}-${outlet}`

                                    // Ligne pointillée animée vers la première cellule mire
                                    links.push(
                                      <CableElement
                                        key={`cab-mire-link-${cab.id}-outlet-${outlet}`}
                                        x1={start.x}
                                        y1={start.y}
                                        x2={cell.centerX}
                                        y2={cell.centerY}
                                        color={outletColor}
                                        linkId={linkId}
                                        type="power"
                                      />
                                    )
                                  }
                                }
                              }
                            }

                            return links
                          }).flat()
                        })()}

                        {/* Overlay PX/numéros - rendu APRÈS les câbles pour apparaître au-dessus */}
                        {(currentStep === 'data' || currentStep === 'cabling' || currentStep === 'export') && workspaceElements
                          .filter(el => el.type === 'image' && el.data?.mireConfig?.ledWallMode)
                          .map(element => {
                            const config = element.data.mireConfig
                            if (!config?.ledWallMode) return null
                            const { columns, rows } = config.ledWallMode
                            const cellWidth = element.size.width / columns
                            const cellHeight = element.size.height / rows
                            const mode = currentStep === 'cabling' || (currentStep === 'export' && exportViewMode === 'power') ? 'power' : 'data'

                            return (
                              <g key={`px-overlay-${element.id}`} style={{ pointerEvents: 'none' }}>
                                {Array.from({ length: rows }, (_, ri) => {
                                  const row = ri + 1
                                  return Array.from({ length: columns }, (_, ci) => {
                                    const col = ci + 1
                                    const x = element.position.x + (col - 1) * cellWidth
                                    const y = element.position.y + (ri) * cellHeight

                                    let connection: MireConnection | MirePowerConnection | null = null
                                    if (mode === 'data') {
                                      connection = mireConnections.find(c => c.imageId === element.id && c.gridPosition.row === row && c.gridPosition.col === col) || null
                                    } else {
                                      connection = mirePowerConnections.find(c => c.imageId === element.id && c.gridPosition.row === row && c.gridPosition.col === col) || null
                                    }
                                    if (!connection) return null

                                    // Déterminer couleur et badges
                                    let cellColor = mode === 'data'
                                      ? getPortColor((connection as MireConnection).port, (connection as MireConnection).processorId)
                                      : getOutletColor((connection as MirePowerConnection).outlet, (connection as MirePowerConnection).cabinetId)
                                    const portOrOutlet = mode === 'data' ? (connection as MireConnection).port : (connection as MirePowerConnection).outlet

                                    // First cell detection
                                    let isFirstCell = false
                                    let isLastCell = false
                                    let hasBackup: number | null = null
                                    if (mode === 'data') {
                                      const allConns = mireConnections.filter(c => c.imageId === element.id && c.port === (connection as MireConnection).port && c.processorId === (connection as MireConnection).processorId).sort((a, b) => a.order - b.order)
                                      isFirstCell = allConns[0]?.gridPosition.row === row && allConns[0]?.gridPosition.col === col
                                      isLastCell = allConns[allConns.length - 1]?.gridPosition.row === row && allConns[allConns.length - 1]?.gridPosition.col === col
                                    } else {
                                      const allConns = mirePowerConnections.filter(c => c.imageId === element.id && c.outlet === (connection as MirePowerConnection).outlet && c.cabinetId === (connection as MirePowerConnection).cabinetId).sort((a, b) => a.order - b.order)
                                      isFirstCell = allConns[0]?.gridPosition.row === row && allConns[0]?.gridPosition.col === col
                                      isLastCell = allConns[allConns.length - 1]?.gridPosition.row === row && allConns[allConns.length - 1]?.gridPosition.col === col
                                    }

                                    // Number position
                                    const cellId = `${element.id}_cell_${row}_${col}`
                                    const cables = cableRoutes.filter(c => (c.from === cellId || c.to === cellId) && hasValidCablePoints(c))
                                    let numberX = x + cellWidth / 2, numberY = y + cellHeight / 2
                                    let textAnchor: 'start' | 'middle' | 'end' = 'middle'
                                    if (cables.length > 0) {
                                      const directions = { top: false, bottom: false, left: false, right: false }
                                      cables.forEach(cable => {
                                        const isIncoming = cable.to === cellId
                                        const otherPoint = isIncoming ? cable.points[0] : cable.points[1]
                                        const cx = x + cellWidth / 2, cy = y + cellHeight / 2
                                        const dx = otherPoint.x - cx, dy = otherPoint.y - cy
                                        if (Math.abs(dx) > Math.abs(dy)) { if (dx > 0) directions.right = true; else directions.left = true }
                                        else { if (dy > 0) directions.bottom = true; else directions.top = true }
                                      })
                                      const corners = [
                                        { blocked: directions.top || directions.left, x: x + 8, y: y + 12, anchor: 'start' as const },
                                        { blocked: directions.top || directions.right, x: x + cellWidth - 8, y: y + 12, anchor: 'end' as const },
                                        { blocked: directions.bottom || directions.left, x: x + 8, y: y + cellHeight - 8, anchor: 'start' as const },
                                        { blocked: directions.bottom || directions.right, x: x + cellWidth - 8, y: y + cellHeight - 8, anchor: 'end' as const }
                                      ]
                                      const best = corners.find(c => !c.blocked) || corners[0]
                                      numberX = best.x; numberY = best.y; textAnchor = best.anchor
                                    }

                                    const mireNumberingMode = element.data?.mireNumberingMode || 'all'
                                    const showThisNumber = mireNumberingMode === 'all' || (mireNumberingMode === 'first-last' && (isFirstCell || isLastCell))

                                    return (
                                      <g key={`px-cell-${row}-${col}`}>
                                        {isFirstCell && mireNumberingMode !== 'none' && (
                                          <>
                                            <rect x={x} y={y} width={cellWidth} height={cellHeight} fill={cellColor} fillOpacity="1" rx="2" />
                                            <text x={x + cellWidth / 2} y={y + cellHeight / 2} fontSize={Math.max(10, Math.min(14, cellWidth * 0.3))} fontWeight="bold" fill="white" textAnchor="middle" dominantBaseline="central">
                                              {mode === 'data' ? `P${portOrOutlet}` : `PC${portOrOutlet}`}
                                            </text>
                                          </>
                                        )}
                                        {isLastCell && hasBackup && mode === 'data' && mireNumberingMode !== 'none' && (
                                          <>
                                            <rect x={x} y={y} width={cellWidth} height={cellHeight} fill={getPortColor(hasBackup)} fillOpacity="1" rx="2" />
                                            <text x={x + cellWidth / 2} y={y + cellHeight / 2} fontSize={Math.max(10, Math.min(14, cellWidth * 0.3))} fontWeight="bold" fill="white" textAnchor="middle" dominantBaseline="central">R{hasBackup}</text>
                                          </>
                                        )}
                                        {showThisNumber && (
                                          <text x={numberX} y={numberY} fill={cellColor} fontSize={Math.max(12, Math.min(16, cellWidth * 0.27))} fontWeight="bold" textAnchor={textAnchor} dominantBaseline="middle" style={{ textShadow: '0 0 3px rgba(0,0,0,0.8), 0 0 6px rgba(0,0,0,0.6)' }}>
                                            {connection.order}
                                          </text>
                                        )}
                                      </g>
                                    )
                                  })
                                })}
                              </g>
                            )
                          })
                        }

                        {/* Contours des groupes */}
                        {Object.entries(elementGroups).map(([groupId, group]) => {
                          // Ne pas afficher les contours si le groupe est temporairement dégroupé
                          if ((group as any).isTemporarilyUngrouped) return null

                          // Ne pas afficher le contour du groupe si c'est juste une image de mire seule
                          if (group.elementIds.length === 1) {
                            const element = workspaceElements.find(e => e.id === group.elementIds[0])
                            if (element && element.type === 'image' && (element.data?.isPendingConversion || element.data?.isConverted)) {
                              return null
                            }
                          }

                          const bounds = getGroupBounds(groupId)
                          if (!bounds) return null

                          // Vérifier si au moins un élément du groupe est sélectionné
                          const isGroupSelected = group.elementIds.some(id => selectedElements.includes(id))

                          // Vérifier si le groupe est en cours de déplacement
                          const isGroupBeingDragged = group.elementIds.some(id =>
                            id === dndWorkspace.activeId || dndWorkspace.draggedElements.includes(id)
                          )

                          // Appliquer le transform si le groupe est déplacé
                          const groupTransform = isGroupBeingDragged && (dndWorkspace.currentDelta.x !== 0 || dndWorkspace.currentDelta.y !== 0)
                            ? `translate(${dndWorkspace.currentDelta.x}, ${dndWorkspace.currentDelta.y})`
                            : undefined

                          return (
                            <g key={`group-outline-${groupId}`} transform={groupTransform}>
                              {/* Contour du groupe */}
                              <rect
                                x={bounds.x}
                                y={bounds.y}
                                width={bounds.width}
                                height={bounds.height}
                                fill={isGroupSelected ? "hsl(var(--primary) / 0.1)" : "none"}
                                stroke={isGroupSelected ? "hsl(var(--primary))" : "#2D2D2D"}
                                strokeWidth={isGroupSelected ? "4" : "3"}
                                strokeDasharray={isGroupSelected ? "5,3" : (group.permanent ? "none" : "10,5")}
                                pointerEvents="none"
                                opacity={isGroupSelected ? "1" : "0.8"}
                              />

                              {/* Handle de redimensionnement du groupe (coin bas-droit) */}
                              {isGroupSelected && (
                                <circle
                                  cx={bounds.x + bounds.width}
                                  cy={bounds.y + bounds.height}
                                  r="8"
                                  fill="hsl(var(--primary))"
                                  stroke="white"
                                  strokeWidth="2"
                                  style={{ cursor: 'nwse-resize' }}
                                  onMouseDown={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    if (readOnly) return

                                    // Trouver le premier élément du groupe
                                    const firstElement = workspaceElements.find(el => el.id === group.elementIds[0])
                                    if (!firstElement) return

                                    setIsResizing(true)
                                    setResizedElementId(firstElement.id)
                                    setInitialSize({ width: firstElement.size.width, height: firstElement.size.height })
                                    setInitialPosition({ x: firstElement.position.x, y: firstElement.position.y })
                                    setResizeStart(screenToSVG(e.clientX, e.clientY))

                                    // Configuration pour le resize de groupe
                                    setResizingGroupId(groupId)
                                    setInitialGroupBounds(bounds)
                                    const groupElements = workspaceElements
                                      .filter(elem => group.elementIds.includes(elem.id))
                                      .map(elem => ({
                                        id: elem.id,
                                        position: { ...elem.position },
                                        size: { ...elem.size }
                                      }))
                                    setInitialGroupElements(groupElements)
                                  }}
                                />
                              )}
                            </g>
                          )
                        })}
                      </svg>

                      {workspaceElements.length === 0 && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center text-muted-foreground">
                            <Grid3X3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                            <p className="text-lg font-medium">{t('workspace.empty')}</p>
                            <p className="text-sm">{t('workspace.startAddingProcessors')}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    <DragOverlay dropAnimation={null}>
                      {null}
                    </DragOverlay>
                  </DndContext>

                  {/* Tauri Drag Overlays (extracted to components/TauriDragOverlay.tsx) */}
                  <TauriDragOverlay
                    draggedItem={draggedItem}
                    dragMousePosition={dragMousePosition}
                    getProcessorImageUrl={getProcessorImageUrl}
                    isDraggingBackupButton={isDraggingBackupButton}
                    backupButtonDragMousePosition={backupButtonDragMousePosition}
                    processorForBackup={processorForBackup}
                    t={t}
                  />

                  {draggedPort && portDragMousePosition && selectedProcessorForConnection && (
                    <PortDragPreview draggedPort={draggedPort} mousePosition={portDragMousePosition} />
                  )}
                </div>
              </div>
            </div>
            <RightPropertiesPanel
              isRightPanelOpen={isRightPanelOpen}
              setIsRightPanelOpen={setIsRightPanelOpen}
              selectedOutlet={selectedOutlet}
              selectedCabinetForConnection={selectedCabinetForConnection}
              getOutletColor={getOutletColor}
              outletColorOverrides={outletColorOverrides}
              setOutletColorOverrides={setOutletColorOverrides}
              selectedPort={selectedPort}
              selectedProcessorForConnection={selectedProcessorForConnection}
              getPortColor={getPortColor}
              portColorOverrides={portColorOverrides}
              setPortColorOverrides={setPortColorOverrides}
              selectedElements={selectedElements}
              workspaceElements={workspaceElements}
              setWorkspaceElements={setWorkspaceElements}
              dataPages={dataPages}
              powerPages={powerPages}
              selectedItemForProperties={selectedItemForProperties}
              getEffectiveSelectionCount={getEffectiveSelectionCount}
              formatNumber={formatNumber}
              elementGroups={elementGroups}
              getGroupForElement={getGroupForElement}
              createGroup={createGroup}
              deleteGroup={deleteGroup}
              panelConnections={panelConnections}
              mireConnections={mireConnections}
              powerConnections={powerConnections}
              mirePowerConnections={mirePowerConnections}
              cableRoutes={cableRoutes}
              backupPorts={backupPorts}
              currentStep={currentStep}
              showCartouche={showCartouche}
              setShowCartouche={setShowCartouche}
              showExportProcessorLink={showExportProcessorLink}
              setShowExportProcessorLink={setShowExportProcessorLink}
              readOnly={readOnly}
              savedMires={savedMires}
              setSavedMires={setSavedMires}
              wallTechnicalConfig={wallTechnicalConfig}
              setWallTechnicalConfig={setWallTechnicalConfig}
            />
            
          </>
        )}
      </div>
      {/* Bottom Panel (extracted to components/BottomPanel.tsx) */}
      <BottomPanel
        currentStep={currentStep}
        isBottomPanelOpen={isBottomPanelOpen}
        setIsBottomPanelOpen={setIsBottomPanelOpen}
        readOnly={readOnly}
        workspaceElements={workspaceElements}
        panelConnections={panelConnections}
        mireConnections={mireConnections}
        powerConnections={powerConnections}
        selectedProcessorForConnection={selectedProcessorForConnection}
        setSelectedProcessorForConnection={setSelectedProcessorForConnection}
        selectedPort={selectedPort}
        setSelectedPort={setSelectedPort}
        selectedCabinetForConnection={selectedCabinetForConnection}
        setSelectedCabinetForConnection={setSelectedCabinetForConnection}
        selectedOutlet={selectedOutlet}
        setSelectedOutlet={setSelectedOutlet}
        cabinetLinkMode={cabinetLinkMode}
        setCabinetLinkMode={setCabinetLinkMode}
        cabinetLinkCables={cabinetLinkCables}
        removeCabinetLink={removeCabinetLink}
        cabinetLoadDirect={cabinetLoadInfo.direct}
        cabinetLoadTotal={cabinetLoadInfo.total}
        isProcessorBackup={isProcessorBackup}
        getBackupProcessor={getBackupProcessor}
        getPrimaryProcessor={getPrimaryProcessor}
        removeBackupProcessor={removeBackupProcessor}
        isPortBackup={isPortBackup}
        isPortPrimary={isPortPrimary}
        getBackupForPort={getBackupForPort}
        getBackupsForPort={getBackupsForPort}
        getPrimaryForBackup={getPrimaryForBackup}
        getPortColor={getPortColor}
        getOutletColor={getOutletColor}
        draggedPort={draggedPort}
        setDraggedPort={setDraggedPort}
        portDragTarget={portDragTarget}
        setPortDragMousePosition={setPortDragMousePosition}
        showProcessorToFirstPanelLinks={showProcessorToFirstPanelLinks}
        setShowProcessorToFirstPanelLinks={setShowProcessorToFirstPanelLinks}
        showCabinetToFirstPanelLinks={showCabinetToFirstPanelLinks}
        setShowCabinetToFirstPanelLinks={setShowCabinetToFirstPanelLinks}
        setBackupPorts={setBackupPorts}
        setSkipNextHistorySave={setSkipNextHistorySave}
        saveToHistoryImmediate={saveToHistoryImmediate}
        setProcessorForBackup={setProcessorForBackup}
        setIsDraggingBackupButton={setIsDraggingBackupButton}
        setBackupButtonDragMousePosition={setBackupButtonDragMousePosition}
        onboarding={onboarding}
        MAX_DAISY_CHAIN_PANELS={calculatedMaxDaisyChain}
        portCapacityConfig={portCapacityConfig}
        setPortCapacityConfig={setPortCapacityConfig}
        portColorOverrides={portColorOverrides}
        setPortColorOverrides={setPortColorOverrides}
      />
      

      {/* Dialog pour créer un panneau personnalisé - Utilise le composant extrait */}
      <CustomPanelDialog
        open={isCustomPanelDialogOpen}
        onOpenChange={setIsCustomPanelDialogOpen}
        onCreatePanel={async (newPanel) => {
          setPanels(prev => [...prev, newPanel])
          toast.success(t("synoptic.panelCreated"), {
            description: `${newPanel.name} (${newPanel.resolution.width}×${newPanel.resolution.height})`
          })
          // Persister le panneau custom
          if (isPro && authSession?.access_token && !isTauriApp) {
            try {
              await fetch(getApiUrl('/api/synoptic/user-panels'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authSession.access_token}` },
                body: JSON.stringify({ panel: newPanel })
              })
            } catch (e) { /* silently fail */ }
          } else {
            try {
              const stored = JSON.parse(localStorage.getItem('vg_custom_panels') || '[]')
              stored.push(newPanel)
              localStorage.setItem('vg_custom_panels', JSON.stringify(stored))
            } catch (e) { /* silently fail */ }
          }
        }}
      />

      {/* Dialog pour créer une armoire personnalisée - Utilise le composant extrait */}
      <CustomCabinetDialog
        open={isCustomCabinetDialogOpen}
        onOpenChange={setIsCustomCabinetDialogOpen}
        onCreateCabinet={async (newCabinet) => {
          setPowerCabinets(prev => [...prev, newCabinet])
          toast.success(t("synoptic.cabinetCreated"), {
            description: `${newCabinet.name} (${newCabinet.maxPower}W, ${newCabinet.outlets} prises)`
          })
          // Persister l'armoire custom
          if (isPro && authSession?.access_token && !isTauriApp) {
            try {
              await fetch(getApiUrl('/api/synoptic/user-cabinets'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authSession.access_token}` },
                body: JSON.stringify({ cabinet: newCabinet })
              })
            } catch (e) { /* silently fail */ }
          } else {
            try {
              const stored = JSON.parse(localStorage.getItem('vg_custom_cabinets') || '[]')
              stored.push(newCabinet)
              localStorage.setItem('vg_custom_cabinets', JSON.stringify(stored))
            } catch (e) { /* silently fail */ }
          }
        }}
      />

      {/* Dialog pour confirmer la suppression d'une mire */}
      <DeleteMireDialog
        open={isDeleteMireDialogOpen}
        onOpenChange={setIsDeleteMireDialogOpen}
        onConfirm={handleConfirmDeleteMire}
        mireName={mireToDelete?.name || ''}
      />

      {/* Project Picker Modal */}
      {/* Dialogue Save As */}
      <Dialog open={showSaveAsDialog} onOpenChange={setShowSaveAsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('synoptic.saveAs') || 'Sauvegarder sous...'}</DialogTitle>
            <DialogDescription>
              {t('synoptic.saveAsDescription') || 'Choisissez un nom pour votre projet'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="saveAsFileName">{t('synoptic.projectName') || 'Nom du projet'}</Label>
              <Input
                id="saveAsFileName"
                value={saveAsFileName}
                onChange={(e) => setSaveAsFileName(e.target.value)}
                placeholder={t('synoptic.dialog.projectNamePlaceholder') || 'Mon Projet Synoptique'}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveAsConfirm()
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveAsDialog(false)}>
              {t('synoptic.dialog.cancel') || 'Annuler'}
            </Button>
            <Button onClick={handleSaveAsConfirm} disabled={!saveAsFileName.trim()}>
              {t('synoptic.save') || 'Sauvegarder'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ProjectPicker
        open={showProjectPicker}
        onClose={() => setShowProjectPicker(false)}
        onSelectProject={async (id) => {
          await loadProject(id)
          setShowProjectPicker(false)
        }}
      />

      {/* Avertissement : modifications non sauvegardées au bouton Retour du navigateur */}
      <AlertDialog
        open={exitPromptOpen}
        onOpenChange={(open) => {
          if (!open) {
            // Fermeture sans action explicite (Échap) = rester et sauvegarder
            if (exitChoiceRef.current === 'leave') {
              confirmExitWithoutSaving()
            } else {
              stayAndSave()
            }
            exitChoiceRef.current = null
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('synopticProvider.unsavedChangesTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('synopticProvider.unsavedChangesDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { exitChoiceRef.current = 'stay' }}>
              {t('synopticProvider.unsavedChangesStay')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => { exitChoiceRef.current = 'leave' }}>
              {t('synopticProvider.unsavedChangesLeave')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

// Wrapper avec le Provider
export default function SynoptiquePage() {
  return (
    <SynopticProjectProvider>
      <SynoptiqueContent />
    </SynopticProjectProvider>
  )
}
